import { getViewMode } from "@/lib/view-mode-context";
import { downscaleImageForUpload } from "@/lib/image-downscale";
import type { LocationNote } from "@/lib/location-notes";

// In the browser, use relative URLs to go through the Next.js API proxy
// (keeps httpOnly cookies on the same domain). On the server (SSR), call
// the backend directly.
const API_BASE =
  typeof window !== "undefined"
    ? ""
    : process.env.API_URL || process.env.NEXT_PUBLIC_API_URL || "http://localhost:9000";

export class ApiError extends Error {
  status: number;
  body: unknown;
  /** Backend-suggested retry delay (Retry-After header, e.g. on a 503 from
   *  connection-pool exhaustion). React Query's retryDelay honors this so
   *  the single retry waits out the saturation instead of piling on. */
  retryAfterMs?: number;

  constructor(status: number, message: string, body?: unknown, retryAfterMs?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.retryAfterMs = retryAfterMs;
  }
}

// FastAPI returns `detail` as a string for HTTPException paths and as an array of
// `{loc, msg, type, ...}` for Pydantic validation (422). Coerce either shape to a
// readable string so `ApiError.message` stays human, not `[object Object],[object Object]`.
function formatErrorDetail(detail: unknown, status: number): string {
  const fallback = `Villa ${status}`;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (item && typeof item === "object") {
          const msg = (item as { msg?: unknown }).msg;
          const loc = (item as { loc?: unknown }).loc;
          const locTail =
            Array.isArray(loc) && loc.length > 0
              ? loc.filter((p) => p !== "body").map(String).join(".")
              : "";
          if (typeof msg === "string" && msg.length > 0) {
            return locTail ? `${locTail}: ${msg}` : msg;
          }
        }
        return String(item);
      })
      .filter((s) => s.length > 0);
    return parts.length > 0 ? parts.join("; ") : fallback;
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return fallback;
  }
}

// ---------- Transparent access-token refresh ----------
//
// The access cookie (xd_token) is now short-lived; a long-lived rotating
// refresh cookie (xd_refresh, path-scoped to /api/auth/refresh) keeps the
// session alive. On a 401 from a normal API call we POST /api/auth/refresh
// ONCE, then replay the original request. A SINGLE module-level in-flight
// promise dedupes concurrent 401s — without it, N parallel requests would each
// fire their own refresh, and the rotating-secret reuse detection on the
// backend would treat the losers as stolen tokens and self-revoke the family.

export type RefreshOutcome = "ok" | "rejected" | "network";

let refreshInFlight: Promise<RefreshOutcome> | null = null;

// Cross-tab coordination for the rotating refresh secret. The module-level
// in-flight promise only dedupes within ONE tab; two tabs waking together
// (session restore, morning laptop-open) used to fire concurrent rotations of
// the SAME family, and whichever Set-Cookie lost the cookie-jar race left the
// browser holding a dead secret — tripping reuse detection an hour later and
// burning the whole session. A Web Lock serializes the POST across tabs, and
// a short "just rotated" timestamp lets the tabs that waited skip their own
// (now redundant) rotation entirely.
const REFRESH_LOCK_NAME = "xd:auth:refresh";
const REFRESH_JUST_ROTATED_KEY = "xd_refresh_ok_at";
const REFRESH_JUST_ROTATED_WINDOW_MS = 3000;

function refreshedJustNow(): boolean {
  try {
    const at = Number(localStorage.getItem(REFRESH_JUST_ROTATED_KEY) ?? "0");
    return Number.isFinite(at) && Date.now() - at < REFRESH_JUST_ROTATED_WINDOW_MS;
  } catch {
    return false; // privacy mode / storage unavailable — just refresh normally
  }
}

function markRefreshOk(): void {
  try {
    localStorage.setItem(REFRESH_JUST_ROTATED_KEY, String(Date.now()));
  } catch {
    // best-effort only
  }
}

async function postRefresh(): Promise<RefreshOutcome> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
    });
    if (res.ok) {
      markRefreshOk();
      return "ok";
    }
    if (res.status === 401 || res.status === 403) return "rejected";
    return "network"; // 5xx / 429 etc — transient, don't destroy the session
  } catch {
    return "network"; // network error / timeout / abort
  } finally {
    clearTimeout(timeout);
  }
}

/** POST /api/auth/refresh at most once concurrently — per tab AND across tabs.
 *
 *  - "ok": refreshed → caller replays the original request.
 *  - "rejected": the refresh cookie is genuinely invalid/expired (explicit
 *    401/403) → caller falls through to the login redirect.
 *  - "network": the refresh couldn't complete (offline, timeout, 5xx). This is
 *    NOT a reason to log a canvasser out mid-shift on a flaky link — the cookie
 *    is likely still valid — so the caller surfaces a retryable error and keeps
 *    walk state intact. Mirrors the mobile app's "only 401/403 logs out" policy.
 */
export function refreshAccessToken(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
      if (locks?.request) {
        try {
          // Bound the acquisition: a SUSPENDED background tab can sit on the
          // lock (iOS Safari freezes pages mid-callback), and an unbounded
          // wait would wedge auth in every other tab. The holder's own fetch
          // aborts at 10s, so 15s comfortably covers the legitimate case.
          const options =
            typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
              ? { signal: AbortSignal.timeout(15000) }
              : {};
          return await locks.request(REFRESH_LOCK_NAME, options, async () => {
            // Another tab may have rotated while we waited for the lock — the
            // jar already holds the fresh cookie, so replaying is enough.
            if (refreshedJustNow()) return "ok" as const;
            return postRefresh();
          });
        } catch {
          // Lock never became available (or the Locks API errored) — refresh
          // without cross-tab serialization rather than wedging auth.
          return await postRefresh();
        }
      }
      // No Web Locks (old browsers): per-tab dedupe only, as before.
      return await postRefresh();
    } finally {
      // Clear AFTER awaiting callers observe the resolved value, so a later
      // 401 starts a fresh refresh rather than reusing a stale result.
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function redirectToLogin(): void {
  if (typeof window !== "undefined") {
    const p = window.location.pathname;
    if (p !== "/login" && p !== "/callback") {
      // ?expired=1 tells the edge guard NOT to bounce this visit back to
      // /dashboard on token presence alone: we get here precisely when the
      // session is dead server-side (rejected refresh) even though an
      // unexpired-looking xd_token may still sit in the jar — bouncing on it
      // produced an infinite /login↔/dashboard loop. The login page's own
      // session probe still rescues a genuinely live session.
      window.location.href = "/login?expired=1";
    }
  }
}

/** RequestInit plus knobs fetchApi handles itself (stripped before fetch()). */
type FetchApiOptions = RequestInit & {
  /** Skip the global "invalid session → /login" bounce. See authMeProbe(). */
  skipLoginRedirect?: boolean;
};

async function fetchApi<T>(
  path: string,
  options: FetchApiOptions = {},
  timeoutMs = 12000,
  retried = false,
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const { skipLoginRedirect, ...init } = options;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  // When admin is viewing as regular user, tell the backend to drop privileges
  const extraHeaders: Record<string, string> = {};
  if (getViewMode() === "user") {
    extraHeaders["X-View-As"] = "user";
  }

  try {
    const res = await fetch(url, {
      ...init,
      signal: controller.signal,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...extraHeaders,
        ...init.headers,
      },
    });

    if (res.status === 401) {
      // Try a one-shot transparent refresh + replay before giving up. Never
      // refresh the refresh call itself, and only retry once.
      if (!retried && path !== "/api/auth/refresh") {
        const outcome = await refreshAccessToken();
        if (outcome === "ok") {
          clearTimeout(timeout);
          return fetchApi<T>(path, options, timeoutMs, true);
        }
        if (outcome === "network") {
          // Refresh couldn't complete on a flaky link — do NOT bounce the
          // volunteer to /login and destroy their in-progress walk. Surface a
          // retryable network error (status 0); the session is likely still
          // valid once signal returns, and disposition submits will queue.
          throw new ApiError(0, "Nettenging töpuð — reyndu aftur");
        }
        // outcome === "rejected": the refresh cookie is genuinely invalid.
      }
      if (!skipLoginRedirect) redirectToLogin();
      throw new ApiError(401, "Óheimil aðgangur");
    }

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const retryAfterS = Number(res.headers.get("Retry-After"));
      throw new ApiError(
        res.status,
        formatErrorDetail(body?.detail, res.status),
        body,
        Number.isFinite(retryAfterS) && retryAfterS > 0 ? retryAfterS * 1000 : undefined
      );
    }

    if (res.status === 204) return undefined as T;
    return res.json();
  } catch (err) {
    // Normalize every failure to an ApiError so downstream error handling
    // (React Query retry policy, auth bootstrap) always sees a status.
    if (err instanceof ApiError) throw err;
    if (
      typeof err === "object" &&
      err !== null &&
      (err as { name?: string }).name === "AbortError"
    ) {
      throw new ApiError(0, "Beiðni rann út á tíma");
    }
    throw new ApiError(0, "Nettenging töpuð, reyndu aftur");
  } finally {
    clearTimeout(timeout);
  }
}

// ---------- Types ----------

export interface UserOrganization {
  id: string;
  name: string;
  slug: string;
  org_type: string;
  accepts_donations?: boolean;
}

export interface ContactPrefs {
  email_events: boolean;
  email_polls: boolean;
  email_campaigns: boolean;
  email_news: boolean;
  sms_reminders: boolean;
  sms_events: boolean;
  sms_campaigns: boolean;
  phone_campaigns: boolean;
  phone_elections: boolean;
}

export interface UserPreferences {
  show_photo_tags: boolean;
  volunteer_willing: boolean;
  can_be_emailed: boolean;
  can_be_called: boolean;
  can_be_smsed: boolean;
  contact_prefs: ContactPrefs;
}

export interface User {
  id: string;
  name: string;
  given_name?: string;
  family_name?: string;
  kennitala?: string | null;
  kennitala_hash?: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  gender?: string | null;
  profile_image_url?: string | null;
  pending_profile_image_url?: string | null;
  is_active?: boolean;
  is_global_admin?: boolean;
  is_muted?: boolean;
  is_blacklisted?: boolean;
  join_date?: string;
  can_be_emailed?: boolean;
  can_be_called?: boolean;
  can_be_smsed?: boolean;
  barcode?: string | null;
  is_donor?: boolean;
  has_email?: boolean;
  has_phone?: boolean;
  bannmerki_thjodskra?: boolean;
  bannmerki_simaskra?: boolean;
  /** True once the member has logged in via the native app. */
  has_mobile_app?: boolean;
  roles?: OrgRole[];
  organizations?: UserOrganization[];
  /** Master org types the member has chosen (almennt_felag, fus, fes, kvk,
   *  malfundafelag). Set at signup, editable on the profile page. */
  preferred_org_types?: string[];
  /** Enabled feature keys. null/undefined = feature system not active → all enabled. */
  features?: string[] | null;
  party_org_id?: string | null;
  preferences?: UserPreferences;
  election_permissions?: ElectionPermissionInfo[];
  has_walk_lists?: boolean;
  /**
   * True for inactive members (is_active=false) who have at least one
   * walk-list assignment matching their kennitala_hash. Drives the stripped
   * canvasser-only UI chrome + routing guard for candidates and other
   * helpers manually added via the Úthluta dialog.
   */
  is_guest_canvasser?: boolean;
  active_elections?: {
    id: string;
    name: string;
    election_day: string;
    // sveitarstjorn | althingi | thjodaratkvaedagreidsla | primary | internal;
    // thjodaratkvaedagreidsla hides "Persónulegur listi" (its surface is /saman).
    election_type?: string;
    training_enabled?: boolean;
  }[];
  needs_registration?: boolean;
  needs_gdpr_consent?: boolean;
  /** Bulk-imported member on first Kenni login — show GdprModal in
   *  'migrated_first_login' mode (collects phone + email alongside GDPR). */
  is_migrated_first_login?: boolean;
  pending_deletion?: { scheduled_for: string; requested_at: string } | null;
  current_gdpr_version?: number;
  gdpr_consent_version?: number | null;
  gdpr_consent_at?: string | null;
  needs_election_consent?: boolean;
  current_election_consent_version?: number;
  election_consent_version?: number | null;
  election_consent_at?: string | null;
}

export interface OrgRole {
  organization_id: string;
  organization_name: string;
  role: string;
  permissions: string[];
  // "membership": the user belongs to the org. "permission": they only hold
  // grants on it (orgs are flat, so such grants never showed before).
  via?: "membership" | "permission";
}

export interface Member {
  id: string;
  name: string;
  given_name?: string;
  family_name?: string;
  kennitala_hash: string;
  kennitala?: string | null;
  email?: string | null;
  phone?: string | null;
  phone_secondary?: string | null;
  postal_code?: string;
  address?: string | null;
  house_number?: number | null;
  house_letter?: string | null;
  municipality?: string | null;
  birth_date?: string | null;
  gender?: string | null;
  organization_name?: string;
  // Populated by the list endpoint with every org the member actively
  // belongs to. Per-row in MembersGrid, rendered as chip-links.
  organizations?: MemberOrganization[];
  profile_image_url?: string | null;
  is_muted?: boolean;
  muted_at?: string | null;
  muted_by_name?: string | null;
  is_blacklisted?: boolean;
  blacklisted_at?: string | null;
  blacklisted_by_name?: string | null;
  deactivated_at?: string | null;
  can_be_emailed?: boolean;
  can_be_called?: boolean;
  can_be_smsed?: boolean;
  can_be_pushed?: boolean;
  bannmerki_thjodskra?: boolean;
  bannmerki_simaskra?: boolean;
  thjodskra_synced_at?: string | null;
  is_donor?: boolean;
  volunteer_willing?: boolean;
  engagement_score?: number;
  barcode?: string | null;
  status: "active" | "inactive" | "pending";
  tags: string[];
  joined_at: string;
  created_at: string;
}

/**
 * Server-computed authority over one member (plan: "Two authority levels").
 * The page renders from this, never from the switcher org, so a button is
 * shown only when the request it fires will succeed.
 */
export interface MemberCapabilities {
  /** "party": any members.* grant on the Landssamband or global admin; "org" otherwise. */
  scope: "party" | "org";
  /** The Landssamband's id, so the client can tell root-org tag categories apart. */
  root_org_id: string | null;
  full_view: boolean;
  can_edit: boolean;
  can_moderate: boolean;
  global_tags: boolean;
  /** null = any org (global admin). */
  addable_org_ids: string[] | null;
  removable_org_ids: string[];
  taggable_org_ids: string[];
  permission_org_ids: string[];
  /** null = any org (global admin). */
  permission_edit_org_ids: string[] | null;
}

export interface MemberDetail extends Member {
  organizations: MemberOrganization[];
  capabilities: MemberCapabilities;
  // Kerfi card, party scope only.
  last_login_at?: string | null;
  mobile_last_login_at?: string | null;
  directory_visible?: boolean | null;
  gdpr_consent_at?: string | null;
  gdpr_consent_version?: string | null;
  election_consent_at?: string | null;
  scheduled_deletion_at?: string | null;
}

export interface MemberOrganization {
  id: string;
  name: string;
  slug: string;
  org_type: string;
  joined_at?: string | null;
  /** "admin" | "organizer" | "reader", or null when the viewer's scope hides it. */
  role_label?: string | null;
}

export interface MemberHistoryEvent {
  kind: string;
  at: string | null;
  organization_id?: string | null;
  organization_name?: string | null;
  actor_name?: string | null;
  detail?: string | string[] | null;
  scheduled_for?: string | null;
}

export interface MemberPermissionOrgRow {
  organization_id: string;
  organization_name: string;
  permissions: string[];
  role_label: string;
  can_edit: boolean;
}

export interface ActivityEntry {
  id: string;
  action: string;
  description: string;
  actor_id: string | null;
  actor_name: string | null;
  created_at: string | null;
}

export interface Organization {
  id: string;
  slug: string;
  name: string;
  /** Short numeric org code (e.g. "1110", "0001"), when assigned. */
  code?: string | null;
  org_type: string;
  description?: string;
  parent_id?: string;
  parent_name?: string;
  is_visible?: boolean;
  open_for_registration?: boolean;
  accepts_donations?: boolean;
  is_gervifelag?: boolean;
  kjordaemi_nr?: number;
  merged_into_id?: string;
  /** Present on /api/organizations/tree rows; a few orgs are wound up. */
  is_active?: boolean;
  member_count: number;
  children?: Organization[];
}

export interface DiscoverOrg {
  id: string;
  name: string;
  slug: string;
  description?: string;
  org_type: string;
  member_count: number;
  leaders: { id: string; name: string; profile_image_url?: string | null }[];
  my_status?: "active" | "pending" | "rejected" | null;
}

export interface MembershipRequest {
  id: string;
  member_id: string;
  member_name: string;
  member_email?: string | null;
  member_postal_code?: string | null;
  member_municipality?: string | null;
  member_join_date?: string | null;
  member_existing_orgs?: string[];
  profile_image_url?: string | null;
  organization_id?: string;
  organization_name?: string;
  organization_slug?: string;
  requested_at?: string;
}

export interface OrganizationDetail extends Organization {
  children: Organization[];
  members: Member[];
  can_read_members?: boolean;
  can_manage_members?: boolean;
  can_delete_members?: boolean;
  // Inheritance-aware payment grants for the viewer on THIS org (resolved down
  // the ltree on the backend), so parent-org staff see the payments tab.
  can_list_payments?: boolean;
  can_create_payments?: boolean;
  can_manage_payments?: boolean;
  // Registry-derived: the viewer is a global admin or the org's active formaður
  // per the trustee registry. Gates the Stjórn tab, which the backend authorizes
  // the same way (not via any RBAC grant or membership row).
  can_manage_board?: boolean;
  my_status?: "active" | "pending" | "rejected" | null;
}

export interface Event {
  id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time?: string;
  location?: string;
  organization_id: string;
  organization_name?: string;
  is_public: boolean;
  is_cancelled?: boolean;
  event_type?: string;
  is_virtual?: boolean;
  virtual_url?: string | null;
  recurrence_rule?: string | null;
  max_attendees?: number | null;
  rsvp_count: number;
  attendance_count: number;
  reminder_at?: string | null;
  my_rsvp?: "attending" | "maybe" | "not_attending" | "waitlisted" | null;
  my_reminder?: boolean;
  announced_at?: string | null;
  created_by?: string | null;
  avg_rating?: number | null;
  feedback_count?: number;
  cover_image_url?: string | null;
}

export interface EventDetail extends Event {
  has_recap?: boolean;
  attendees: EventAttendee[];
}

export interface EventAttendee {
  id: string;
  member_id: string;
  member_name: string;
  rsvp_status: string;
  status?: string;
  checked_in?: boolean;
  checked_in_at?: string;
}

export interface EventRecapPhoto {
  id: string;
  url: string;
  caption?: string;
  tags: { member_id: string; member_name: string }[];
}

export interface EventRecap {
  id: string;
  event_id: string;
  title: string;
  content?: string;
  photos: EventRecapPhoto[];
  created_by: string;
  created_by_name: string;
  created_at: string;
  event_title?: string;
  event_location?: string;
  event_start_time?: string;
}

// Media types
export interface MediaItem {
  id: string;
  storage_path: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  width?: number;
  height?: number;
  duration_seconds?: number;
  thumbnail_path?: string;
  thumbnail_url?: string;
  caption?: string;
  public_url: string;
  uploaded_by: string;
  uploader_name?: string;
  organization_id: string;
  organization_name?: string;
  status: "pending" | "approved" | "rejected";
  person_tags: MediaPersonTag[];
  event_ids: string[];
  is_primary_for_events: string[];
  created_at: string;
}

export interface MediaPersonTag {
  member_id: string;
  member_name: string;
  tagged_by: string;
  created_at: string;
}

export interface TaggableMember {
  id: string;
  name: string;
}

// Poll types
export type PollType = "survey" | "motion" | "deliberative" | "prioritization" | "election";
export type PollPhase = "draft" | "briefing" | "voting" | "closed";
export type AnonymityLevel = "open" | "anonymous" | "pseudonymous";
export type QuestionType = "single_choice" | "multiple_choice" | "text" | "likert" | "ranking" | "approval" | "yes_no";
export type ElectionType = "board" | "chair" | "delegate" | "congress";

export interface Poll {
  id: string;
  title: string;
  description?: string;
  poll_type: PollType;
  phase: PollPhase;
  anonymity_level: AnonymityLevel;
  is_public: boolean;
  organization_id: string;
  organization_name?: string;
  questions: PollQuestion[];
  briefings?: PollBriefing[];
  arguments?: PollArgumentSummary;
  response_count: number;
  eligible_member_count: number;
  min_participation_pct?: number;
  created_at: string;
  opens_at?: string;
  closes_at?: string;
  my_response?: PollResponse | null;
  // Election-specific
  election_type?: ElectionType;
  candidate_registration_deadline?: string;
  voter_eligibility_rules?: Record<string, unknown>;
  candidates?: ElectionCandidate[];
  voter_eligible?: boolean;
}

export interface PollBriefing {
  id: string;
  title: string;
  content_markdown: string;
  source_url?: string;
  order: number;
}

export interface PollQuestion {
  id: string;
  text: string;
  description?: string;
  question_type: QuestionType;
  options?: PollOption[];
  required: boolean;
  min_selections?: number;
  max_selections?: number;
}

export interface PollOption {
  id: string;
  text: string;
  vote_count?: number;
}

export interface PollResponse {
  id: string;
  answers: PollAnswer[];
  submitted_at: string;
}

export interface PollAnswer {
  question_id: string;
  option_ids?: string[];
  ranking?: string[];
  text?: string;
}

export interface PollArgument {
  id: string;
  side: "pro" | "con";
  title: string;
  body: string;
  author_name?: string;
  vote_count: number;
  my_vote: boolean;
  created_at: string;
}

export interface PollArgumentSummary {
  pro_count: number;
  con_count: number;
  top_pro?: PollArgument[];
  top_con?: PollArgument[];
}

export interface ElectionCandidate {
  id: string;
  poll_id: string;
  member_id: string;
  member_name: string | null;
  statement: string | null;
  endorsed_by: Record<string, unknown> | null;
  registered_at: string;
  approved: boolean;
  approved_at: string | null;
}

export interface IrvRound {
  round: number;
  counts: Record<string, number>;
  eliminated: string | null;
}

export interface IrvResults {
  rounds: IrvRound[];
  winner: { id: string; name: string } | null;
  total_ballots: number;
}

export interface PollResults {
  poll_id: string;
  total_responses: number;
  eligible_member_count: number;
  participation_pct: number;
  questions: PollQuestionResult[];
}

export interface PollQuestionResult {
  question_id: string;
  text: string;
  question_type: string;
  options: PollOptionResult[];
  rankings?: PollRankingResult[];
}

export interface PollOptionResult {
  id: string;
  text: string;
  count: number;
  percentage: number;
}

export interface PollRankingResult {
  id: string;
  text: string;
  avg_rank: number;
  rank_distribution: number[];
}

export interface Donation {
  id: string;
  donor_name: string;
  donor_kennitala_hash?: string;
  amount: number;
  currency: string;
  method: "bank_transfer" | "card" | "cash" | "other";
  reference?: string;
  status: "pending" | "completed" | "refunded";
  payment_id?: string | null;
  organization_id: string;
  organization_name?: string;
  donated_at: string;
  created_at: string;
}

export interface DonationSummary {
  total_amount: number;
  total_count: number;
  by_method: { method: string; amount: number; count: number }[];
  by_source_type?: { source_type: string | null; amount: number; count: number }[];
  by_month: { month: string; amount: number; count: number }[];
}

export interface AuditLogEntry {
  id: string;
  actor_id: string;
  actor_name: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details?: Record<string, unknown>;
  ip_address?: string;
  created_at: string;
}

export interface GDPRRequest {
  id: string;
  member_id: string;
  member_name: string;
  request_type: "access" | "deletion" | "portability";
  status: "pending" | "processing" | "completed" | "rejected";
  created_at: string;
  processed_at?: string;
}


export interface PaginatedResponse<T> {
  items: T[];
  next_cursor?: string;
  has_more: boolean;
  total_count?: number;
}

// ---------- Auth ----------

export async function authLogin(
  accessKey?: string
): Promise<{ redirect_url: string; binding_token: string }> {
  // Use local Next.js proxy to keep the xd_token cookie same-domain
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (accessKey) {
    headers["x-access-key"] = accessKey;
  }
  const res = await fetch("/api/auth", {
    method: "POST",
    credentials: "include",
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.detail || "Login failed", body);
  }
  return res.json();
}

export async function authCallback(
  code: string,
  state: string,
  bindingToken: string
): Promise<{ ok: boolean }> {
  // Use local Next.js proxy so the xd_token cookie set by the backend lands
  // on the same origin as the app.
  const res = await fetch("/api/auth/callback", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, state, binding_token: bindingToken }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.detail || "Callback failed", body);
  }
  return res.json();
}

export async function authMe(): Promise<User> {
  return fetchApi("/api/auth/me");
}

/**
 * Read the session the way a public page needs to: without the global
 * "401 → redirect to /login" side effect.
 *
 * A page that renders for signed-in and logged-out visitors alike (the /hjalpa
 * landing) has to ask who the visitor is, but for it a 401 is an answer rather
 * than a failure. Letting authMe()'s bounce fire there kicks every logged-out
 * visitor to /login the moment the page hydrates. An expired access token that
 * the refresh cookie can still rescue resolves to the user as normal; null means
 * no usable session, or a probe that failed outright, both of which a public
 * page should treat as "guest".
 */
export async function authMeProbe(): Promise<User | null> {
  try {
    return await fetchApi<User>("/api/auth/me", { skipLoginRedirect: true });
  } catch {
    return null;
  }
}

export interface UserDetail {
  kennitala: string | null;
  email: string | null;
  phone: string | null;
}

/** Fetch PII fields (kennitala, email, phone). Only call when needed (profile/settings page). */
export async function authMeDetail(): Promise<UserDetail> {
  return fetchApi("/api/auth/me/detail");
}

export async function authLogout(): Promise<void> {
  return fetchApi("/api/auth/logout", { method: "POST" });
}

export interface EligibleOrgType {
  type: string;
  label_is: string;
  eligible: boolean;
  reason_code?: string | null;
  reason_is?: string | null;
}

export interface JoinedOrgInfo {
  id: string;
  name: string;
  slug: string;
  org_type: string;
}

export interface RegisterResponse {
  ok: boolean;
  joined_orgs: JoinedOrgInfo[];
  stub_created: boolean;
}

export async function getEligibleOrgTypes(): Promise<EligibleOrgType[]> {
  return fetchApi("/api/auth/eligible-org-types");
}

export async function registerMember(
  gdpr_consent_version: number,
  preferred_org_types: string[],
  phone: string,
  email: string,
  referral_code?: string | null,
): Promise<RegisterResponse> {
  return fetchApi("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({
      gdpr_consent_version,
      phone,
      email,
      preferred_org_types,
      ...(referral_code ? { referral_code } : {}),
    }),
  });
}

export async function unregisterMember(): Promise<{ ok: boolean }> {
  return fetchApi("/api/auth/unregister", {
    method: "POST",
    body: JSON.stringify({ confirm: "AFSKRÁ" }),
  });
}

export async function requestDeletion(): Promise<{
  ok: boolean;
  scheduled_for: string;
}> {
  return fetchApi("/api/auth/request-deletion", {
    method: "POST",
    body: JSON.stringify({ confirm: "EYÐA" }),
  });
}

export async function cancelDeletion(): Promise<{ ok: boolean }> {
  return fetchApi("/api/auth/cancel-deletion", {
    method: "POST",
  });
}

export async function acceptGdpr(
  gdpr_consent_version: number,
): Promise<{ ok: boolean }> {
  return fetchApi("/api/auth/gdpr-consent", {
    method: "POST",
    body: JSON.stringify({ gdpr_consent_version }),
  });
}

/** First-login completion for bulk-imported members. Records GDPR consent,
 *  writes phone + email (both required), and backfills last_login_at to exit
 *  the migrated-first-login state. */
export async function submitMigratedOnboarding(
  gdpr_consent_version: number,
  phone: string,
  email: string,
): Promise<{ ok: boolean }> {
  return fetchApi("/api/auth/migrated-onboarding", {
    method: "POST",
    body: JSON.stringify({ gdpr_consent_version, phone, email }),
  });
}

export interface GDPRDeclaration {
  version: number;
  title: string;
  content: string;
}

export async function fetchGdprDeclaration(
  version?: number,
): Promise<GDPRDeclaration> {
  const qs = version != null ? `?version=${version}` : "";
  return fetchApi(`/api/auth/gdpr-declaration${qs}`);
}

export interface ElectionDeclaration {
  version: number;
  title: string;
  content: string;
}

export async function fetchElectionDeclaration(
  version?: number,
): Promise<ElectionDeclaration> {
  const qs = version != null ? `?version=${version}` : "";
  return fetchApi(`/api/auth/election-declaration${qs}`);
}

export async function acceptElectionConsent(
  election_consent_version: number,
): Promise<{ ok: boolean }> {
  return fetchApi("/api/auth/election-consent", {
    method: "POST",
    body: JSON.stringify({ election_consent_version }),
  });
}

// ---------- Walk-list export consent + download ----------

/**
 * Walk-list download mode.
 * - `gagnaskra`      — analytical rows keyed by opaque `kennitala_hash`,
 *                      no name / kennitala / phone / address. Safe to
 *                      filter in Excel and re-import via the split-modal
 *                      CSV tab.
 * - `tengilidalisti` — contact sheet: name, kennitala, phone. Nothing
 *                      else. Deliberately omits the hash so the two
 *                      files can't be joined on a stable key.
 */
export type ExportMode = "gagnaskra" | "tengilidalisti" | "kjordagsgogn";

export interface ExportDeclaration {
  version: number;
  title: string;
  content: string;
}

/**
 * Fetch the current versioned declaration text for the export consent
 * dialog. Structurally identical to `fetchElectionDeclaration`.
 */
export async function fetchExportDeclaration(): Promise<ExportDeclaration> {
  return fetchApi(`/api/exports/declaration`);
}

/**
 * Mint a single-use download token for a specific walk list + mode.
 *
 * Preconditions the backend enforces:
 * 1. Election consent accepted (otherwise 412 with
 *    `requires_election_consent`).
 * 2. Valid mode string.
 * 3. Caller has permission on the walk list.
 *
 * Token TTL is 5 minutes. Single-use — the download endpoint atomically
 * marks it delivered.
 */
export async function requestExportConsent(args: {
  mode: ExportMode;
  walkListId: string;
  rowCount?: number;
}): Promise<{ token: string; expires_at: string }> {
  return fetchApi(`/api/exports/consent`, {
    method: "POST",
    body: JSON.stringify({
      mode: args.mode,
      walk_list_id: args.walkListId,
      row_count: args.rowCount,
    }),
  });
}

/**
 * Download a walk list as xlsx using a previously-minted consent token.
 *
 * Triggers a browser save via a blob URL. The returned promise resolves
 * once the download has kicked off — the actual disk write is handled
 * by the browser. Errors (403 invalid/expired token, 403 permission
 * revoked, 404 walk list gone) surface as thrown `ApiError`s.
 *
 * Note: we can't use `fetchApi` here because the response is a binary
 * stream, not JSON. This replicates the auth cookie + CSRF behavior
 * inline.
 */
export async function downloadWalkListExport(args: {
  walkListId: string;
  mode: ExportMode;
  token: string;
}): Promise<void> {
  const url = `${API_BASE}/api/exports/walk-list/${args.walkListId}`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(getViewMode() === "user" ? { "X-View-As": "user" } : {}),
    },
    body: JSON.stringify({ token: args.token, mode: args.mode }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }

  // Parse filename out of Content-Disposition if present, else fall
  // back to a sensible default. The backend sends
  // `attachment; filename="Gagnaskrá-Listi.xlsx"`.
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const fallbackExt = args.mode === "kjordagsgogn" ? "zip" : "xlsx";
  const filename = match?.[1] || `gongulistinn-${args.mode}.${fallbackExt}`;

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  // Chrome needs a tick to initiate the download before cleanup
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }, 100);
}

// ---------------------------------------------------------------------------
// Campaign-level kjördagsgögn cohort exports
// ---------------------------------------------------------------------------

/**
 * Campaign-level kjördagsgögn cohort. Mirrors the backend
 * ``CampaignCohort`` literal in ``app/exports/campaign_cohorts.py``.
 *
 * Seven mutually-exclusive buckets — proved by the data-integrity test
 * at ``backend/tests/test_campaign_cohorts.py::test_cohort_assignment_matrix_no_row_in_two_cohorts``.
 * Already-voted rows live solely in ``already_voted``; everything else
 * splits by member status / disposition / Forgangur band as documented
 * in the backend module.
 */
export type CampaignCohort =
  | "personal"
  | "flokksskra_callback_yes"
  | "flokksskra_other"
  | "blindir_callback_yes"
  | "blindir_other_f1_3"
  | "blindir_other_f4"
  | "blindir_other_f5_6"
  | "already_voted"
  | "callback_no";

export const CAMPAIGN_COHORT_LABELS: Record<CampaignCohort, string> = {
  personal: "Persónulegir Listar",
  flokksskra_callback_yes: "Flokksskrá – Má hringja aftur",
  flokksskra_other: "Flokksskrá – Aðrir",
  blindir_callback_yes: "Blindir – Má hringja aftur",
  blindir_other_f1_3: "Blindir Aðrir – F1-3",
  blindir_other_f4: "Blindir Aðrir – F4",
  blindir_other_f5_6: "Blindir Aðrir – F5-6",
  already_voted: "Búinn að kjósa",
  callback_no: "Má ekki hringja aftur",
};

export type CampaignCohortCounts = Record<CampaignCohort, number>;

/**
 * Fetch the per-cohort row counts for a campaign. Counts are
 * post-universal-filters — the number on the button equals the number
 * of rows in the downloaded zip. Manager-only on the backend.
 */
export async function fetchCampaignCohortCounts(
  campaignId: string
): Promise<CampaignCohortCounts> {
  return fetchApi(`/api/exports/campaign/${campaignId}/cohort-counts`);
}

/**
 * Mint a single-use download token for a campaign-level cohort
 * kjördagsgögn export. Same TTL + single-use semantics as the
 * walk-list path.
 */
export async function requestCampaignExportConsent(args: {
  campaignId: string;
  cohort: CampaignCohort;
  rowCount?: number;
}): Promise<{ token: string; expires_at: string }> {
  return fetchApi(`/api/exports/campaign-consent`, {
    method: "POST",
    body: JSON.stringify({
      campaign_id: args.campaignId,
      cohort: args.cohort,
      row_count: args.rowCount,
    }),
  });
}

/**
 * Download a campaign-cohort kjördagsgögn zip with a previously-minted
 * consent token. Streams the zip via a blob URL like
 * ``downloadWalkListExport``.
 */
export async function downloadCampaignCohortExport(args: {
  campaignId: string;
  cohort: CampaignCohort;
  token: string;
  colorCode: boolean;
}): Promise<void> {
  const url = `${API_BASE}/api/exports/campaign/${args.campaignId}/download`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(getViewMode() === "user" ? { "X-View-As": "user" } : {}),
    },
    body: JSON.stringify({
      token: args.token,
      cohort: args.cohort,
      color_code: args.colorCode,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }

  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `kjordagsgogn-${args.cohort}.zip`;

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }, 100);
}

export async function updateProfile(data: {
  given_name?: string;
  family_name?: string;
  phone?: string;
  email?: string;
  gender?: string;
  profile_image_url?: string | null;
  interest_ids?: string[];
  preferred_org_types?: string[];
}): Promise<User> {
  return fetchApi("/api/auth/me", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ---------- Interests ----------

export interface InterestOption {
  id: string;
  name: string;
  selected: boolean;
}

export async function getInterests(): Promise<{ interests: InterestOption[] }> {
  return fetchApi("/api/interests");
}

export type PreferencesUpdate = Partial<Omit<UserPreferences, "contact_prefs">> & {
  contact_prefs?: Partial<ContactPrefs>;
};

export async function updatePreferences(
  prefs: PreferencesUpdate
): Promise<UserPreferences> {
  return fetchApi("/api/auth/me/preferences", {
    method: "PUT",
    body: JSON.stringify(prefs),
  });
}

// ---------- Members ----------

export async function getMembers(params?: {
  cursor?: string;
  limit?: number;
  search?: string;
  organization_id?: string;
  status?: string;
  postal_code?: string;
  /** Comma-separated tag names; every one must be present (SQL, pre-pagination). */
  has_tags?: string;
  /** Þaggaðir or á bannlista only. */
  flagged?: boolean;
  include_kennitala?: boolean;
  exclude_org_id?: string;
  // ag-grid SSRM: JSON-encoded sortModel / filterModel. When either is
  // non-empty the backend switches to OFFSET pagination (cursor becomes a
  // numeric row index) and returns a real pre-slice total_count.
  sortModel?: string;
  filterModel?: string;
}): Promise<PaginatedResponse<Member>> {
  const sp = new URLSearchParams();
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.search) sp.set("search", params.search);
  if (params?.organization_id) sp.set("organization_id", params.organization_id);
  if (params?.status) sp.set("status", params.status);
  if (params?.postal_code) sp.set("postal_code", params.postal_code);
  if (params?.has_tags) sp.set("has_tags", params.has_tags);
  if (params?.flagged) sp.set("flagged", "true");
  if (params?.include_kennitala) sp.set("include_kennitala", "true");
  if (params?.exclude_org_id) sp.set("exclude_org_id", params.exclude_org_id);
  if (params?.sortModel) sp.set("sort_model", params.sortModel);
  if (params?.filterModel) sp.set("filter_model", params.filterModel);
  return fetchApi(`/api/members?${sp.toString()}`);
}

export async function getMember(id: string, opts?: { includeKennitala?: boolean }): Promise<MemberDetail> {
  const q = opts?.includeKennitala ? "?include_kennitala=true" : "";
  return fetchApi(`/api/members/${id}${q}`);
}

/** Member detail by kennitala_hash — the /people route key. Throws ApiError
 *  with status 404 when the hash belongs to no member (i.e. a non-member
 *  person), which the /people page handles as the CRM-only view. */
export async function getMemberByHash(
  kennitalaHash: string,
  opts?: { includeKennitala?: boolean },
): Promise<MemberDetail> {
  const q = opts?.includeKennitala ? "?include_kennitala=true" : "";
  return fetchApi(`/api/members/by-hash/${encodeURIComponent(kennitalaHash)}${q}`);
}

/** Partial update: pass only the fields that changed; ``null`` clears one. */
export async function updateMember(
  id: string,
  data: Record<string, unknown>
): Promise<Member> {
  return fetchApi(`/api/members/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function getMemberHistory(id: string): Promise<{ items: MemberHistoryEvent[] }> {
  return fetchApi(`/api/members/${id}/history`);
}

export async function getMemberPermissionRows(
  id: string,
): Promise<{ items: MemberPermissionOrgRow[]; scope: "party" | "org" }> {
  return fetchApi(`/api/members/${id}/permissions`);
}

/** Assign a tag by id (the picker) or by name (typed). */
export async function addMemberTag(
  memberId: string,
  tag: { tag_id: string } | { name: string },
): Promise<{ id: string; tag_id: string; tag_name: string | null }> {
  return fetchApi(`/api/members/${memberId}/tags`, {
    method: "POST",
    body: JSON.stringify(tag),
  });
}

export async function removeMemberTag(
  memberId: string,
  tagId: string
): Promise<void> {
  return fetchApi(`/api/members/${memberId}/tags/${encodeURIComponent(tagId)}`, {
    method: "DELETE",
  });
}

export async function bulkTagMembers(
  memberIds: string[],
  tagId: string,
): Promise<{ applied: number; skipped: string[] }> {
  return fetchApi(`/api/members/bulk/tags`, {
    method: "POST",
    body: JSON.stringify({ member_ids: memberIds, tag_id: tagId }),
  });
}

// ---------- Organizations ----------

export async function getOrganizations(): Promise<Organization[]> {
  return fetchApi("/api/organizations");
}

export async function getOrgTree(): Promise<Organization[]> {
  return fetchApi("/api/organizations/tree");
}

export async function getOrganization(slug: string): Promise<OrganizationDetail> {
  return fetchApi(`/api/organizations/${slug}`);
}

export async function createOrganization(data: {
  name: string;
  slug: string;
  org_type: string;
  description?: string;
  parent_id?: string;
}): Promise<Organization> {
  return fetchApi("/api/organizations", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateOrganization(
  id: string,
  data: { name?: string; slug?: string; org_type?: string; description?: string; is_visible?: boolean; open_for_registration?: boolean },
): Promise<Organization> {
  return fetchApi(`/api/organizations/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function getAllPendingRequests(): Promise<MembershipRequest[]> {
  return fetchApi("/api/organizations/requests/pending");
}

export async function discoverOrgs(): Promise<DiscoverOrg[]> {
  return fetchApi("/api/organizations/discover");
}

export async function requestAccess(orgId: string): Promise<{ id: string; status: string }> {
  return fetchApi(`/api/organizations/${orgId}/request`, { method: "POST" });
}

export async function getOrgRequests(orgId: string): Promise<MembershipRequest[]> {
  return fetchApi(`/api/organizations/${orgId}/requests`);
}

export async function getOrgActivityLog(
  orgId: string,
  params?: { limit?: number; cursor?: string },
): Promise<{ items: ActivityEntry[]; has_more: boolean; next_cursor: string | null }> {
  const sp = new URLSearchParams();
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.cursor) sp.set("cursor", params.cursor);
  return fetchApi(`/api/organizations/${orgId}/activity?${sp.toString()}`);
}

export async function approveRequest(
  orgId: string,
  membershipId: string,
): Promise<{ id: string; status: string }> {
  return fetchApi(`/api/organizations/${orgId}/requests/${membershipId}/approve`, {
    method: "POST",
  });
}

export async function rejectRequest(
  orgId: string,
  membershipId: string,
): Promise<{ id: string; status: string }> {
  return fetchApi(`/api/organizations/${orgId}/requests/${membershipId}/reject`, {
    method: "POST",
  });
}

export async function addMemberToOrg(
  orgId: string,
  memberId: string,
): Promise<{ id: string; organization_id: string; member_id: string }> {
  return fetchApi(`/api/organizations/${orgId}/members`, {
    method: "POST",
    body: JSON.stringify({ member_id: memberId }),
  });
}

export async function bulkAddMembersToOrg(
  orgId: string,
  memberIds: string[],
): Promise<{ applied: number; skipped: string[] }> {
  return fetchApi(`/api/organizations/${orgId}/members/bulk`, {
    method: "POST",
    body: JSON.stringify({ member_ids: memberIds }),
  });
}

export async function removeOrgMember(
  orgId: string,
  memberId: string,
): Promise<void> {
  return fetchApi(`/api/organizations/${orgId}/members/${memberId}`, {
    method: "DELETE",
  });
}

// ---------- Events ----------

export async function getEvents(params?: {
  cursor?: string;
  limit?: number;
  organization_id?: string;
  upcoming?: boolean;
}): Promise<PaginatedResponse<Event>> {
  const sp = new URLSearchParams();
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.organization_id) sp.set("organization_id", params.organization_id);
  if (params?.upcoming !== undefined) sp.set("upcoming", String(params.upcoming));
  return fetchApi(`/api/events?${sp.toString()}`);
}

export async function getEvent(id: string): Promise<EventDetail> {
  return fetchApi(`/api/events/${id}`);
}

export async function createEvent(data: {
  title: string;
  description?: string;
  start_time: string;
  end_time?: string;
  location?: string;
  organization_id: string;
  event_type?: string;
  is_virtual?: boolean;
  virtual_url?: string;
  recurrence_rule?: string;
}): Promise<Event> {
  return fetchApi("/api/events", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function rsvpEvent(
  eventId: string,
  status: "attending" | "maybe" | "not_attending"
): Promise<void> {
  return fetchApi(`/api/events/${eventId}/rsvp`, {
    method: "POST",
    body: JSON.stringify({ status }),
  });
}

export async function toggleEventReminder(
  eventId: string,
  enabled: boolean
): Promise<{ event_id: string; enabled: boolean; method: string }> {
  return fetchApi(`/api/events/${eventId}/reminder`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}

export interface MyReminder {
  event_id: string;
  event_title: string;
  event_start_time: string;
  reminder_at: string | null;
  method: string;
}

export async function getMyReminders(): Promise<MyReminder[]> {
  return fetchApi("/api/events/my-reminders");
}

export async function checkInAttendee(
  eventId: string,
  memberId: string
): Promise<void> {
  return fetchApi(`/api/events/${eventId}/attendance`, {
    method: "POST",
    body: JSON.stringify({ member_id: memberId }),
  });
}

export async function removeAttendee(
  eventId: string,
  memberId: string
): Promise<void> {
  return fetchApi(`/api/events/${eventId}/attendance/${memberId}`, {
    method: "DELETE",
  });
}

// ---------- Event Recaps ----------

export async function getEventRecap(eventId: string): Promise<EventRecap> {
  return fetchApi(`/api/events/${eventId}/recap`);
}

export async function createEventRecap(
  eventId: string,
  data: { title: string; content?: string }
): Promise<EventRecap> {
  return fetchApi(`/api/events/${eventId}/recap`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function addRecapPhoto(
  eventId: string,
  data: { url: string; caption?: string }
): Promise<EventRecapPhoto> {
  return fetchApi(`/api/events/${eventId}/recap/photos`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function tagPhotoMembers(
  eventId: string,
  photoId: string,
  memberIds: string[]
): Promise<EventRecapPhoto> {
  return fetchApi(`/api/events/${eventId}/recap/photos/${photoId}/tags`, {
    method: "PUT",
    body: JSON.stringify({ member_ids: memberIds }),
  });
}

export async function getRecentRecaps(): Promise<EventRecap[]> {
  return fetchApi("/api/events/recaps/recent");
}

// ---------- Event CRUD ----------

export async function updateEvent(
  eventId: string,
  data: {
    title?: string;
    description?: string;
    start_time?: string;
    end_time?: string;
    location?: string;
    event_type?: string;
    is_virtual?: boolean;
    virtual_url?: string;
    max_attendees?: number | null;
  }
): Promise<Event> {
  return fetchApi(`/api/events/${eventId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function cancelEvent(eventId: string): Promise<void> {
  return fetchApi(`/api/events/${eventId}`, {
    method: "DELETE",
  });
}

export async function announceEvent(eventId: string): Promise<{ announced_at?: string; status?: string; approval_id?: string }> {
  return fetchApi(`/api/events/${eventId}/announce`, {
    method: "POST",
  });
}

// ---------- Event Feedback ----------

export interface EventFeedbackData {
  avg_rating: number | null;
  feedback_count: number;
  my_feedback: { rating: number; comment: string | null; created_at: string } | null;
}

export async function getEventFeedback(eventId: string): Promise<EventFeedbackData> {
  return fetchApi(`/api/events/${eventId}/feedback`);
}

export async function submitEventFeedback(
  eventId: string,
  data: { rating: number; comment?: string }
): Promise<{ rating: number; comment: string | null; created_at: string }> {
  return fetchApi(`/api/events/${eventId}/feedback`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------- iCal ----------

export async function generateIcalKey(): Promise<{ ical_feed_key: string }> {
  return fetchApi("/api/events/ical-key", { method: "POST" });
}

// ---------- Event Conflicts ----------

export async function checkEventConflicts(params: {
  organization_id: string;
  starts_at: string;
  ends_at: string;
  exclude_event_id?: string;
}): Promise<{ id: string; title: string; start_time: string }[]> {
  const sp = new URLSearchParams();
  sp.set("organization_id", params.organization_id);
  sp.set("starts_at", params.starts_at);
  sp.set("ends_at", params.ends_at);
  if (params.exclude_event_id) sp.set("exclude_event_id", params.exclude_event_id);
  return fetchApi(`/api/events/conflicts?${sp.toString()}`);
}

// ---------- Media ----------

export async function uploadMedia(
  file: File,
  organizationId: string,
  options?: { caption?: string; eventId?: string },
): Promise<MediaItem> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("organization_id", organizationId);
  if (options?.caption) formData.append("caption", options.caption);
  if (options?.eventId) formData.append("event_id", options.eventId);

  const url = `${API_BASE}/api/media/upload`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 120000); // 2 min for large files

  const headers: Record<string, string> = {};
  if (getViewMode() === "user") {
    headers["X-View-As"] = "user";
  }

  const doUpload = () =>
    fetch(url, {
      method: "POST",
      body: formData,
      signal: controller.signal,
      credentials: "include",
      headers,
      // Do NOT set Content-Type - browser sets it with boundary for multipart
    });

  try {
    let res = await doUpload();

    // Route a 401 through the same one-shot transparent refresh as fetchApi so
    // an expired short access token doesn't bounce an in-progress upload to
    // /login. FormData is reusable here because the body wasn't consumed by a
    // 401 (no successful stream).
    if (res.status === 401) {
      const outcome = await refreshAccessToken();
      if (outcome === "ok") {
        res = await doUpload();
      } else if (outcome === "network") {
        // Flaky link — don't log out mid-upload; let the caller retry.
        throw new ApiError(0, "Nettenging töpuð — reyndu aftur");
      }
      // "rejected" falls through to the redirect below.
    }

    if (res.status === 401) {
      redirectToLogin();
      throw new ApiError(401, "Óheimil aðgangur");
    }

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(
        res.status,
        body?.detail || `Villa ${res.status}`,
        body,
      );
    }

    return res.json();
  } finally {
    clearTimeout(timeout);
  }
}

export async function listMedia(params?: {
  cursor?: string;
  limit?: number;
  organization_id?: string;
  event_id?: string;
}): Promise<PaginatedResponse<MediaItem>> {
  const sp = new URLSearchParams();
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.organization_id) sp.set("organization_id", params.organization_id);
  if (params?.event_id) sp.set("event_id", params.event_id);
  return fetchApi(`/api/media?${sp.toString()}`);
}

export async function getMediaItem(id: string): Promise<MediaItem> {
  return fetchApi(`/api/media/${id}`);
}

export async function updateMedia(
  id: string,
  data: { caption?: string; duration_seconds?: number; width?: number; height?: number },
): Promise<{ ok: boolean; id: string; caption?: string }> {
  return fetchApi(`/api/media/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteMedia(id: string): Promise<void> {
  return fetchApi(`/api/media/${id}`, { method: "DELETE" });
}

export async function setMediaTags(
  mediaId: string,
  memberIds: string[],
): Promise<{ id: string; person_tags: MediaPersonTag[] }> {
  return fetchApi(`/api/media/${mediaId}/tags`, {
    method: "PUT",
    body: JSON.stringify({ member_ids: memberIds }),
  });
}

export async function attachMediaToEvent(
  mediaId: string,
  eventId: string,
): Promise<void> {
  return fetchApi(`/api/media/${mediaId}/events`, {
    method: "POST",
    body: JSON.stringify({ event_id: eventId }),
  });
}

export async function detachMediaFromEvent(
  mediaId: string,
  eventId: string,
): Promise<void> {
  return fetchApi(`/api/media/${mediaId}/events/${eventId}`, {
    method: "DELETE",
  });
}

export async function setEventCoverImage(
  eventId: string,
  mediaId: string,
): Promise<void> {
  return fetchApi(`/api/events/${eventId}/cover-image`, {
    method: "PUT",
    body: JSON.stringify({ media_id: mediaId }),
  });
}

export async function removeEventCoverImage(
  eventId: string,
): Promise<void> {
  return fetchApi(`/api/events/${eventId}/cover-image`, {
    method: "DELETE",
  });
}

export async function searchTaggableMembers(
  organizationId: string,
  search: string = "",
  limit: number = 5,
  eventId?: string,
): Promise<TaggableMember[]> {
  const sp = new URLSearchParams();
  sp.set("organization_id", organizationId);
  if (search) sp.set("search", search);
  sp.set("limit", String(limit));
  if (eventId) sp.set("event_id", eventId);
  return fetchApi(`/api/media/taggable-members?${sp.toString()}`);
}

/** @deprecated Use searchTaggableMembers instead */
export async function getTaggableMembers(
  organizationId: string,
): Promise<TaggableMember[]> {
  return searchTaggableMembers(organizationId, "", 20);
}

// ---------- Media moderation ----------

export async function getPendingMedia(params?: {
  organization_id?: string;
  cursor?: string;
  limit?: number;
}): Promise<PaginatedResponse<MediaItem> & { total_pending: number }> {
  const sp = new URLSearchParams();
  if (params?.organization_id) sp.set("organization_id", params.organization_id);
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return fetchApi(`/api/media/pending${qs ? `?${qs}` : ""}`);
}

export async function approveMedia(...ids: string[]): Promise<{ approved: number; total: number }> {
  return fetchApi("/api/media/approve", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export async function rejectMedia(mediaId: string): Promise<void> {
  return fetchApi(`/api/media/${mediaId}/reject`, { method: "POST" });
}

// ---------- Polls ----------

export async function getPolls(params?: {
  cursor?: string;
  limit?: number;
  organization_id?: string;
  phase?: string;
  poll_type?: string;
}): Promise<PaginatedResponse<Poll>> {
  const sp = new URLSearchParams();
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.organization_id) sp.set("organization_id", params.organization_id);
  if (params?.phase) sp.set("phase", params.phase);
  if (params?.poll_type) sp.set("poll_type", params.poll_type);
  return fetchApi(`/api/polls?${sp.toString()}`);
}

export async function getPoll(id: string): Promise<Poll> {
  return fetchApi(`/api/polls/${id}`);
}

export async function getPollResults(id: string): Promise<PollResults> {
  return fetchApi(`/api/polls/${id}/results`);
}

export async function submitPollResponse(
  pollId: string,
  answers: PollAnswer[]
): Promise<void> {
  return fetchApi(`/api/polls/${pollId}/respond`, {
    method: "POST",
    body: JSON.stringify({ answers }),
  });
}

export async function createPoll(data: {
  title: string;
  description?: string;
  poll_type: PollType;
  anonymity_level: AnonymityLevel;
  organization_id: string;
  target_org_ids?: string[];
  questions: {
    text: string;
    description?: string;
    question_type: QuestionType;
    options?: string[];
    required: boolean;
    min_selections?: number;
    max_selections?: number;
  }[];
  briefings?: {
    title: string;
    content_markdown: string;
    source_url?: string;
    order: number;
  }[];
  min_participation_pct?: number;
  opens_at?: string;
  closes_at?: string;
  // Election-specific
  election_type?: ElectionType;
  candidate_registration_deadline?: string;
  voter_eligibility_rules?: Record<string, unknown>;
}): Promise<Poll> {
  return fetchApi("/api/polls", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getPollArguments(pollId: string): Promise<PollArgument[]> {
  const res = await fetchApi<{ arguments: PollArgument[] }>(`/api/polls/${pollId}/arguments`);
  return res.arguments;
}

export async function addPollArgument(
  pollId: string,
  data: { stance: "pro" | "con"; title: string; body: string }
): Promise<PollArgument> {
  return fetchApi(`/api/polls/${pollId}/arguments`, {
    method: "POST",
    body: JSON.stringify({ side: data.stance, title: data.title, body: data.body }),
  });
}

export async function votePollArgument(
  pollId: string,
  argumentId: string
): Promise<void> {
  return fetchApi(`/api/polls/${pollId}/arguments/${argumentId}/vote`, {
    method: "POST",
  });
}

export async function updatePollPhase(
  pollId: string,
  phase: PollPhase
): Promise<void> {
  return fetchApi(`/api/polls/${pollId}/phase`, {
    method: "PATCH",
    body: JSON.stringify({ phase }),
  });
}

export async function deletePoll(id: string): Promise<void> {
  await fetchApi(`/api/polls/${id}`, { method: "DELETE" });
}

// ---------- Discussions ----------

export interface Discussion {
  id: string;
  title: string;
  body: string;
  category: string | null;
  organization_id: string;
  author_id: string;
  author_name: string;
  is_pinned: boolean;
  is_locked: boolean;
  reply_count: number;
  upvote_count: number;
  last_reply_at: string | null;
  created_at: string;
  updated_at: string;
  my_upvote: boolean;
}

export interface DiscussionReply {
  id: string;
  discussion_id: string;
  parent_reply_id: string | null;
  author_id: string;
  author_name: string;
  body: string;
  upvote_count: number;
  created_at: string;
  updated_at: string;
  my_upvote: boolean;
  child_replies: DiscussionReply[];
}

export interface DiscussionDetail extends Discussion {
  replies: DiscussionReply[];
}

export async function getDiscussions(params?: {
  org_id?: string;
  category?: string;
  limit?: number;
  cursor?: string;
}): Promise<PaginatedResponse<Discussion>> {
  const sp = new URLSearchParams();
  if (params?.org_id) sp.set("org_id", params.org_id);
  if (params?.category) sp.set("category", params.category);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.cursor) sp.set("cursor", params.cursor);
  // The backend route is defined with a trailing slash; calling it directly
  // avoids a 307 redirect round trip on every list load.
  return fetchApi(`/api/discussions/?${sp.toString()}`);
}

export async function getDiscussion(id: string): Promise<DiscussionDetail> {
  return fetchApi(`/api/discussions/${id}`);
}

export async function createDiscussion(data: {
  title: string;
  body: string;
  category?: string;
  organization_id: string;
}): Promise<Discussion> {
  return fetchApi("/api/discussions/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function createDiscussionReply(
  discussionId: string,
  data: { body: string; parent_reply_id?: string }
): Promise<DiscussionReply> {
  return fetchApi(`/api/discussions/${discussionId}/replies`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function toggleDiscussionUpvote(
  discussionId: string
): Promise<{ upvoted: boolean; new_count: number }> {
  return fetchApi(`/api/discussions/${discussionId}/upvote`, {
    method: "POST",
  });
}

export async function toggleReplyUpvote(
  replyId: string
): Promise<{ upvoted: boolean; new_count: number }> {
  return fetchApi(`/api/discussions/replies/${replyId}/upvote`, {
    method: "POST",
  });
}

export async function toggleDiscussionPin(
  discussionId: string
): Promise<{ is_pinned: boolean }> {
  return fetchApi(`/api/discussions/${discussionId}/pin`, {
    method: "POST",
  });
}

export async function toggleDiscussionLock(
  discussionId: string
): Promise<{ is_locked: boolean }> {
  return fetchApi(`/api/discussions/${discussionId}/lock`, {
    method: "POST",
  });
}

export async function updateDiscussion(
  id: string,
  data: { body: string }
): Promise<{ id: string; body: string; updated_at: string }> {
  return fetchApi(`/api/discussions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteDiscussion(id: string): Promise<void> {
  await fetchApi(`/api/discussions/${id}`, { method: "DELETE" });
}

// ---------- Donations ----------

export async function getMyDonations(): Promise<PaginatedResponse<Donation>> {
  return fetchApi("/api/donations/mine");
}

export async function getDonations(params?: {
  cursor?: string;
  limit?: number;
  organization_id?: string;
}): Promise<PaginatedResponse<Donation>> {
  const sp = new URLSearchParams();
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.organization_id) sp.set("organization_id", params.organization_id);
  return fetchApi(`/api/donations?${sp.toString()}`);
}

export async function getDonationSummary(
  organizationId?: string
): Promise<DonationSummary> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organization_id", organizationId);
  return fetchApi(`/api/donations/summary?${sp.toString()}`);
}

export async function createDonationCheckout(data: {
  amount: number;
  organization_id: string;
  frequency: string;
  promotion_code?: string;
}): Promise<{ checkout_url: string; reference: string }> {
  return fetchApi("/api/donations/checkout", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getBankTransferInfo(): Promise<{ title: string; body: string }> {
  return fetchApi("/api/donations/bank-transfer-info");
}

/** Create a Kling bank claim (krafa) for a one-time donation billed to the
 *  logged-in member's own kennitala. The krafa lands in their online banking. */
export async function createDonationClaim(data: {
  amount: number;
  organization_id: string;
}): Promise<{ claim_id: string; status: string }> {
  return fetchApi("/api/donations/claim", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Public (no-login) donation. Identified by kennitala — anonymous political
 *  donations are prohibited. Returns a Kling checkout URL to redirect to.
 *  prefill_token (from a newsletter ?b= link) only adds campaign attribution. */
export async function publicDonate(data: {
  amount: number;
  name: string;
  kennitala: string;
  email?: string | null;
  prefill_token?: string | null;
}): Promise<{ checkout_url: string; reference?: string }> {
  return fetchApi("/api/public/donate", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Public (no-login) MONTHLY donation — sets up a Kling subscription.
export async function publicDonateRecurring(data: {
  amount: number;
  name: string;
  kennitala: string;
  email?: string | null;
  prefill_token?: string | null;
}): Promise<{ checkout_url: string }> {
  return fetchApi("/api/public/donate-recurring", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface DonationPrefill {
  name: string | null;
  kennitala: string | null;
  suggested_amount: number | null;
}

/** Prefill for a newsletter's per-recipient styrkja link (?b=<signed token>).
 *  Stateless server-side verification; 404 = invalid/expired. */
export async function getDonationPrefill(token: string): Promise<DonationPrefill> {
  return fetchApi(`/api/public/donation-prefill/${encodeURIComponent(token)}`);
}

// ---------- Canvasser donation offers / volunteer referrals ----------

export interface DonationOfferResult {
  id: string;
  method: "claim" | "link";
  status: string;
  sms_sent: boolean;
}

/** "Vill styrkja": create a bank-claim (krafa) or self-serve link offer.
 *  ``client_request_id`` is the caller-minted idempotency key: a timeout+retry
 *  of the same configurator session must not create two real bank claims. */
export async function createDonationOffer(
  campaignId: string,
  data: {
    kennitala_hash: string;
    method: "claim" | "link";
    amount?: number | null;
    client_request_id?: string;
  },
): Promise<DonationOfferResult> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/donation-offers`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** "Vill vera sjálfboðaliði" — SMS the person a portal-login volunteer-confirm link. */
export async function createVolunteerReferral(
  campaignId: string,
  data: { kennitala_hash: string },
): Promise<{ id: string; status: string; sms_sent: boolean }> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/volunteer-referrals`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface PublicDonationOffer {
  status: string; // sent | reminded | paid | expired
  election_label: string | null;
  suggested_amount?: number | null;
  name?: string | null;
  kennitala?: string | null;
  muted?: boolean;
}

export async function getPublicDonationOffer(token: string): Promise<PublicDonationOffer> {
  return fetchApi(`/api/public/donation-offer/${token}`);
}

export async function donateViaOffer(
  token: string,
  data: {
    amount: number;
    name: string;
    kennitala: string;
    email?: string | null;
    frequency?: "once" | "monthly";
  },
): Promise<{ checkout_url: string }> {
  return fetchApi(`/api/public/donation-offer/${token}/donate`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function mutePublicDonationOffer(token: string): Promise<{ muted: boolean }> {
  return fetchApi(`/api/public/donation-offer/${token}/mute`, { method: "POST" });
}

export async function getVolunteerReferral(token: string): Promise<{
  status: string;
  source: string;
  election_label: string | null;
  confirmed: boolean;
  muted: boolean;
  /** Person already volunteers (profile flag or a prior confirmed token). */
  already_volunteer?: boolean;
  /** Per-person signed styrkja link (prefill + campaign attribution). */
  styrkja_link?: string | null;
}> {
  return fetchApi(`/api/public/volunteer-referral/${token}`);
}

export async function muteVolunteerReferral(token: string): Promise<{ muted: boolean }> {
  return fetchApi(`/api/public/volunteer-referral/${token}/mute`, { method: "POST" });
}

// Newsletter signup: confirm WITHOUT a login (the token is the identity proof).
export async function confirmVolunteerSignup(
  token: string,
): Promise<{ status: string; confirmed: boolean; election_label: string | null }> {
  return fetchApi(`/api/public/volunteer-signup/${token}/confirm`, { method: "POST" });
}

// Email unsubscribe (no login; signed token). GET reads status, POST unsubscribes.
export async function getUnsubscribeStatus(
  token: string,
): Promise<{ valid: boolean; unsubscribed: boolean }> {
  return fetchApi(`/api/public/unsubscribe/${token}`);
}

export async function postUnsubscribe(token: string): Promise<{ unsubscribed: boolean; valid: boolean }> {
  return fetchApi(`/api/public/unsubscribe/${token}`, { method: "POST" });
}

export async function confirmVolunteerReferral(
  token: string,
): Promise<{ status: string; confirmed: boolean; election_label: string | null }> {
  return fetchApi(`/api/volunteer-referrals/${token}/confirm`, { method: "POST" });
}

export interface FundraisingSummary {
  election_id: string;
  total_raised: number;
  donation_count: number;
  unique_donors: number;
  by_day: { day: string; amount: number; count: number }[];
  by_source_type: { source_type: string | null; amount: number; count: number }[];
}

export async function getElectionFundraisingSummary(electionId: string): Promise<FundraisingSummary> {
  return fetchApi(`/api/elections/${electionId}/fundraising-summary`);
}

export async function createDonation(data: {
  donor_name: string;
  donor_kennitala_hash?: string;
  amount: number;
  method: string;
  reference?: string;
  organization_id: string;
  donated_at: string;
}): Promise<Donation> {
  return fetchApi("/api/donations", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------- Admin ----------

export async function getAuditLog(params?: {
  cursor?: string;
  limit?: number;
  action?: string;
  actor_id?: string;
  actor_name?: string;
  organization_id?: string;
}): Promise<PaginatedResponse<AuditLogEntry>> {
  const sp = new URLSearchParams();
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.action) sp.set("action", params.action);
  if (params?.actor_id) sp.set("actor_id", params.actor_id);
  if (params?.actor_name) sp.set("actor_name", params.actor_name);
  if (params?.organization_id) sp.set("organization_id", params.organization_id);
  return fetchApi(`/api/admin/audit-logs?${sp.toString()}`);
}

export interface ExportDeliveryEntry {
  id: string;
  actor_id: string | null;
  actor_name: string | null;
  walk_list_id: string | null;
  walk_list_name: string | null;
  mode: string | null;
  row_count: number | null;
  ip_address: string | null;
  created_at: string;
}

export async function getExportDeliveries(params?: {
  cursor?: string;
  limit?: number;
}): Promise<PaginatedResponse<ExportDeliveryEntry>> {
  const sp = new URLSearchParams();
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return fetchApi(`/api/admin/exports${qs ? `?${qs}` : ""}`);
}


export async function getGDPRRequests(): Promise<PaginatedResponse<GDPRRequest>> {
  return fetchApi("/api/admin/gdpr/requests");
}

export async function processGDPRRequest(
  id: string,
  action: "approve" | "reject"
): Promise<void> {
  return fetchApi(`/api/admin/gdpr/requests/${id}/process`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}


// ---------- Member Permissions ----------

export interface MemberPermissionRow {
  member_id: string | null;
  member_name: string | null;
  kennitala?: string | null;
  kennitala_hash?: string | null;
  organization_id: string;
  organization_name: string;
  permissions: string[];
  assigned_by_name?: string | null;
  assigned_at?: string | null;
  status?: "active" | "pending";
  // False when the grant sits on an org the person is not a member of;
  // null for pending rows (nobody to check yet).
  member_of_org?: boolean | null;
}

export async function getAllMemberPermissions(): Promise<MemberPermissionRow[]> {
  return fetchApi("/api/admin/member-permissions/all");
}

export async function getMemberPermissions(
  memberId: string,
  organizationId: string,
): Promise<string[]> {
  const sp = new URLSearchParams();
  sp.set("member_id", memberId);
  sp.set("organization_id", organizationId);
  return fetchApi(`/api/admin/member-permissions?${sp.toString()}`);
}

export interface PermissionHistoryEntry {
  id: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_kennitala: string | null;
  permissions: string[];
  created_at: string | null;
}

export async function getPermissionHistory(
  memberId: string,
  organizationId: string,
): Promise<PermissionHistoryEntry[]> {
  const sp = new URLSearchParams();
  sp.set("member_id", memberId);
  sp.set("organization_id", organizationId);
  return fetchApi(`/api/admin/member-permissions/history?${sp.toString()}`);
}

export async function setMemberPermissions(
  memberId: string,
  organizationId: string,
  permissions: string[],
): Promise<void> {
  return fetchApi("/api/admin/member-permissions", {
    method: "PUT",
    body: JSON.stringify({
      member_id: memberId,
      organization_id: organizationId,
      permissions,
    }),
  });
}

// ---------- Kennitala Lookup + Pending Permissions ----------

export interface KennitalaLookupResult {
  found: boolean;
  member?: { id: string; name: string; kennitala: string | null; organization_ids?: string[] };
  kennitala_hash: string;
}

export async function lookupMemberByKennitala(
  kennitala: string,
): Promise<KennitalaLookupResult> {
  const sp = new URLSearchParams();
  sp.set("kennitala", kennitala);
  return fetchApi(`/api/admin/member-lookup-by-kennitala?${sp.toString()}`);
}

export async function setPendingPermissions(
  opts: { kennitala: string; kennitalaHash?: undefined } | { kennitala?: undefined; kennitalaHash: string },
  organizationId: string,
  permissions: string[],
): Promise<void> {
  return fetchApi("/api/admin/pending-permissions", {
    method: "PUT",
    body: JSON.stringify({
      kennitala: opts.kennitala ?? null,
      kennitala_hash: opts.kennitalaHash ?? null,
      organization_id: organizationId,
      permissions,
    }),
  });
}

export async function getPendingPermissions(
  kennitalaHash: string,
  organizationId: string,
): Promise<string[]> {
  const sp = new URLSearchParams();
  sp.set("kennitala_hash", kennitalaHash);
  sp.set("organization_id", organizationId);
  return fetchApi(`/api/admin/pending-permissions?${sp.toString()}`);
}

export async function deletePendingPermissions(
  kennitalaHash: string,
  organizationId: string,
): Promise<void> {
  return fetchApi("/api/admin/pending-permissions", {
    method: "DELETE",
    body: JSON.stringify({
      kennitala_hash: kennitalaHash,
      organization_id: organizationId,
    }),
  });
}

export interface PermissionCatalogRow {
  resource: string;
  action: string;
  description?: string | null;
}

/** The permission catalog as the database holds it; the only list the editor renders. */
export async function getAvailablePermissions(): Promise<PermissionCatalogRow[]> {
  return fetchApi("/api/admin/permissions");
}

export interface PermissionPreset {
  label: string;
  description: string;
  permissions: string[];
}

export interface PermissionPresets {
  /** Bundles the trustee registry pushes by role (formaður, gjaldkeri). */
  trustee: Record<string, PermissionPreset>;
  /** Presets the Heimildir editor offers (yfirstjornandi, stjornandi, skipuleggjandi). */
  admin: Record<string, PermissionPreset>;
}

export async function getPermissionPresets(): Promise<PermissionPresets> {
  return fetchApi("/api/admin/permission-presets");
}

// ---------- Global admin management ----------

export interface GlobalAdmin {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string | null;
}

export async function getGlobalAdmins(): Promise<GlobalAdmin[]> {
  return fetchApi("/api/admin/global-admins");
}

export async function setGlobalAdmin(data: {
  member_id: string;
  grant: boolean;
  confirm_name: string;
}): Promise<{ id: string; name: string; is_global_admin: boolean }> {
  return fetchApi("/api/admin/global-admin", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------- Feature Flags ----------

export interface FeatureFlag {
  key: string;
  label: string;
  enabled: boolean;
  updated_at: string | null;
}

export async function getFeatureFlags(): Promise<FeatureFlag[]> {
  return fetchApi("/api/admin/features");
}

export async function toggleFeatureFlag(
  key: string,
  enabled: boolean,
): Promise<FeatureFlag> {
  return fetchApi(`/api/admin/features/${key}`, {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });
}

// ---------- Message Templates ----------

export interface MessageTemplate {
  key: string;
  title: string;
  body: string;
  description: string;
  variables: string[];
  is_default: boolean;
  updated_at: string | null;
}

export async function getMessageTemplates(): Promise<MessageTemplate[]> {
  return fetchApi("/api/admin/templates");
}

export async function updateMessageTemplate(
  key: string,
  data: { title: string; body: string },
): Promise<MessageTemplate> {
  return fetchApi(`/api/admin/templates/${key}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ---------- Config Warnings ----------

export interface ConfigWarning {
  key: string;
  message: string;
}

export async function getConfigWarnings(): Promise<{ warnings: ConfigWarning[] }> {
  return fetchApi("/api/admin/config-warnings");
}

// ---------- Maintenance mode ----------

export interface MaintenanceStatus {
  enabled: boolean;
  message: string;
}

/** Public endpoint — no auth required. */
export async function getMaintenanceStatus(): Promise<MaintenanceStatus> {
  return fetchApi("/api/maintenance/status");
}

/** Admin-only — get maintenance mode for admin UI. */
export async function getMaintenanceMode(): Promise<MaintenanceStatus> {
  return fetchApi("/api/admin/maintenance");
}

/** Admin-only — toggle maintenance mode. */
export async function setMaintenanceMode(
  enabled: boolean,
  message: string,
): Promise<MaintenanceStatus> {
  return fetchApi("/api/admin/maintenance", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled, message }),
  });
}

// ---------- Announcement banner ----------

export type AnnouncementColor = "primary" | "yellow" | "orange" | "red";

export interface AnnouncementStatus {
  message: string;
  color: AnnouncementColor;
}

/** Authenticated — get announcement status for all users. */
export async function getAnnouncementStatus(): Promise<AnnouncementStatus | null> {
  return fetchApi("/api/announcement/status");
}

/** Admin-only — get announcement for admin UI. */
export async function getAnnouncementMode(): Promise<AnnouncementStatus | null> {
  return fetchApi("/api/admin/announcement");
}

/** Admin-only — enable/update announcement. */
export async function setAnnouncementMode(
  message: string,
  color: AnnouncementColor,
): Promise<AnnouncementStatus> {
  return fetchApi("/api/admin/announcement", {
    method: "PUT",
    body: JSON.stringify({ message, color }),
  });
}

/** Admin-only — disable announcement. */
export async function deleteAnnouncement(): Promise<void> {
  return fetchApi("/api/admin/announcement", {
    method: "DELETE",
  });
}

// ---------- Moderation (Mute/Blacklist/Appeals) ----------

export interface BlacklistAppeal {
  id: string;
  member_id: string;
  member_name: string;
  message: string;
  status: string;
  created_at: string;
}

export async function submitAppeal(message: string): Promise<{ id: string; status: string }> {
  return fetchApi("/api/appeals", {
    method: "POST",
    body: JSON.stringify({ message }),
  });
}

export async function adminMuteMember(
  memberId: string
): Promise<{ id: string; is_muted: boolean }> {
  return fetchApi(`/api/admin/members/${memberId}/mute`, {
    method: "POST",
  });
}

export async function adminBlacklistMember(
  memberId: string
): Promise<{ id: string; is_blacklisted: boolean }> {
  return fetchApi(`/api/admin/members/${memberId}/blacklist`, {
    method: "POST",
  });
}

export async function adminDeactivateMember(
  memberId: string
): Promise<{ id: string; is_active: boolean }> {
  return fetchApi(`/api/admin/members/${memberId}/deactivate`, {
    method: "POST",
  });
}

export async function getAppeals(): Promise<{ items: BlacklistAppeal[]; total_count: number }> {
  return fetchApi("/api/admin/appeals");
}

export async function approveAppeal(
  appealId: string,
  note?: string
): Promise<{ id: string; status: string }> {
  return fetchApi(`/api/admin/appeals/${appealId}/approve`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function rejectAppeal(
  appealId: string,
  note?: string
): Promise<{ id: string; status: string }> {
  return fetchApi(`/api/admin/appeals/${appealId}/reject`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

// ---------- Dashboard ----------

export interface DashboardStats {
  member_count: number;
  active_orgs: number;
  upcoming_events: number;
  active_polls: number;
}

export interface RecentActivity {
  items: ActivityEntry[];
}

export async function getDashboardStats(
  organizationId?: string
): Promise<DashboardStats> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organization_id", organizationId);
  return fetchApi(`/api/dashboard/stats?${sp.toString()}`);
}

export async function getRecentActivity(
  organizationId?: string
): Promise<RecentActivity> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organization_id", organizationId);
  return fetchApi(`/api/dashboard/activity?${sp.toString()}`);
}

export async function getUpcomingEvents(
  organizationId?: string
): Promise<Event[]> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organization_id", organizationId);
  sp.set("limit", "5");
  return fetchApi(`/api/dashboard/upcoming-events?${sp.toString()}`);
}

// ---------- Engagement ----------

export interface EngagementDistribution {
  high: number;
  medium: number;
  low: number;
  inactive: number;
}

export interface AtRiskMember {
  id: string;
  name: string;
  engagement_score: number;
  last_activity_at: string | null;
  organization_name: string | null;
}

export async function getEngagementDistribution(
  organizationId?: string
): Promise<EngagementDistribution> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organization_id", organizationId);
  return fetchApi(`/api/dashboard/engagement?${sp.toString()}`);
}

export async function getAtRiskMembers(
  organizationId?: string
): Promise<{ items: AtRiskMember[] }> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organization_id", organizationId);
  return fetchApi(`/api/dashboard/at-risk?${sp.toString()}`);
}

// ---------- Member Dashboard ----------

export async function getMyActivePolls(
  organizationId?: string
): Promise<Poll[]> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organization_id", organizationId);
  return fetchApi(`/api/dashboard/my-polls?${sp.toString()}`);
}

export async function getMyTaggedMedia(params?: {
  limit?: number;
  cursor?: string;
}): Promise<PaginatedResponse<MediaItem>> {
  const sp = new URLSearchParams();
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.cursor) sp.set("cursor", params.cursor);
  return fetchApi(`/api/dashboard/my-media?${sp.toString()}`);
}

export async function getMyUpcomingEvents(
  organizationId?: string
): Promise<Event[]> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organization_id", organizationId);
  sp.set("limit", "6");
  return fetchApi(`/api/dashboard/my-events?${sp.toString()}`);
}

export interface MemberSummary {
  contribution: {
    events_attended: number;
    polls_answered: number;
    volunteer_hours: number;
    donation_total: number;
  };
  next_event: {
    id: string;
    title: string;
    start_time: string;
    location?: string;
  } | null;
  community: {
    new_members_30d: number;
  };
  volunteer_shifts: {
    id: string;
    role_name: string;
    event_title: string;
    start_time: string;
    spots_left: number;
  }[];
  hot_discussions: {
    id: string;
    title: string;
    reply_count: number;
    organization_id: string;
  }[];
  member_since: string | null;
}

export async function getMyDashboardSummary(
  organizationId?: string,
): Promise<MemberSummary> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organization_id", organizationId);
  return fetchApi(`/api/dashboard/my-summary?${sp.toString()}`);
}

// ---------- Notifications ----------

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  is_read: boolean;
  created_at: string;
}

export interface NotificationList {
  items: Notification[];
  unread_count: number;
}

export async function getNotifications(unreadOnly?: boolean): Promise<NotificationList> {
  const params = new URLSearchParams();
  if (unreadOnly) params.set("unread_only", "true");
  const qs = params.toString();
  return fetchApi<NotificationList>(`/api/notifications/${qs ? `?${qs}` : ""}`);
}

export async function getUnreadNotificationCount(): Promise<{ count: number }> {
  return fetchApi<{ count: number }>("/api/notifications/unread-count");
}

export async function markNotificationsRead(ids: string[]): Promise<void> {
  await fetchApi("/api/notifications/mark-read", {
    method: "POST",
    body: JSON.stringify({ notification_ids: ids }),
  });
}

export async function markAllNotificationsRead(): Promise<void> {
  await fetchApi("/api/notifications/mark-all-read", { method: "POST" });
}

// ---------- Skilaboð (Inbox) ----------

export interface InboxNotification extends Notification {
  category: string | null;
}

export async function getInboxNotifications(params?: {
  cursor?: string;
  limit?: number;
  unread_only?: boolean;
  category?: string;
}): Promise<PaginatedResponse<InboxNotification> & { unread_count: number }> {
  const sp = new URLSearchParams();
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.unread_only) sp.set("unread_only", "true");
  if (params?.category) sp.set("category", params.category);
  const qs = sp.toString();
  return fetchApi(`/api/skilabod/${qs ? `?${qs}` : ""}`);
}

export async function markInboxRead(ids: string[]): Promise<void> {
  await fetchApi("/api/skilabod/mark-read", {
    method: "POST",
    body: JSON.stringify({ notification_ids: ids }),
  });
}

export async function markAllInboxRead(): Promise<void> {
  await fetchApi("/api/skilabod/mark-all-read", { method: "POST" });
}

export async function archiveInboxNotifications(ids: string[]): Promise<void> {
  await fetchApi("/api/skilabod/archive", {
    method: "POST",
    body: JSON.stringify({ notification_ids: ids }),
  });
}

// --- Dues ---

export interface MembershipDue {
  id: string;
  member_id: string;
  member_name: string | null;
  organization_id: string;
  organization_name: string | null;
  organization_slug: string | null;
  payment_id: string | null;
  year: number;
  amount: number;
  status: string;
  description: string | null;
  due_date: string | null;
  paid_at: string | null;
  created_at: string | null;
  active_claim: {
    id: string;
    status: string;
    kling_claim_id: string;
  } | null;
}

export async function fetchMyDues(): Promise<{ items: MembershipDue[]; has_more: boolean; total_count: number }> {
  return fetchApi("/api/dues/mine");
}

export async function fetchMyDuesByOrg(organizationId: string): Promise<{ items: MembershipDue[]; total_count: number }> {
  return fetchApi(`/api/dues/mine/by-org?organization_id=${organizationId}`);
}

export async function fetchDues(params: {
  organization_id?: string;
  year?: number;
  status?: string;
  cursor?: string;
  limit?: number;
}): Promise<{ items: MembershipDue[]; has_more: boolean; total_count: number }> {
  const search = new URLSearchParams();
  if (params.organization_id) search.set("organization_id", params.organization_id);
  if (params.year) search.set("year", String(params.year));
  if (params.status) search.set("status", params.status);
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit) search.set("limit", String(params.limit));
  return fetchApi(`/api/dues?${search}`);
}

export interface MemberDueStatus {
  member_id: string;
  member_name: string | null;
  kennitala: string | null;
  due_id: string | null;
  amount: number | null;
  status: string | null; // null = no due record
  due_date: string | null;
  paid_at: string | null;
  created_at: string | null;
}

export async function fetchMemberDuesStatus(params: {
  organization_id: string;
  year: number;
  status?: string;
  cursor?: string;
  limit?: number;
}): Promise<{ items: MemberDueStatus[]; has_more: boolean }> {
  const search = new URLSearchParams();
  search.set("organization_id", params.organization_id);
  search.set("year", String(params.year));
  if (params.status) search.set("status", params.status);
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit) search.set("limit", String(params.limit));
  return fetchApi(`/api/dues/member-status?${search}`);
}

export interface MemberDuesRow {
  member_id: string;
  member_name: string | null;
  years: { year: number; status: string; amount: number }[];
}

export async function fetchDuesByMember(organizationId: string): Promise<{ items: MemberDuesRow[] }> {
  return fetchApi(`/api/dues/by-member?organization_id=${organizationId}`);
}

export async function generateDues(body: {
  organization_id: string;
  year: number;
  amount: number;
  due_date?: string;
  payment_deadline_days?: number;
}): Promise<{ generated: number; status: "generated" | "pending_approval"; approval_id?: string }> {
  return fetchApi("/api/dues/generate", { method: "POST", body: JSON.stringify(body) });
}

export async function checkoutDue(dueId: string): Promise<{ checkout_url: string; reference: string }> {
  return fetchApi(`/api/dues/${dueId}/checkout`, { method: "POST" });
}

export async function withdrawDue(dueId: string): Promise<unknown> {
  return fetchApi(`/api/dues/${dueId}/withdraw`, { method: "POST" });
}

export async function deleteDue(dueId: string): Promise<{ ok: boolean }> {
  return fetchApi(`/api/dues/${dueId}`, { method: "DELETE" });
}

export async function fetchDuesStats(organizationId: string, year?: number): Promise<{
  total_dues: number;
  paid: number;
  unpaid: number;
  collection_rate: number;
  collected_amount: number;
}> {
  const search = new URLSearchParams({ organization_id: organizationId });
  if (year) search.set("year", String(year));
  return fetchApi(`/api/dues/stats?${search}`);
}

// --- Due Periods ---

export interface DuePeriod {
  id: string;
  organization_id: string;
  organization_name: string | null;
  year: number;
  amount: number;
  status: string; // active | frozen
  due_date: string | null;
  payment_deadline_days: number | null;
  created_at: string | null;
  frozen_at: string | null;
  total_count: number;
  paid_count: number;
}

export async function fetchDuePeriods(): Promise<{ items: DuePeriod[] }> {
  return fetchApi("/api/dues/periods");
}

export async function freezeDuePeriod(periodId: string): Promise<DuePeriod> {
  return fetchApi(`/api/dues/periods/${periodId}/freeze`, { method: "POST" });
}

export async function unfreezeDuePeriod(periodId: string): Promise<DuePeriod> {
  return fetchApi(`/api/dues/periods/${periodId}/unfreeze`, { method: "POST" });
}

// --- Claims (bank claims / kröfur) ---

export interface PaymentClaim {
  id: string;
  due_id: string | null;
  member_id: string;
  organization_id: string;
  kling_claim_id: string;
  amount: number;
  status: "pending" | "overdue" | "paid" | "canceled" | "escalated";
  claim_type: "mandatory" | "voluntary";
  description: string | null;
  due_date: string | null;
  final_due_date: string | null;
  created_at: string | null;
}

export async function sendClaimForDue(dueId: string): Promise<PaymentClaim> {
  return fetchApi(`/api/dues/${dueId}/send-claim`, { method: "POST" });
}

export async function bulkSendClaims(data: { organization_id: string; year: number }): Promise<{ enqueued: number }> {
  return fetchApi("/api/dues/bulk-claims", { method: "POST", body: JSON.stringify(data) });
}

export async function getClaimForDue(dueId: string): Promise<PaymentClaim | null> {
  return fetchApi(`/api/dues/${dueId}/claim`);
}

export async function cancelClaim(claimId: string): Promise<{ id: string; status: string }> {
  return fetchApi(`/api/claims/${claimId}/cancel`, { method: "POST" });
}

export async function escalateClaim(claimId: string): Promise<{ id: string; status: string }> {
  return fetchApi(`/api/claims/${claimId}/escalate`, { method: "POST" });
}

export async function fetchMyClaims(): Promise<{ items: PaymentClaim[]; total_count: number }> {
  return fetchApi("/api/claims/mine");
}

export async function checkoutClaim(claimId: string): Promise<{ checkout_url: string; reference: string }> {
  return fetchApi(`/api/claims/${claimId}/checkout`, { method: "POST" });
}

// --- Payment Links ---

export interface PaymentLink {
  session_id: string;
  url: string;
  short_url: string;
  amount: number;
  description: string;
  status: "open" | "complete" | "expired";
  reusable: boolean;
  payment_count: number;
  created_at: string | null;
}

export async function createPaymentLink(data: { amount: number; description: string; organization_id: string; reusable?: boolean }): Promise<PaymentLink> {
  return fetchApi("/api/payments/links", { method: "POST", body: JSON.stringify(data) });
}

export async function deletePaymentLink(sessionId: string): Promise<{ ok: boolean }> {
  return fetchApi(`/api/payments/links/${sessionId}`, { method: "DELETE" });
}

export async function getPaymentLinks(params?: { limit?: number; organization_id?: string }): Promise<{ items: PaymentLink[] }> {
  const sp = new URLSearchParams();
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.organization_id) sp.set("organization_id", params.organization_id);
  const qs = sp.toString();
  return fetchApi(`/api/payments/links${qs ? `?${qs}` : ""}`);
}

// --- Subscriptions (recurring donations) ---

export interface DonationSubscription {
  id: string;
  amount: number;
  currency: string;
  status: "trialing" | "active" | "past_due" | "canceled";
  collection_method: string;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  created_at: string | null;
}

export async function createDonationSubscription(data: { amount: number; organization_id: string }): Promise<{ checkout_url: string }> {
  return fetchApi("/api/donations/subscribe", { method: "POST", body: JSON.stringify(data) });
}

export async function getMySubscriptions(): Promise<{ items: DonationSubscription[] }> {
  return fetchApi("/api/donations/subscriptions");
}

export async function cancelSubscription(subscriptionId: string): Promise<DonationSubscription> {
  return fetchApi(`/api/donations/subscriptions/${subscriptionId}/cancel`, { method: "POST" });
}

// --- Promotion Codes ---

export async function validatePromotionCode(code: string, subtotal?: number): Promise<Record<string, unknown>> {
  return fetchApi("/api/promotions/validate", { method: "POST", body: JSON.stringify({ code, subtotal }) });
}

// --- Refunds ---

export async function refundPayment(paymentId: string, data?: { amount?: number; reason?: string }): Promise<{ status: string; refunded_amount: number }> {
  return fetchApi(`/api/payments/${paymentId}/refund`, { method: "POST", body: JSON.stringify(data ?? {}) });
}

// --- Briefings ---

export async function createBriefing(pollId: string, body: {
  title: string;
  content_markdown: string;
  source_url?: string;
  order?: number;
}): Promise<Record<string, unknown>> {
  return fetchApi(`/api/polls/${pollId}/briefings`, { method: "POST", body: JSON.stringify(body) });
}

export async function updateBriefing(pollId: string, briefingId: string, body: {
  title?: string;
  content_markdown?: string;
  source_url?: string;
  order?: number;
}): Promise<Record<string, unknown>> {
  return fetchApi(`/api/polls/${pollId}/briefings/${briefingId}`, { method: "PUT", body: JSON.stringify(body) });
}

export async function deleteBriefing(pollId: string, briefingId: string): Promise<void> {
  await fetchApi(`/api/polls/${pollId}/briefings/${briefingId}`, { method: "DELETE" });
}

// --- Election Candidates ---

export async function registerCandidate(pollId: string, statement?: string): Promise<ElectionCandidate> {
  return fetchApi(`/api/polls/${pollId}/candidates`, {
    method: "POST",
    body: JSON.stringify({ statement }),
  });
}

export async function fetchCandidates(pollId: string): Promise<{ poll_id: string; candidates: ElectionCandidate[]; total_count: number }> {
  return fetchApi(`/api/polls/${pollId}/candidates`);
}

export async function approveCandidate(pollId: string, candidateId: string): Promise<ElectionCandidate> {
  return fetchApi(`/api/polls/${pollId}/candidates/${candidateId}/approve`, { method: "POST" });
}

export async function fetchElectionResults(pollId: string): Promise<IrvResults> {
  return fetchApi(`/api/polls/${pollId}/election-results`);
}

// --- Discussion Reports ---

export async function reportDiscussion(discussionId: string, reason: string): Promise<Record<string, unknown>> {
  return fetchApi(`/api/discussions/${discussionId}/report`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function reportReply(replyId: string, reason: string): Promise<Record<string, unknown>> {
  return fetchApi(`/api/discussions/replies/${replyId}/report`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function fetchReports(status?: string): Promise<{ items: Record<string, unknown>[] }> {
  const search = status ? `?status=${status}` : "";
  return fetchApi(`/api/discussions/admin/reports${search}`);
}

export async function resolveReport(reportId: string, action: string): Promise<Record<string, unknown>> {
  return fetchApi(`/api/discussions/admin/reports/${reportId}/resolve`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

// --- Donation Receipts ---

export async function fetchDonationReceipt(donationId: string): Promise<{ receipt_url: string }> {
  return fetchApi(`/api/donations/${donationId}/receipt`);
}

// ---------- Campaigns ----------

// The v2 block document edited by the in-place newsletter editor. Type-only
// import: the shapes live beside the editor, the API client just carries them.
import type { NewsletterDoc } from "@/components/campaigns/editor/types";
export type { NewsletterDoc } from "@/components/campaigns/editor/types";

export interface NewsletterStat {
  value: string;
  label: string;
}

export interface NewsletterCta {
  enabled: boolean;
  label: string;
  url?: string | null;
  /** Per-recipient prefilled styrkja link ({{styrkja_link}}); url is ignored. */
  personalized?: boolean;
  /** "button" (filled navy) or "link" (centered text link). */
  style?: "button" | "link";
}

/** Structured newsletter-composer fields. The backend renders these into
 *  subject/html_body/body_template server-side (with HTML escaping); merge
 *  tags {{nafn}}/{{skrad}} survive and expand per recipient at send time. */
export interface NewsletterData {
  template_key?: "election_2026" | "afram_island_2026" | "fundarbod_local";
  subject: string;
  greeting: string;
  intro_paragraphs: string[];
  /** Horizontal rule between the intro block and what follows. */
  divider_after_intro?: boolean;
  stats: NewsletterStat[];
  call_paragraphs: string[];
  tagline: string;
  volunteer_cta: NewsletterCta;
  donation_cta: NewsletterCta;
  signoff: string;
}

export interface Campaign {
  id: string;
  title: string;
  description: string | null;
  campaign_type: "email" | "sms";
  status: "draft" | "scheduled" | "sending" | "sent" | "failed" | "pending_approval" | "rejected";
  subject: string | null;
  /** Optional A/B subject; recipients split by UUID parity when set. */
  subject_b?: string | null;
  /** Sender display name override (email only): recipients see
   *  "Nafn" <no-reply@xd.is>. Null = the global default name. */
  from_name?: string | null;
  /** SMS sender ID override (sms only): the alphanumeric string recipients see
   *  as the message sender. Null = the global default ("XD"). Max 11 chars. */
  sms_sender_id?: string | null;
  body_template: string;
  /** Rendered HTML (email newsletters); sent multipart with body_template. */
  html_body?: string | null;
  content_format?: "text" | "html";
  /** Election the {{sjalfbodalidi_link}} volunteer CTA enrols into. */
  election_id?: string | null;
  /** Composer source fields; present only for UI-authored newsletters.
   *  Either the legacy v1 shape or the v2 block document (version: 2). */
  newsletter_data?: NewsletterData | NewsletterDoc | null;
  /** The v2 block document for the editor: identity for v2 campaigns, a
   *  deterministic upconversion for v1. Detail GET only. */
  newsletter_doc?: NewsletterDoc | null;
  /** Hard send gate: HTML campaigns refuse to mass-send until test-sent. */
  last_test_sent_at?: string | null;
  organization_id: string | null;
  organization_name?: string;
  created_by: string;
  creator_name?: string;
  scheduled_at: string | null;
  sent_at: string | null;
  total_recipients: number;
  delivered_count: number;
  failed_count: number;
  /** Live engagement counts — DETAIL GET only (absent in lists). Opens come
   *  from the Postmark webhook; clicks are first-party per-link stamps. */
  opened_count?: number | null;
  clicked_count?: number | null;
  clicked_volunteer_count?: number | null;
  clicked_styrkja_count?: number | null;
  clicked_folk_count?: number | null;
  bounced_count?: number | null;
  /** Subject A/B split (only when subject_b is set), by recipient-UUID parity. */
  ab_delivered_a?: number | null;
  ab_delivered_b?: number | null;
  ab_opened_a?: number | null;
  ab_opened_b?: number | null;
  /** The slice of failed_count that could never be sent to (no address on
   *  file, opted out). failed_count minus this = real send failures. Present
   *  on detail AND list responses for campaigns in sending/sent/failed. */
  unreachable_count?: number | null;
  filter_tags: string[] | null;
  filter_postal_codes: string[] | null;
  filter_org_ids: string[] | null;
  filter_org_names?: string[] | null;
  /** react-querybuilder DSL over sensitive.people — the population-wide
   *  targeting that supersedes the flat filter_* fields above. */
  filter_json?: QueryGroup | null;
  created_at: string;
  updated_at: string;
}

export async function getCampaigns(params: {
  organization_id?: string;
  status?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}): Promise<PaginatedResponse<Campaign>> {
  const sp = new URLSearchParams();
  if (params.organization_id) sp.set("org_id", params.organization_id);
  if (params.status) sp.set("status", params.status);
  if (params.search) sp.set("search", params.search);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.cursor) sp.set("cursor", params.cursor);
  return fetchApi(`/api/campaigns?${sp.toString()}`);
}

export async function createCampaign(data: Partial<Campaign>): Promise<Campaign> {
  return fetchApi("/api/campaigns", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getCampaign(id: string): Promise<Campaign> {
  return fetchApi(`/api/campaigns/${id}`);
}

export async function updateCampaign(id: string, data: Partial<Campaign>): Promise<Campaign> {
  return fetchApi(`/api/campaigns/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteCampaign(id: string): Promise<void> {
  return fetchApi(`/api/campaigns/${id}`, { method: "DELETE" });
}

export async function previewCampaignRecipients(id: string): Promise<{ count: number }> {
  return fetchApi(`/api/campaigns/${id}/preview-recipients`, { method: "POST" });
}

export async function sendCampaign(id: string): Promise<void> {
  return fetchApi(`/api/campaigns/${id}/send`, { method: "POST" });
}

/** One cause-bucket of people a walk-list campaign will NOT reach: already
 *  dispositioned, utan kosningakerfis, á stofnun, firewall, ... Keys come
 *  from the backend breakdown; labels are backend-supplied Icelandic. */
export interface AudienceExclusionBucket {
  key: string;
  label: string;
  count: number;
  /** delivery-gap report only: may this bucket feed a follow-up send? */
  selectable?: boolean;
  /** delivery-gap report only: per-person rows, capped server-side. */
  people?: { kennitala_hash: string; name: string | null }[];
}

export interface AudienceExclusions {
  total: number;
  buckets: AudienceExclusionBucket[];
}

export async function estimateRecipients(
  data: Partial<Campaign>,
): Promise<{
  recipient_count: number;
  unreachable_count?: number;
  /** Present when the filter references a walk list: who is ON the list but
   *  will NOT get the message, bucketed by cause (the audience truth panel). */
  excluded?: AudienceExclusions;
}> {
  return fetchApi("/api/campaigns/estimate-recipients", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Post-send "Hverjir fengu ekki skilaboðin?" report: failed recipients by
 *  error plus (walk-list campaigns) the never-in-audience exclusion buckets,
 *  with names. */
export async function getCampaignDeliveryGap(id: string): Promise<{
  failed: AudienceExclusions;
  excluded: AudienceExclusions | null;
}> {
  return fetchApi(`/api/campaigns/${id}/delivery-gap`);
}

/** Create the gap-closing follow-up (draft walk list + draft SMS campaign)
 *  from selected delivery-gap bucket keys. Never sends. */
export async function createCampaignFollowUp(
  id: string,
  buckets: string[],
): Promise<{ campaign_id: string; walk_list_id: string; count: number }> {
  return fetchApi(`/api/campaigns/${id}/follow-up`, {
    method: "POST",
    body: JSON.stringify({ buckets }),
  });
}

export async function duplicateCampaign(id: string): Promise<Campaign> {
  return fetchApi(`/api/campaigns/${id}/duplicate`, { method: "POST" });
}

/** Query-builder field catalogue for herferðir viðtakendur (population-wide
 *  over sensitive.people), scoped to the caller's targetable orgs. When
 *  ``electionId`` is passed, the "Merkt með málefni" field is included with
 *  that election's applied issue-tags as options. */
export async function getCampaignFilterFields(electionId?: string): Promise<FieldDefinition[]> {
  const qs = electionId ? `?election_id=${encodeURIComponent(electionId)}` : "";
  return fetchApi(`/api/campaigns/filter-fields${qs}`);
}

/** One option in the "Er eftir á lista" value picker. ``label`` is the full
 *  "Kosning – Herferð – Listi" path; the parts are kept split so the picker can
 *  group results under an election/campaign header. ``is_live`` is false only
 *  for a stored list whose election has closed (id-resolution path). */
export interface WalkListOption {
  value: string;
  label: string;
  election_name: string;
  campaign_name: string;
  list_name: string;
  list_type: string;
  is_live: boolean;
}

/** Searchable walk/call-list options for the herferðir "Er eftir á lista" field.
 *  Pass ``q`` to token-search live elections' lists, or ``ids`` to resolve labels
 *  for already-stored values (edit flow, resolves across all statuses). The field
 *  catalogue only seeds the active elections' master lists, so the full set lives
 *  behind this endpoint. */
export async function searchWalkListOptions(params: {
  q?: string;
  ids?: string[];
  limit?: number;
}): Promise<{ options: WalkListOption[]; truncated: boolean }> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.ids?.length) qs.set("ids", params.ids.join(","));
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return fetchApi(`/api/campaigns/walk-list-options${suffix}`);
}

/** Newsletter composer presets ("Nota síðasta fréttabréf"). */
export async function getNewsletterTemplates(): Promise<
  { key: string; label: string; data: NewsletterData; doc: NewsletterDoc }[]
> {
  return fetchApi(`/api/campaigns/newsletter-templates`);
}

/** Server-side render + sample expansion for the composer's live preview. */
export async function previewNewsletter(
  data: NewsletterData | NewsletterDoc,
): Promise<{ subject: string; html: string; text: string }> {
  return fetchApi(`/api/campaigns/newsletter-preview`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Send ONE copy of a draft campaign to an arbitrary address; stamps the
 *  last_test_sent_at gate the mass send requires. */
export async function testSendCampaign(
  id: string,
  email: string,
): Promise<{ status: string; to: string; last_test_sent_at: string }> {
  return fetchApi(`/api/campaigns/${id}/test-send`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

// ---------- Tags ----------

export interface TagCategory {
  id: string;
  /** null = global (org-less) category. */
  organization_id: string | null;
  name: string;
  color: string;
  tags: { id: string; category_id: string; name: string }[];
}

/** Create a tag in a category. Global admins only (the backend gate). */
export async function createTag(categoryId: string, name: string): Promise<{ id: string; category_id: string; name: string }> {
  return fetchApi(`/api/tags`, {
    method: "POST",
    body: JSON.stringify({ category_id: categoryId, name }),
  });
}

/** With an org: that org's categories. Without: the categories the caller
 *  may assign FROM (their tags.manage orgs, plus global for root holders and
 *  global admins), which is what the tag picker needs. */
export async function getTagCategories(
  organizationId?: string,
  opts?: { scope?: "visible" },
): Promise<TagCategory[]> {
  if (organizationId) return fetchApi(`/api/tags/categories?organization_id=${organizationId}`);
  return fetchApi(opts?.scope ? `/api/tags/categories?scope=${opts.scope}` : `/api/tags/categories`);
}

// ---------- Documents ----------

export type DocumentCategory = "stefnuskra" | "log" | "fundargerdir" | "arsskyrsla" | "annad";

export interface DocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  file_url: string;
  file_name: string;
  file_size: number | null;
  uploaded_by: string;
  uploader_name?: string;
  change_note?: string;
  created_at: string;
}

export interface Document {
  id: string;
  title: string;
  description?: string;
  category: DocumentCategory;
  file_url?: string;
  file_name?: string;
  file_size?: number;
  content_type?: string;
  organization_id: string;
  event_id?: string;
  poll_id?: string;
  uploaded_by: string;
  uploader_name: string;
  version: number;
  is_public: boolean;
  versions_count: number;
  created_at: string;
  updated_at: string;
}

export interface DocumentDetail extends Document {
  versions: DocumentVersion[];
  event_title?: string;
  poll_title?: string;
}

export async function getDocuments(params: {
  organization_id: string;
  category?: string;
  limit?: number;
  cursor?: string;
}): Promise<PaginatedResponse<Document>> {
  const sp = new URLSearchParams();
  sp.set("organization_id", params.organization_id);
  if (params.category) sp.set("category", params.category);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.cursor) sp.set("cursor", params.cursor);
  return fetchApi(`/api/documents?${sp.toString()}`);
}

export async function getDocument(id: string): Promise<DocumentDetail> {
  return fetchApi(`/api/documents/${id}`);
}

export async function createDocument(formData: FormData): Promise<DocumentDetail> {
  const url = `${API_BASE}/api/documents`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  const extraHeaders: Record<string, string> = {};
  if (getViewMode() === "user") {
    extraHeaders["X-View-As"] = "user";
  }

  try {
    const res = await fetch(url, {
      method: "POST",
      signal: controller.signal,
      credentials: "include",
      headers: extraHeaders,
      body: formData,
    });

    if (res.status === 401) {
      if (typeof window !== "undefined" && window.location.pathname !== "/login" && window.location.pathname !== "/callback") window.location.href = "/login";
      throw new ApiError(401, "Óheimil aðgangur");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
    }
    return res.json();
  } finally {
    clearTimeout(timeout);
  }
}

export async function updateDocument(
  id: string,
  data: { title?: string; description?: string; category?: string; is_public?: boolean }
): Promise<DocumentDetail> {
  return fetchApi(`/api/documents/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteDocument(id: string): Promise<void> {
  return fetchApi(`/api/documents/${id}`, { method: "DELETE" });
}

export async function addDocumentVersion(
  documentId: string,
  formData: FormData
): Promise<DocumentVersion> {
  const url = `${API_BASE}/api/documents/${documentId}/versions`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  const extraHeaders: Record<string, string> = {};
  if (getViewMode() === "user") {
    extraHeaders["X-View-As"] = "user";
  }

  try {
    const res = await fetch(url, {
      method: "POST",
      signal: controller.signal,
      credentials: "include",
      headers: extraHeaders,
      body: formData,
    });

    if (res.status === 401) {
      if (typeof window !== "undefined" && window.location.pathname !== "/login" && window.location.pathname !== "/callback") window.location.href = "/login";
      throw new ApiError(401, "Óheimil aðgangur");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
    }
    return res.json();
  } finally {
    clearTimeout(timeout);
  }
}

// ---------- Volunteers ----------

export interface VolunteerRole {
  id: string;
  name: string;
  description: string | null;
  organization_id: string;
  created_at: string;
}

export interface VolunteerSignup {
  id: string;
  shift_id: string;
  member_id: string;
  member_name: string;
  status: string;
  hours_logged: number | null;
  created_at: string;
}

export interface VolunteerShift {
  id: string;
  event_id: string;
  role_id: string;
  role_name: string;
  start_time: string;
  end_time: string;
  required_count: number;
  signed_up_count: number;
  signups: VolunteerSignup[];
  created_at: string;
}

export interface VolunteerHistory {
  id: string;
  shift_id: string;
  event_id: string;
  event_title: string;
  role_name: string;
  start_time: string;
  end_time: string;
  status: string;
  hours_logged: number | null;
  created_at: string;
}

export interface TopVolunteer {
  member_id: string;
  member_name: string;
  total_hours: number;
  total_signups: number;
}

export interface VolunteerStats {
  total_hours: number;
  total_signups: number;
  top_volunteers: TopVolunteer[];
}

export async function getVolunteerRoles(
  orgId: string
): Promise<VolunteerRole[]> {
  return fetchApi(`/api/volunteers/roles?org_id=${orgId}`);
}

export async function createVolunteerRole(data: {
  name: string;
  description?: string;
  organization_id: string;
}): Promise<VolunteerRole> {
  return fetchApi("/api/volunteers/roles", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getVolunteerShifts(
  eventId: string
): Promise<VolunteerShift[]> {
  return fetchApi(`/api/volunteers/shifts?event_id=${eventId}`);
}

export async function createVolunteerShift(data: {
  event_id: string;
  role_id: string;
  start_time: string;
  end_time: string;
  required_count: number;
}): Promise<VolunteerShift> {
  return fetchApi("/api/volunteers/shifts", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function signupForShift(shiftId: string): Promise<void> {
  return fetchApi(`/api/volunteers/shifts/${shiftId}/signup`, {
    method: "POST",
  });
}

export async function cancelShiftSignup(shiftId: string): Promise<void> {
  return fetchApi(`/api/volunteers/shifts/${shiftId}/signup`, {
    method: "DELETE",
  });
}

export async function completeVolunteerSignup(
  signupId: string,
  hours: number
): Promise<void> {
  return fetchApi(`/api/volunteers/signups/${signupId}/complete`, {
    method: "POST",
    body: JSON.stringify({ hours }),
  });
}

export async function getVolunteerStats(
  orgId?: string
): Promise<VolunteerStats> {
  const sp = new URLSearchParams();
  if (orgId) sp.set("org_id", orgId);
  return fetchApi(`/api/volunteers/stats?${sp.toString()}`);
}

export async function getMyVolunteerHistory(): Promise<VolunteerHistory[]> {
  return fetchApi("/api/volunteers/my-history");
}

// ---------- Tasks ----------

export type TaskStatus = "todo" | "in_progress" | "review" | "done";
export type TaskPriority = "low" | "medium" | "high" | "urgent";

export interface TaskBoard {
  id: string;
  name: string;
  description?: string;
  organization_id: string;
  created_by: string;
  is_archived: boolean;
  created_at: string;
  task_counts: Record<string, number>;
}

export interface TaskItem {
  id: string;
  board_id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigned_to?: string;
  assigned_to_name?: string;
  created_by: string;
  creator_name: string;
  due_date?: string;
  completed_at?: string;
  sort_order: number;
  // Member-facing self-signup (Starfið / Verkefni in the app).
  open_for_signup: boolean;
  capacity?: number | null;
  starts_at?: string | null;
  location?: string | null;
  signup_count: number;
  created_at: string;
  updated_at: string;
}

export interface TaskSignup {
  id: string;
  member_id: string;
  member_name: string;
  created_at: string;
  completed_at: string | null;
}

// Task fields a manager can set alongside the classic ones.
export interface TaskSignupFields {
  open_for_signup?: boolean;
  capacity?: number | null;
  starts_at?: string | null;
  location?: string | null;
}

export async function getTaskBoards(orgId: string): Promise<TaskBoard[]> {
  return fetchApi(`/api/tasks/boards?org_id=${orgId}`);
}

export async function createTaskBoard(data: {
  name: string;
  description?: string;
  organization_id: string;
}): Promise<TaskBoard> {
  return fetchApi("/api/tasks/boards", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getTaskBoard(boardId: string): Promise<TaskBoard> {
  return fetchApi(`/api/tasks/boards/${boardId}`);
}

export async function getBoardTasks(
  boardId: string,
  params?: { status?: string; assigned_to?: string }
): Promise<TaskItem[]> {
  const sp = new URLSearchParams();
  if (params?.status) sp.set("status", params.status);
  if (params?.assigned_to) sp.set("assigned_to", params.assigned_to);
  return fetchApi(`/api/tasks/boards/${boardId}/tasks?${sp.toString()}`);
}

export async function createTask(
  boardId: string,
  data: {
    title: string;
    description?: string;
    status?: TaskStatus;
    priority?: TaskPriority;
    assigned_to?: string;
    due_date?: string;
  } & TaskSignupFields
): Promise<TaskItem> {
  return fetchApi(`/api/tasks/boards/${boardId}/tasks`, {
    method: "POST",
    body: JSON.stringify({ ...data, board_id: boardId }),
  });
}

export async function updateTask(
  taskId: string,
  data: {
    title?: string;
    description?: string;
    status?: TaskStatus;
    priority?: TaskPriority;
    assigned_to?: string | null;
    due_date?: string | null;
  } & TaskSignupFields
): Promise<TaskItem> {
  return fetchApi(`/api/tasks/tasks/${taskId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function getTaskSignups(taskId: string): Promise<TaskSignup[]> {
  return fetchApi(`/api/tasks/tasks/${taskId}/signups`);
}

export async function completeTaskSignup(
  taskId: string,
  memberId: string
): Promise<TaskSignup[]> {
  return fetchApi(`/api/tasks/tasks/${taskId}/signups/${memberId}/complete`, {
    method: "PATCH",
  });
}

export async function moveTask(
  taskId: string,
  status: TaskStatus
): Promise<TaskItem> {
  return fetchApi(`/api/tasks/tasks/${taskId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function deleteTask(taskId: string): Promise<void> {
  return fetchApi(`/api/tasks/tasks/${taskId}`, { method: "DELETE" });
}

export async function getMyTasks(): Promise<TaskItem[]> {
  return fetchApi("/api/tasks/my-tasks");
}


// ---------- Advanced Analytics ----------

/** Shared scope every analytics tab respects: org drill-down + time window. */
export type AnalyticsRange = "30d" | "90d" | "12mo" | "all";

export interface AnalyticsScope {
  orgId?: string | null;
  range?: AnalyticsRange;
}

function scopeQuery(scope?: AnalyticsScope): string {
  const params = new URLSearchParams();
  if (scope?.orgId) params.set("organization_id", scope.orgId);
  if (scope?.range) params.set("range", scope.range);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export interface MemberGrowth {
  label: string;
  total: number;
  new: number;
  churned: number;
}

export interface AgeDistribution {
  range: string;
  count: number;
}

export interface GenderDistribution {
  gender: string;
  count: number;
}

export interface GeographicDistribution {
  postal_code: string;
  count: number;
}

export interface EngagementFunnel {
  total: number;
  active_30d: number;
  event_attendee: number;
  poll_voter: number;
  donor: number;
}

export type OrgHealthLevel = "good" | "medium" | "weak" | "idle";

export interface OrgHealth {
  org_name: string;
  member_count: number;
  events_30d: number;
  participation_rate: number | null;
  health: OrgHealthLevel;
}

export interface VolunteerPipeline {
  willing: number;
  signed_up: number;
  completed: number;
}

export interface AnalyticsData {
  org_health: OrgHealth[];
  volunteer_pipeline: VolunteerPipeline;
}

export async function getAnalytics(scope?: AnalyticsScope): Promise<AnalyticsData> {
  return fetchApi(`/api/dashboard/analytics${scopeQuery(scope)}`);
}

export async function exportAnalyticsCsv(): Promise<Blob> {
  const url = `${API_BASE}/api/dashboard/analytics/export`;
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    throw new ApiError(res.status, "Villa við útflutning");
  }
  return res.blob();
}

// --- Per-Tab Analytics ---

export interface AnalyticsOverview {
  total_members: number;
  avg_engagement_score: number;
  new_this_month: number;
  dues_collection_rate: number;
  member_growth: MemberGrowth[];
  engagement_funnel: EngagementFunnel;
}

export interface AnalyticsDemographics {
  age_distribution: AgeDistribution[];
  gender_distribution: GenderDistribution[];
  geographic: GeographicDistribution[];
  contact_stats: { email: number; phone: number; sms: number };
  volunteer_willing_count: number;
  total_active: number;
}

export interface EngagementDistribution {
  high: number;
  medium: number;
  low: number;
  inactive: number;
}

export interface AtRiskMember {
  id: string;
  name: string;
  engagement_score: number;
  last_activity_at: string | null;
  organization_name: string | null;
}

export interface SeriesPoint {
  label: string;
  count: number;
}

export interface RetentionCohort {
  cohort: string;
  joined: number;
  retained: number;
  rate: number;
}

export interface EventFunnel {
  rsvp: number;
  attended: number;
}

export interface PollTurnout {
  eligible: number;
  voted: number;
  rate: number;
}

export interface AnalyticsEngagement {
  distribution: EngagementDistribution;
  at_risk: AtRiskMember[];
  event_participation: SeriesPoint[];
  poll_participation: SeriesPoint[];
  retention: RetentionCohort[];
  event_funnel: EventFunnel;
  poll_turnout: PollTurnout;
}

export interface DuesAgingBucket {
  bucket: string;
  count: number;
  amount: number;
}

export interface AnalyticsFinances {
  donations: DonationSummary;
  dues: {
    total_dues: number;
    paid: number;
    unpaid: number;
    collection_rate: number;
    collected_amount: number;
  };
  dues_aging: DuesAgingBucket[];
}

export async function getAnalyticsOverview(scope?: AnalyticsScope): Promise<AnalyticsOverview> {
  return fetchApi(`/api/dashboard/analytics/overview${scopeQuery(scope)}`);
}

export async function getAnalyticsDemographics(scope?: AnalyticsScope): Promise<AnalyticsDemographics> {
  return fetchApi(`/api/dashboard/analytics/demographics${scopeQuery(scope)}`);
}

export async function getAnalyticsEngagement(scope?: AnalyticsScope): Promise<AnalyticsEngagement> {
  return fetchApi(`/api/dashboard/analytics/engagement${scopeQuery(scope)}`);
}

export async function getAnalyticsFinances(scope?: AnalyticsScope): Promise<AnalyticsFinances> {
  return fetchApi(`/api/dashboard/analytics/finances${scopeQuery(scope)}`);
}

// ---------- Canvassing ----------

export type CanvassingCampaignType = "door" | "phone" | "combined";
/**
 * Geographic scope of a canvassing campaign. ``municipality`` (default) covers
 * one svfnr; ``national`` ("landslisti") covers the whole country and carries a
 * NULL svfnr. Everything below the campaign (territories, walk lists, split,
 * allocation, work views) is identical for both scopes.
 */
export type CampaignScopeType = "municipality" | "national";
export type WalkListMode = "canvass" | "call" | "drive";
export type CanvassingCampaignStatus =
  | "planning"
  | "building"
  | "active"
  | "paused"
  | "completed"
  | "archived";
export type TerritoryStatus =
  | "unassigned"
  | "assigned"
  | "in_progress"
  | "completed";
export type Disposition =
  | "callback_yes"
  | "callback_no"
  | "not_home"
  | "wrong_number"
  | "inactive_number"
  | "already_voted"
  | "message_sent";

export const POSITIVE_DISPOSITIONS = new Set<Disposition>(["callback_yes"]);
export const NEGATIVE_DISPOSITIONS = new Set<Disposition>(["callback_no"]);
export const NO_CONTACT_DISPOSITIONS = new Set<Disposition>(["not_home", "wrong_number", "inactive_number", "already_voted"]);
export type ContactType = "door" | "phone" | "sms" | "email" | "other";

export interface CanvassingCampaign {
  id: string;
  name: string;
  svfnr?: string;
  /** "municipality" (default) or "national" (landslisti — whole country, NULL svfnr). */
  scope_type?: CampaignScopeType;
  municipality_name?: string;
  description?: string;
  campaign_type: CanvassingCampaignType;
  status: CanvassingCampaignStatus;
  start_date: string;
  end_date?: string;
  retention_until: string;
  goal_contacts: number;
  actual_contacts: number;
  briefing_md?: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  organization_id: string;
  election_id?: string;
  kjordaemi?: string;
  manager_image_url?: string | null;
  manager_message?: string | null;
}

export interface Territory {
  id: string;
  campaign_id: string;
  organization_id: string;
  name: string;
  /** NULL for the master territory of a national ("Allt landið") campaign. */
  svfnr: string | null;
  postal_codes?: string[];
  assigned_to?: string;
  status: TerritoryStatus;
  priority: number;
  notes?: string;
  created_by: string | null;
  created_at: string;
  target_count?: number;
  contacted_count?: number;
  walk_list_count?: number;
}

export interface CanvassingTarget {
  id: string;
  kennitala_hash: string;
  is_member: boolean;
  banned?: boolean;
  name: string;
  address?: string;
  postal_code?: string;
  svfnr?: string;
  /**
   * TRUE when the svfnr was overridden by the enrichment worker because the
   * kjörskrá row marks this target "Lögheimili utan Grindavíkur" (displaced
   * post-2023 eruption). UI renders a badge so canvassers know the physical
   * address is outside the svfnr-attributed municipality.
   */
  svfnr_overridden?: boolean;
  street_name?: string;
  house_number?: number;
  house_letter?: string;
  lat?: number;
  lon?: number;
  heinum?: number;
  apartment?: string;
  contact_attempts?: number;
  contact_score?: number | null;
  age?: number | null;
  phone_number?: string | null;
  phone_secondary?: string | null;
  /**
   * Extra numbers from Símaskrá beyond primary/secondary. Each entry is
   * `{ phone: string }` — no per-element type tag; the UI treats them
   * as fallback tap-to-call rows after the two main slots.
   */
  additional_phones?: Array<{ phone: string }> | null;
  /**
   * Set once a final (non-`not_home`) disposition has been attached.
   * The walk-list grid grays these rows out and the call page
   * auto-advances past them; the chunk endpoint and SMS bulk send
   * filter them out server-side.
   */
  final_disposition?: string | null;
}

export interface WalkListAssignee {
  id: string;
  name?: string;
  photo_url?: string;
  is_mine: boolean;
}

export interface WalkList {
  id: string;
  territory_id: string;
  campaign_id: string;
  election_id?: string;
  name: string;
  assignees: WalkListAssignee[];
  // Backward-compat (derived from first assignee)
  assigned_to?: string;
  assigned_name?: string;
  assigned_photo_url?: string;
  parent_walk_list_id?: string;
  filter_json?: QueryGroup | null;
  list_type: "default" | "personal" | "regular";
  mode: WalkListMode | null;
  /** True when a Kosningastjóri/admin has flagged this list assignable by
   *  volunteer managers (Mannauðsstjóri). Drives the "Úthluta á lista" flow. */
  volunteer_assignable?: boolean;
  /** True when this list is open to every active party member (no assignment
   *  needed). Drives the dashboard's "hringja í flokksmenn" hero CTA. */
  open_to_all_members?: boolean;
  status: string;
  /** Set by the idempotent admin create-personal-list endpoints: true when
   *  THIS call created the list, false when an existing one was returned
   *  (the UI branches its toast). Null/absent on every other surface. */
  created?: boolean | null;
  total_entries: number | null;
  completed_entries: number | null;
  // Outreach response breakdown — populated by batch_walk_list_live_counts
  // alongside total/completed. Streams in via NDJSON updates the same way
  // total_entries does; null while a row is in skeleton state.
  callback_yes: number | null;
  callback_no: number | null;
  already_voted: number | null;
  no_contact: number | null;
  unreached: number | null;
  // "Óákveðinn" sub-counts of the callback buckets. Only GET /walk-lists/{id}
  // computes them (detail card's four-row spectrum); null/absent everywhere
  // else and the card falls back to the two-row layout.
  callback_yes_undecided?: number | null;
  callback_no_undecided?: number | null;
  // Election shell for the /call page, populated ONLY by GET /walk-lists/{id}.
  // A plain volunteer 403s on GET /elections/{id}, so the softphone gate and
  // Hjálp modal read these off the (assignment-authorized) list instead.
  calling_enabled?: boolean | null;
  election_day?: string | null;
  early_voting_md?: string | null;
  has_help_clip?: boolean | null;
  /** Election-day screen of /call ("Kjördagshamur"): one column, name, age,
   *  Hringja, two outcome buttons, and the switch's verdicts filed by the
   *  machine. Mirrored per dispatch on NextCallerResponse too. */
  kjordagur_mode?: boolean | null;
  /** Admin-edited location notes (elections.call_location_notes), matched to
   *  the person by postal code on the call page. */
  call_location_notes?: LocationNote[] | null;
  // Audience-mode counts for shrinking lists (Fyrstu samskipti subtree, Má
  // hringja aftur, SPEZ, Keyra á kjörstað). Members of those lists are by
  // definition the uncontacted/unfinished slice, so the bucket counts above
  // collapse to 0 — these come from a parallel audience CTE built with the
  // shrinking predicates stripped, so they reflect the static audience the
  // list represents (demographic for first_contact, callback_yes pool for
  // callback). null on non-shrinking lists; UI falls back to the bucket
  // fields above. progress_total/progress_completed are the unified
  // Framvinda denominator/numerator the backend computes per list type so
  // every surface renders the same percentage with the same definition.
  audience_total?: number | null;
  audience_callback_yes?: number | null;
  audience_callback_no?: number | null;
  audience_already_voted?: number | null;
  audience_no_contact?: number | null;
  audience_responded?: number | null;
  audience_mode?: "first_contact" | "callback" | null;
  progress_total?: number | null;
  progress_completed?: number | null;
  excluded_count: number;
  avg_contact_score?: number | null;
  // Walk route optimization
  walk_order_ready: boolean;
  walk_sort_order?: string[] | null;
  route_geojson?: string | null;
  route_distance_m?: number | null;
  route_duration_s?: number | null;
  created_by: string;
  /** Non-null = archived ("falinn"): hidden from all list-level surfaces but
   *  fully intact and restorable. Only present in territory listings when
   *  fetched with includeArchived. */
  archived_at?: string | null;
  /** Non-null = this list is working a retarget pass ("Heyra í öllum AFTUR
   *  frá") since this moment: everyone whose latest final outcome predates it
   *  is callable again. Drives the amber warning on the list page and the
   *  amber marker in the Listar table. */
  retarget_since?: string | null;
  /** Per-list call-script override (walk_lists.call_script_id). Null/absent =
   *  the list inherits the campaign's active script. */
  call_script_id?: string | null;
  /** Outbound call source ("Hringt út frá", walk_lists.call_source). Null/absent
   *  = the party's own fleet. Only the key travels: the label comes from
   *  listCallSources() so no source name lives in this bundle. */
  call_source?: string | null;
  /** True = personal-list firewall bypass ("Hafa með fólk af persónulegum
   *  listum"): this one list also serves people held on other members'
   *  personal lists. Drives the amber warning on the list page and the amber
   *  marker in the Listar table. */
  include_personal_members?: boolean;
  /** True = kjörskrá/institution bypass ("Hafa með fólk utan kosningakerfis
   *  og á stofnunum"): this one list's filter also serves members not on the
   *  election's kjörskrá and people at institutional addresses. */
  include_non_voters?: boolean;
  created_at: string;
}

export interface WalkOrderStatus {
  ready: boolean;
  too_large?: boolean;
  route_distance_m?: number | null;
  route_duration_s?: number | null;
}

export interface ContactLog {
  id: string;
  contact_type: ContactType;
  // null after admin soft-clear (see clearLatestDisposition); other fields
  // (notes/issue_tags/action_responses/contacted_at) stay populated.
  disposition: Disposition | null;
  notes?: string | null;
  contacted_by: string | null;
  contacted_at: string;
  target_name?: string;
  target_address?: string;
  target_postal_code?: string;
  // Activity grid only: opens the person-admin modal for the contacted
  // person (null for claim rows whose action log lacks the hash).
  target_kennitala_hash?: string | null;
  canvasser_name?: string;
  issue_tags?: string[];
  // issue_tags keys resolved to per-campaign display labels on the backend
  // (raw key survives when the tag was deleted). Populated by the activity
  // grid AND the /targets/{kh}/contacts history endpoint; ContactHistoryList
  // falls back to raw issue_tags keys when absent.
  issue_tag_labels?: string[];
  action_responses?: Record<string, string> | null;
  call_duration?: number | null;
  visit_duration_s?: number | null;
  // Origin of the log: `canvassing` | `personal_list` | `call_center` |
  // `folk` | `newsletter` | `auto_supporter`. Drives the history-row source
  // chip (see CONTACT_SOURCE_META); populated on the /contacts endpoint.
  source?: string | null;
  walk_list_id?: string | null;
  walk_list_name?: string | null;
}

export interface WalkListEntry {
  id: string;
  sort_order: number;
  target: CanvassingTarget;
  latest_contact?: ContactLog;
  claimed_by?: string;
  claimer_name?: string;
  claim_relation?: string;
  claim_priority?: number;
  on_other_lists?: number;
  excluded?: boolean;
  // Per-target lock from canvassing_targets. NULL when no active lock.
  // The list-management browse view can use these to show "Anna is currently
  // calling this person" badges.
  locked_by?: string;
  locked_until?: string;
}

export interface CampaignProgress {
  goal_contacts: number;
  actual_contacts: number;
  target_count: number;
  member_count: number;
  contacted_count: number;
  // responded_count = people whose latest disposition is in the meaningful-
  // response set (callback_yes / callback_no / already_voted). Powers the
  // Framvinda widget's response-rate math; complements contacted_count
  // (which still reports anyone reached at all, including dead-end numbers).
  responded_count: number;
  not_contacted_count: number;
  disposition_summary: Record<string, number>;
  territory_progress?: Array<Record<string, unknown>>;
  // Expanded detail-header metrics. Cohort counts are static; reached /
  // not_reached split each cohort by contact_logs presence.
  population_count: number;
  member_reached: number;
  member_not_reached: number;
  on_personal_list_count: number;
  on_personal_list_reached: number;
  on_personal_list_not_reached: number;
  non_member_count: number;
  non_member_reached: number;
  non_member_not_reached: number;
  // Per-cohort disposition mix — populates the in-card Niðurstöður stack
  // on each CohortCard. Sum of values <= cohort *_count; the frontend
  // synthesizes an "Engin samskipti" row for the difference.
  member_disposition_summary: Record<string, number>;
  non_member_disposition_summary: Record<string, number>;
  on_personal_list_disposition_summary: Record<string, number>;
}

export interface Municipality {
  svfnr: string;
  name: string;
  population?: number;
  area_km2?: number;
  member_count: number;
}

// Canvassing API functions

export async function getCanvassingCampaigns(params?: {
  organization_id?: string;
}): Promise<CanvassingCampaign[]> {
  const qs = params?.organization_id
    ? `?organization_id=${params.organization_id}`
    : "";
  const res = await fetchApi<{ items: CanvassingCampaign[] }>(
    `/api/canvassing/campaigns${qs}`
  );
  return res.items;
}

export async function createCanvassingCampaign(data: {
  name: string;
  /** NULL/omitted for a national (landslisti) campaign; required for a municipality one. */
  svfnr?: string | null;
  /** Defaults to "municipality" server-side. Pass "national" for a landslisti. */
  scope_type?: CampaignScopeType;
  organization_id: string;
  description?: string;
  campaign_type?: CanvassingCampaignType;
  start_date?: string;
  end_date?: string;
  retention_until?: string;
  goal_contacts?: number;
  /** Required for a national campaign — the election it belongs to. */
  election_id?: string;
  kjordaemi?: string;
}): Promise<CanvassingCampaign> {
  return fetchApi("/api/canvassing/campaigns", {
    method: "POST",
    body: JSON.stringify(data),
  }, 60000);
}

export async function getCanvassingCampaign(
  id: string
): Promise<CanvassingCampaign> {
  return fetchApi(`/api/canvassing/campaigns/${id}`);
}

export async function updateCanvassingCampaign(
  id: string,
  data: Partial<CanvassingCampaign>
): Promise<CanvassingCampaign> {
  return fetchApi(`/api/canvassing/campaigns/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function buildCampaignTargets(
  id: string,
  territoryIds?: string[]
): Promise<{ targets_created: number }> {
  return fetchApi(`/api/canvassing/campaigns/${id}/build`, {
    method: "POST",
    body: JSON.stringify({ territory_ids: territoryIds ?? null }),
  }, 60000);
}

export async function getCampaignTerritories(
  campaignId: string
): Promise<Territory[]> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/territories`);
}

export async function createTerritory(
  campaignId: string,
  data: { name: string; svfnr: string; postal_codes?: string[]; priority?: number }
): Promise<Territory> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/territories`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateTerritory(
  id: string,
  data: Partial<Territory>
): Promise<Territory> {
  return fetchApi(`/api/canvassing/territories/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function generateWalkList(
  territoryId: string,
  data: { territory_id: string; name: string; sort_strategy?: string; assigned_to?: string[]; mode?: WalkListMode | null }
): Promise<WalkList> {
  return fetchApi(`/api/canvassing/territories/${territoryId}/generate-lists`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getWalkList(id: string): Promise<WalkList> {
  return fetchApi(`/api/canvassing/walk-lists/${id}`);
}

/** Slim ancestor chain (root-first) for the breadcrumb. Reads only walk_lists
 *  via a recursive CTE — no heavy virtual-membership counts. Replaces the old
 *  per-ancestor getWalkList fan-out. */
export interface WalkListAncestor {
  id: string;
  name: string;
  list_type: string;
  parent_walk_list_id: string | null;
}
export async function getWalkListAncestors(id: string): Promise<WalkListAncestor[]> {
  return fetchApi(`/api/canvassing/walk-lists/${id}/ancestors`);
}

export async function ensureWalkOrder(id: string): Promise<WalkOrderStatus> {
  return fetchApi(`/api/canvassing/walk-lists/${id}/ensure-walk-order`, { method: "POST" });
}

export async function createPersonalListForMember(
  campaignId: string,
  memberId: string,
): Promise<WalkList> {
  return fetchApi(
    `/api/canvassing/campaigns/${campaignId}/members/${memberId}/personal-list`,
    { method: "POST" },
  );
}

export async function getTerritoryWalkLists(
  territoryId: string,
  withCounts = true,
  includeArchived = false,
): Promise<WalkList[]> {
  // counts=false skips the entry_stats CTE and batch_walk_list_live_counts
  // fan-out — used by surfaces that only need id/name/parent metadata
  // (move-target picker, archive-descendant counter) where loading hundreds
  // of bucket counts would push past the Vercel function timeout on
  // Reykjavik-scale territories.
  const params = new URLSearchParams();
  if (!withCounts) params.set("counts", "false");
  if (includeArchived) params.set("include_archived", "true");
  const qs = params.size > 0 ? `?${params.toString()}` : "";
  return fetchApi(`/api/canvassing/territories/${territoryId}/walk-lists${qs}`);
}

export type WalkListCountUpdate = {
  id: string;
  total: number;
  completed: number;
  avg_score: number | null;
  callback_yes: number;
  callback_no: number;
  already_voted: number;
  no_contact: number;
  unreached: number;
  // Audience-mode block — null on non-shrinking lists, populated for
  // Fyrstu samskipti subtree / callback-style auto_lists where bucket
  // counts above are 0 by construction.
  audience_total?: number | null;
  audience_callback_yes?: number | null;
  audience_callback_no?: number | null;
  audience_already_voted?: number | null;
  audience_no_contact?: number | null;
  audience_responded?: number | null;
  audience_mode?: "first_contact" | "callback" | null;
  progress_total?: number | null;
  progress_completed?: number | null;
};

export interface WalkListLogBreakdown {
  contacted_people: number;
  callback_yes: number;
  callback_no: number;
  /** Sub-counts of the callback buckets whose latest per-this-list log carries
   *  the "Óákveðinn" tag — powers the four-row MHA / +Ó / +Ó / MEHA spectrum. */
  callback_yes_undecided: number;
  callback_no_undecided: number;
  already_voted: number;
  no_contact: number;
  unreached: number;
  last_contacted_at: string | null;
  svör: number;
}

/** `day` (YYYY-MM-DD) narrows the breakdown to one calendar day; omit/null
 *  for all-time. Days are UTC on the server, which is the Icelandic clock
 *  year-round. */
export async function getWalkListLogBreakdown(
  walkListId: string,
  day?: string | null,
): Promise<WalkListLogBreakdown> {
  const qs = day ? `?day=${encodeURIComponent(day)}` : "";
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/log-breakdown${qs}`);
}

/** One UTC calendar day (YYYY-MM-DD) of the log-breakdown buckets: latest
 *  log per person within that day, so a row equals
 *  getWalkListLogBreakdown(id, day) for the same day. */
export interface WalkListLogBreakdownDay {
  day: string;
  contacted_people: number;
  callback_yes: number;
  callback_no: number;
  callback_yes_undecided: number;
  callback_no_undecided: number;
  already_voted: number;
  no_contact: number;
  unreached: number;
  svör: number;
}

export interface WalkListLogBreakdownDaily {
  /** Only days with at least one log through the list, oldest first; the
   *  chart zero-fills the gaps. */
  days: WalkListLogBreakdownDay[];
}

export async function getWalkListLogBreakdownDaily(walkListId: string): Promise<WalkListLogBreakdownDaily> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/log-breakdown/daily`);
}

export interface WalkListIssueFrequency {
  /** Per-LOG tag counts (war-room chart semantics): repeat contacts count
   *  again, unlike the latest-per-person buckets of WalkListLogBreakdown. */
  totals: IssueCount[];
}

export async function getWalkListIssueFrequency(walkListId: string): Promise<WalkListIssueFrequency> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/issue-frequency`);
}

export interface WalkListContributor {
  member_id: string | null;
  member_name: string | null;
  profile_image_url: string | null;
  /** Every log the volunteer recorded through this list, repeats included. */
  disposition_count: number;
  /** Distinct people the volunteer touched; buckets below reflect the
   *  volunteer's latest log per person (same scheme as WalkListLogBreakdown). */
  contacted_people: number;
  callback_yes: number;
  callback_no: number;
  already_voted: number;
  no_contact: number;
  unreached: number;
  last_contacted_at: string | null;
  svör: number;
}

/** ``day`` (YYYY-MM-DD, UTC) narrows every count to that calendar day, with
 *  the same latest-per-person-per-day rule as getWalkListLogBreakdown; a
 *  volunteer who logged nothing that day is absent from the list. Omit or
 *  pass null for all-time. */
export async function getWalkListContributors(
  walkListId: string,
  day?: string | null,
): Promise<WalkListContributor[]> {
  const qs = day ? `?day=${encodeURIComponent(day)}` : "";
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/contributors${qs}`);
}

/**
 * Stream walk lists with progressive count loading. The returned Promise
 * resolves as soon as line 1 arrives (metadata + cached counts) so React
 * Query can render the table immediately. Remaining count updates are read
 * in the background and pushed to ``onCounts`` which should call
 * ``queryClient.setQueryData`` to merge them in.
 */
export async function streamTerritoryWalkLists(
  territoryId: string,
  onCounts: (updates: WalkListCountUpdate[]) => void,
  includeArchived = false,
): Promise<WalkList[]> {
  const archQs = includeArchived ? "&include_archived=true" : "";
  const url = `${API_BASE}/api/canvassing/territories/${territoryId}/walk-lists?stream=true${archQs}`;

  const extraHeaders: Record<string, string> = {};
  if (getViewMode() === "user") {
    extraHeaders["X-View-As"] = "user";
  }

  const res = await fetch(url, {
    credentials: "include",
    headers: extraHeaders,
  });

  if (res.status === 401) {
    if (typeof window !== "undefined") {
      const p = window.location.pathname;
      if (p !== "/login" && p !== "/callback") window.location.href = "/login";
    }
    throw new ApiError(401, "Óheimil aðgangur");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // Read until we get line 1 (the initial lists with cached counts).
  // Resolve the Promise immediately so React Query can render the table.
  while (true) {
    const { done, value } = await reader.read();
    if (done) return [];
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop()!;
    for (const line of lines) {
      if (!line.trim()) continue;
      const data = JSON.parse(line);
      if (data.lists) {
        // Got initial lists — continue reading count updates in background.
        _readCountUpdates(reader, decoder, buffer, onCounts);
        return data.lists as WalkList[];
      }
    }
  }
}

/** Fire-and-forget: reads remaining NDJSON lines after the initial lists. */
function _readCountUpdates(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
  buffer: string,
  onCounts: (updates: WalkListCountUpdate[]) => void,
) {
  (async () => {
    let buf = buffer;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop()!;
        const batch: WalkListCountUpdate[] = [];
        for (const line of lines) {
          if (!line.trim()) continue;
          const data = JSON.parse(line);
          if (data.id) batch.push(data);
        }
        if (batch.length > 0) onCounts(batch);
      }
      // Flush remaining buffer
      if (buf.trim()) {
        try {
          const data = JSON.parse(buf);
          if (data.id) onCounts([data]);
        } catch { /* partial line at end — ignore */ }
      }
    } catch {
      /* stream error — counts will be stale until next refetch */
    }
  })();
}

export interface MapPoint {
  // walk_list_entry UUID, null for filter-only members (no physical row yet).
  i: string | null;
  // kennitala_hash — stable per-person id, used by move/exclude/restore.
  h: string;
  n: string;
  y: number;
  x: number;
  m: boolean;
  d: string | null;
  // Address (address_street_nom) — used by the offline route card so a target
  // shown from the cached snapshot has more than a name + dot.
  a?: string | null;
}

export async function getWalkListEntries(
  id: string,
  opts?: {
    cursor?: number;
    limit?: number;
    excluded?: boolean;
    orderBy?: "walk_route" | "contact_score";
    q?: string;
    sortModel?: string;
    filterModel?: string;
  },
): Promise<{ entries: WalkListEntry[]; next_cursor?: number; has_more: boolean; total_count?: number }> {
  const params = new URLSearchParams();
  if (opts?.cursor !== undefined) params.set("cursor", String(opts.cursor));
  if (opts?.limit) params.set("limit", String(opts.limit));
  if (opts?.excluded) params.set("excluded", "true");
  if (opts?.orderBy) params.set("order_by", opts.orderBy);
  if (opts?.q) params.set("q", opts.q);
  if (opts?.sortModel) params.set("sort_model", opts.sortModel);
  if (opts?.filterModel) params.set("filter_model", opts.filterModel);
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetchApi<{ items: WalkListEntry[]; next_cursor?: number; has_more: boolean; total_count?: number }>(
    `/api/canvassing/walk-lists/${id}/entries${qs}`
  );
  return { entries: res.items, next_cursor: res.next_cursor, has_more: res.has_more, total_count: res.total_count };
}

export async function getWalkListMapPoints(
  id: string,
  excluded?: boolean,
): Promise<{ p: MapPoint[]; t: number; o?: string[] }> {
  const qs = excluded ? "?excluded=true" : "";
  return fetchApi(`/api/canvassing/walk-lists/${id}/map-points${qs}`);
}

export async function updateWalkList(
  id: string,
  data: Partial<WalkList>
): Promise<WalkList> {
  return fetchApi(`/api/canvassing/walk-lists/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function archiveWalkList(
  id: string,
  opts?: { confirmPersonal?: boolean }
): Promise<{ archived: boolean; descendant_count: number }> {
  // Archives ("felur") the list and its whole subtree — reversible, nothing
  // is destroyed. Personal lists are a member's own projection — the backend
  // refuses to archive one without the explicit confirm flag.
  const qs = opts?.confirmPersonal ? "?confirm_personal=true" : "";
  return fetchApi(`/api/canvassing/walk-lists/${id}/archive${qs}`, {
    method: "POST",
  });
}

export async function unarchiveWalkList(
  id: string,
): Promise<{ unarchived: boolean; restored_count: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${id}/unarchive`, {
    method: "POST",
  });
}

export async function freezeWalkList(
  id: string,
): Promise<{ frozen: boolean; entries_added: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${id}/freeze`, { method: "POST" });
}

export async function getWalkListSmsEligible(
  walkListId: string,
): Promise<{ eligible_count: number; total_entries: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/sms-eligible`, {
    method: "POST",
  });
}

export async function sendWalkListSms(
  walkListId: string,
  data: { body_template: string; scheduled_at?: string },
): Promise<{
  status: string;
  approval_id: string;
  campaign_id: string;
  recipient_count: number;
}> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/sms`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function addWalkListAssignees(
  walkListId: string,
  kennitalaHashes: string[],
): Promise<{ added: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/assignees`, {
    method: "POST",
    body: JSON.stringify({ kennitala_hashes: kennitalaHashes }),
  });
}

export async function removeWalkListAssignee(
  walkListId: string,
  assignmentId: string,
): Promise<{ removed: boolean; volunteer_removed?: boolean }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/assignees/${assignmentId}`, {
    method: "DELETE",
  });
}

/** One current assignee of a walk list, with their activity ON that list
 *  (zero counts until they've worked it). Backs the "Núverandi úthlutun"
 *  table in the Úthluta dialog. */
export interface WalkListAssigneeStats {
  assignment_id: string;
  kennitala_hash: string;
  name: string | null;
  is_member: boolean;
  assigned_at: string | null;
  /** Every disposition-bearing log on this list (repeat attempts included). */
  disposition_count: number;
  /** Distinct people this assignee touched through this list. */
  contacted_people: number;
  /** Distinct people whose latest disposition by this assignee is callback_yes. */
  callback_yes: number;
  last_contacted_at: string | null;
}

export async function getWalkListAssignees(walkListId: string): Promise<WalkListAssigneeStats[]> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/assignees`);
}

/** A walk list a Mannauðsstjóri may assign volunteers to (flagged assignable). */
export interface AssignableCallList {
  id: string;
  campaign_id: string;
  campaign_name: string;
  /** Parent list name — the Flokksskrá exists twice per election ("Fyrstu
   *  samskipti" / "Svarar ekki"), so this is what tells the two apart. */
  parent_name: string | null;
  name: string;
  mode: WalkListMode | null;
  /** How many volunteers are already assigned to this list. */
  assignee_count: number;
}

/** Lists the current user may assign volunteers to in this election — flagged
 *  (volunteer_assignable), regular, non-archived. Call-mode lists sort first.
 *  Backs the roster "Úthluta á lista" dialog. */
export async function getAssignableCallLists(electionId: string): Promise<AssignableCallList[]> {
  return fetchApi(`/api/canvassing/elections/${electionId}/assignable-call-lists`);
}

/** One walk list a volunteer is assigned to, with their work on that list.
 *  The reverse of {@link WalkListAssigneeStats}. */
export interface VolunteerListAssignment {
  assignment_id: string;
  walk_list_id: string;
  list_name: string;
  list_type: string;
  mode: WalkListMode | null;
  campaign_id: string;
  campaign_name: string;
  parent_name: string | null;
  volunteer_assignable: boolean;
  assigned_at: string | null;
  /** Every disposition-bearing log this person made on this list. */
  disposition_count: number;
  /** Distinct people they touched through this list. */
  contacted_people: number;
  callback_yes: number;
  last_contacted_at: string | null;
  /** Whether the current user may unassign them from this list — resolved
   *  server-side because the assign gate differs per list. */
  can_unassign: boolean;
}

/** Which walk lists this volunteer is assigned to in the election. Backs the
 *  "Vinnulistar" section of the volunteer detail drawer. */
export async function getVolunteerListAssignments(
  electionId: string,
  kennitalaHash: string,
): Promise<VolunteerListAssignment[]> {
  return fetchApi(`/api/canvassing/elections/${electionId}/volunteers/${kennitalaHash}/lists`);
}

/** Kosningastjóri/admin toggle: flag (or unflag) a list assignable by volunteer
 *  managers. Thin wrapper over the walk-list PATCH. */
export async function setWalkListAssignable(walkListId: string, assignable: boolean): Promise<WalkList> {
  return updateWalkList(walkListId, { volunteer_assignable: assignable });
}

/** Kosningastjóri/admin toggle: open (or close) a list to every active party
 *  member. Thin wrapper over the walk-list PATCH. */
export async function setWalkListOpenToAllMembers(walkListId: string, open: boolean): Promise<WalkList> {
  return updateWalkList(walkListId, { open_to_all_members: open });
}

// ---------- Walk-list working chunk (per-target lock) ----------
//
// The chunk endpoint is the single entry point for both door canvassing
// and phone calling. The frontend renders one target at a time from the
// current chunk, calls /next after every contact, and the server refills
// the slot atomically. The lock is acquired and refreshed as part of /next.

export interface ChunkItem {
  id: string;
  kennitala_hash: string;
  is_member: boolean;
  banned?: boolean;
  name: string;
  address: string | null;
  postal_code: string | null;
  svfnr: string | null;
  /** See CanvassingTarget.svfnr_overridden — Grindavík displaced-resident flag. */
  svfnr_overridden?: boolean;
  street_name: string | null;
  house_number: number | null;
  house_letter: string | null;
  heinum: number | null;
  apartment: string | null;
  contact_attempts: number;
  age: number | null;
  phone_number: string | null;
  phone_secondary: string | null;
  /** Extra Símaskrá numbers — fallback tap-to-call rows. See CanvassingTarget. */
  additional_phones: Array<{ phone: string }> | null;
  sort_order: number;
  lat: number | null;
  lon: number | null;
  /** Informational only — the per-target lock prevents cross-list collision. */
  on_other_lists: number;
}

export interface ChunkResponse {
  items: ChunkItem[];
  remaining: number;
  completed: number;
  total: number;
  done: boolean;
}

export interface ProgressResponse {
  remaining: number;
  completed: number;
  total: number;
}

/**
 * Chunk ordering modes — same string values as ``WalkListMode``.
 *   ``canvass`` — door canvas: walk route → entry sort_order → address
 *   ``drive``   — drive mode: same chain as canvass (route was built with
 *                 a driving ORS profile at compute time)
 *   ``call``    — phone canvas: ML contact_score DESC → name (likely
 *                 supporters first, ignoring geographic order)
 */
export type ChunkOrderBy = "canvass" | "call" | "drive";

export async function fetchNextChunk(
  walkListId: string,
  orderBy: ChunkOrderBy = "canvass",
): Promise<ChunkResponse> {
  return fetchApi(
    `/api/canvassing/walk-lists/${walkListId}/next?order_by=${orderBy}`,
    { method: "POST" },
  );
}

/**
 * Resolve the best phone number to call for a target.
 *
 * The backend runs the full local cascade (sensitive.people →
 * members.phone_enc → sensitive.simaskra cache) and returns the
 * resolved phone or null. No live external calls — the three simaskra
 * importers own populating the cache. The frontend has zero business
 * logic — just display whatever comes back. A null response means
 * "no phone available from any source" and is terminal for this
 * target until something changes server-side.
 */
export async function getEntryPhone(
  walkListId: string,
  kennitalaHash: string,
): Promise<{ phone_number: string | null }> {
  return fetchApi(
    `/api/canvassing/walk-lists/${walkListId}/entries/${kennitalaHash}/phone`,
  );
}

export async function claimSpecificTarget(
  walkListId: string,
  kennitalaHash: string,
  orderBy: ChunkOrderBy = "canvass",
): Promise<ChunkResponse> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/claim`, {
    method: "POST",
    body: JSON.stringify({ kennitala_hash: kennitalaHash, order_by: orderBy }),
  });
}

export function releaseChunk(walkListId: string): void {
  // keepalive so it survives tab close
  fetch(`${API_BASE}/api/canvassing/walk-lists/${walkListId}/release`, {
    method: "POST",
    credentials: "include",
    keepalive: true,
  }).catch(() => {});
}

/**
 * Single-person dispatch for the /call page. One atomic round-trip:
 * "here's the contact I just had, please lock me the next person".
 *
 * Pass an empty body on the first call (page mount). On subsequent calls
 * supply ``previous_kh`` + the disposition payload so the server writes
 * the ContactLog, releases that lock, and acquires the next person in
 * one go. Replaces the chunk/heartbeat/session dance entirely.
 */
export interface NextCallerRequest {
  previous_kh?: string;
  disposition?: string;
  notes?: string;
  issue_tags?: string[];
  action_responses?: Record<string, string>;
  call_duration?: number;
  script_responses?: Record<string, unknown>;
  contacted_at?: string;
  call_id?: string;
  telephony_source?: string;
  /** The number actually shown/dialed for this person, so a wrong_number
   *  disposition blocklists the number the volunteer judged, not whatever
   *  happens to sit in phone_number. */
  dialed_number?: string;
}

export interface NextCallerResponse {
  person: ChunkItem | null;
  done: boolean;
  /**
   * Only meaningful when ``done`` is true. ``"exhausted"`` means every
   * person on the list has a final disposition (show the celebration
   * screen). ``"unreachable"`` means the list still has uncontacted
   * people but the phone-resolution cascade couldn't land on a reachable
   * one within the per-request budget (show a retry card instead).
   */
  reason?: "exhausted" | "unreachable";
  remaining: number;
  completed: number;
  total: number;
  /**
   * Of ``remaining`` (uncontacted), how many can never be called — split by
   * reason. ``banned_remaining`` = bannmerktir, ``no_phone_remaining`` = án
   * símanúmers. Only populated on the ``unreachable`` done path; the rest of
   * ``remaining`` is transiently blocked (locked by another caller or in the
   * not_home cooldown) and may surface on retry.
   */
  banned_remaining?: number;
  no_phone_remaining?: number;
  /**
   * True when outbound calling is paused for time-of-day (server calling_hours,
   * 08:00-22:00 in production). The previous disposition was still logged; no
   * next person is handed out. The /call page shows a calling-hours banner.
   */
  calling_paused?: boolean;
  /**
   * Mirror of election.calling_enabled, refreshed per dispatch so the /call
   * page tears the softphone down when a kosningastjóri flips the
   * "Símhringingar í vafra" kill switch mid-shift (plain volunteers cannot
   * read GET /elections/{id}). Absent on older responses = enabled.
   */
  calling_enabled?: boolean;
  /** Mirror of election.kjordagur_mode, refreshed per dispatch so flipping
   *  the switch mid-shift takes effect at the volunteer's next person. */
  kjordagur_mode?: boolean;
  /** This volunteer's contact logs today in this election, for the kjördagur
   *  progress bar. Null when the server could not count. */
  calls_today?: number | null;
}

export async function nextCaller(
  walkListId: string,
  body: NextCallerRequest = {},
): Promise<NextCallerResponse> {
  return fetchApi(
    `/api/canvassing/walk-lists/${walkListId}/next-caller`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

/**
 * Single-target dispatch for the /canvass page. Mirrors nextCaller for door
 * canvassing. Three dispatch paths:
 *
 *   - Empty body → start from rank 0 (page mount, or "Byrja aftur" after
 *     end-of-route to revisit skipped targets)
 *   - ``previous_kh`` + optional ``disposition`` → advance forward/backward
 *     past that person. ``disposition`` set = log a ContactLog; unset = skip.
 *   - ``target_kh`` → marker-tap. Claim that specific person directly,
 *     bypassing route order. Returns 409 if held by another user.
 *
 * ``lat`` / ``lon`` / ``accuracy_m`` piggyback the session GPS + heartbeat
 * update so the canvasser map stays fresh without a separate PATCH.
 *
 * Locks accumulate on the caller as they walk — the server never releases
 * them. TTL (60 min) is the sole expiry path. This matches the "never
 * release" design documented in person_work_claim_service.py.
 */
export interface NextCanvasserRequest {
  previous_kh?: string;
  disposition?: string;
  notes?: string;
  issue_tags?: string[];
  action_responses?: Record<string, string>;
  visit_duration_s?: number;
  target_kh?: string;
  direction?: "forward" | "backward";
  lat?: number;
  lon?: number;
  accuracy_m?: number;
  contacted_at?: string;
}

export interface NextCanvasserResponse {
  person: ChunkItem | null;
  done: boolean;
  /**
   * Only meaningful when ``done`` is true. ``"exhausted"`` = every person
   * on the list has a final disposition; show the celebration screen.
   * ``"end_of_route"`` = forward scan reached the last stop (or backward
   * scan reached the first), but skipped/cooldown targets remain — show
   * a "Byrja aftur" card.
   */
  reason?: "exhausted" | "end_of_route";
  remaining: number;
  completed: number;
  total: number;
}

export async function nextCanvasser(
  walkListId: string,
  body: NextCanvasserRequest = {},
): Promise<NextCanvasserResponse> {
  return fetchApi(
    `/api/canvassing/walk-lists/${walkListId}/next-canvasser`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export async function getWalkListProgress(walkListId: string): Promise<ProgressResponse> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/progress`);
}

// ---------- Canvassing Sessions ----------

export interface CanvassSessionResponse {
  id: string;
  campaign_id: string;
  walk_list_id: string;
  started_at: string;
  ended_at: string | null;
  end_reason: string | null;
  contacts_logged: number;
}

export async function createCanvassSession(data: {
  walk_list_id: string;
  started_at: string;
}): Promise<CanvassSessionResponse> {
  return fetchApi("/api/canvassing/sessions", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function heartbeatCanvassSession(sessionId: string): Promise<void> {
  await fetchApi(`/api/canvassing/sessions/${sessionId}/heartbeat`, {
    method: "PATCH",
  });
}

export async function postCanvassingSessionLocation(
  sessionId: string,
  data: { lat: number; lon: number; accuracy_m: number | null },
): Promise<void> {
  await fetchApi(`/api/canvassing/sessions/${sessionId}/location`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export interface ActiveVolunteer {
  session_id: string;
  volunteer_id: string | null;
  display_name: string;
  full_name: string | null;
  lat: number;
  lon: number;
  accuracy_m: number | null;
  last_seen_at: string; // ISO-8601, UTC
  recency: "active" | "recent"; // <10 min / <30 min since last ping
  walk_list_id: string;
  walk_list_name: string | null;
  contacts_logged: number;
}

export function endCanvassSession(
  sessionId: string,
  data: { end_reason: string; contacts_logged?: number },
): void {
  // Use keepalive so it survives tab close (same pattern as unlockAll)
  fetch(`${API_BASE}/api/canvassing/sessions/${sessionId}/end`, {
    method: "PATCH",
    credentials: "include",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }).catch(() => {});
}

export async function logContact(
  kennitalaHash: string,
  campaignId: string,
  data: { contact_type: ContactType; disposition: Disposition; notes?: string; call_duration?: number; visit_duration_s?: number; contacted_at?: string; issue_tags?: string[]; action_responses?: Record<string, string>; walk_list_id?: string; household?: boolean }
): Promise<ContactLog> {
  return fetchApi(`/api/canvassing/targets/${kennitalaHash}/contact?campaign_id=${campaignId}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function clearLatestContact(
  kennitalaHash: string,
  campaignId: string,
): Promise<{ cleared: boolean }> {
  return fetchApi(`/api/canvassing/targets/${kennitalaHash}/contact?campaign_id=${campaignId}`, {
    method: "DELETE",
  });
}

// Admin override: NULL the disposition on the latest dispositioned log
// while preserving notes, issue_tags, action_responses, contacted_at, etc.
// ``contactLogId`` is the staleness guard — backend 409s if it no longer
// matches the latest dispositioned log (race with another admin or with a
// canvasser appending a fresh log between grid render and click).
export async function clearLatestDisposition(
  kennitalaHash: string,
  campaignId: string,
  contactLogId: string,
): Promise<{ cleared: boolean }> {
  return fetchApi(
    `/api/canvassing/targets/${kennitalaHash}/contact/clear-disposition?campaign_id=${campaignId}`,
    {
      method: "POST",
      body: JSON.stringify({ contact_log_id: contactLogId }),
    },
  );
}

export async function getTargetContacts(kennitalaHash: string, campaignId: string): Promise<ContactLog[]> {
  return fetchApi(`/api/canvassing/targets/${kennitalaHash}/contacts?campaign_id=${campaignId}`);
}

// ---------- Household ----------

export interface HouseholdMember {
  kennitala_hash: string;
  name: string;
  apartment: string | null;
  is_member: boolean;
  last_disposition: Disposition | null;
  walk_list_id: string | null;
  walk_list_name: string | null;
  on_current_list: boolean;
}

export interface HouseholdResponse {
  building_address: string | null;
  heinum: number;
  total_targets: number;
  contacted_count: number;
  member_count: number;
  dwellings: Record<string, HouseholdMember[]>;
}

export async function getHousehold(
  campaignId: string,
  kennitalaHash: string,
  walkListId?: string,
): Promise<HouseholdResponse> {
  const sp = new URLSearchParams({ kennitala_hash: kennitalaHash });
  if (walkListId) sp.set("walk_list_id", walkListId);
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/household?${sp.toString()}`);
}

export async function getMyWalkLists(opts?: {
  /**
   * Skip the inline live-count compute for cold filter/centroid lists:
   * those rows come back with null count fields (skeleton) so the caller
   * can render immediately and follow up with a full request. See
   * useMyWalkLists, which wires the two phases together.
   */
  fast?: boolean;
}): Promise<WalkList[]> {
  return fetchApi(`/api/canvassing/my-lists${opts?.fast ? "?fast=true" : ""}`);
}

export async function getCampaignProgress(
  id: string
): Promise<CampaignProgress> {
  return fetchApi(`/api/canvassing/campaigns/${id}/progress`);
}

export async function getCampaignActivity(
  id: string,
  opts: {
    startRow: number;
    endRow: number;
    snapshotAt: string;
    sortModel?: string;
    filterModel?: string;
  },
): Promise<{ items: ContactLog[]; has_more: boolean }> {
  const params = new URLSearchParams();
  params.set("start_row", String(opts.startRow));
  params.set("end_row", String(opts.endRow));
  params.set("snapshot_at", opts.snapshotAt);
  if (opts.sortModel) params.set("sort_model", opts.sortModel);
  if (opts.filterModel) params.set("filter_model", opts.filterModel);
  // 30 s — safety net for outlier query plans on large elections. The
  // shared fetchApi default of 15 s was the trigger for AG Grid's "ERR"
  // skeleton: an aborted block load sets node.failedLoad and renders
  // the literal "ERR" string from SkeletonCellRenderer.setupFailed().
  return fetchApi(
    `/api/canvassing/campaigns/${id}/activity?${params.toString()}`,
    {},
    30_000,
  );
}

export async function getActivityFilterValues(
  campaignId: string,
): Promise<{
  canvassers: { id: string; name: string }[];
  walk_lists: { id: string; name: string; list_type: string }[];
  dispositions: string[];
}> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/activity/filter-values`);
}

/** Every non-personal walk list for a campaign (default list + all descendants
 *  at any depth + parentless smart-split lists) — the injection targets for the
 *  election-level tag picker. Nest via `parent_walk_list_id`. */
export async function getCampaignWalkLists(
  campaignId: string,
): Promise<
  { id: string; name: string; list_type: string; parent_walk_list_id: string | null }[]
> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/walk-lists`);
}

export async function getCampaignMapData(
  id: string,
  /** Restricts the background layer for national campaigns — without it they
   *  return an empty layer (the countrywide set is far too large to serve). */
  bbox?: { minLat: number; minLon: number; maxLat: number; maxLon: number }
): Promise<{
  targets: GeoJSON.FeatureCollection;
  boundary: string | null;
  target_count: number;
}> {
  const qs = bbox
    ? `?min_lat=${bbox.minLat}&min_lon=${bbox.minLon}&max_lat=${bbox.maxLat}&max_lon=${bbox.maxLon}`
    : "";
  return fetchApi(`/api/canvassing/campaigns/${id}/map-data${qs}`);
}

export async function getCampaignActiveVolunteers(
  id: string,
): Promise<{ volunteers: ActiveVolunteer[] }> {
  return fetchApi(`/api/canvassing/campaigns/${id}/active-volunteers`);
}

export async function splitCampaignList(
  id: string,
  body: { method: string; list_size?: number; postal_codes?: string[]; coordinates?: number[][]; polygons?: number[][][]; name?: string; filter_json?: QueryGroup; mode?: WalkListMode | null; parent_walk_list_id?: string; kennitalas?: string[]; retarget_since?: string | null; include_personal_members?: boolean; include_non_voters?: boolean }
): Promise<{ method: string; lists_created: number; lists: Array<{ id: string; name: string; entries: number; hull_geojson?: string }> }> {
  return fetchApi(`/api/canvassing/campaigns/${id}/split`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function previewCsvMatchCount(
  campaignId: string,
  kennitalas: string[]
): Promise<{ matched: number; submitted: number }> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/csv-match-count`, {
    method: "POST",
    body: JSON.stringify({ kennitalas }),
  });
}

export async function splitWalkList(
  walkListId: string,
  body: { method: "smart" | "size"; list_size: number; mode?: WalkListMode | null }
): Promise<{ method: string; lists_created: number; lists: Array<{ id: string; name: string; entries: number; hull_geojson?: string }> }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/split`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function batchSyncContacts(
  items: Array<{
    kennitala_hash: string;
    campaign_id: string;
    walk_list_id?: string;
    contact_type: string;
    disposition: string;
    notes?: string;
    issue_tags?: string[];
    action_responses?: Record<string, string>;
    call_duration?: number;
    visit_duration_s?: number;
    script_responses?: Record<string, unknown>;
    contacted_at: string;
    client_id: string;
  }>
): Promise<{ synced: number; results: Array<{ client_id?: string; kennitala_hash: string; status: string }> }> {
  return fetchApi("/api/canvassing/contacts/batch", {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}

export async function getWalkListBriefing(
  id: string
): Promise<{ briefing_md: string }> {
  return fetchApi(`/api/canvassing/walk-lists/${id}/briefing`);
}

// Walk list entry mutations

export async function addWalkListEntries(
  walkListId: string,
  kennitalaHashes: string[],
  dispositions?: Record<string, string>,
  phones?: Record<string, string>,
): Promise<{ added: number; logged_dispositions: number; skipped_final: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/entries/add`, {
    method: "POST",
    body: JSON.stringify({
      kennitala_hashes: kennitalaHashes,
      ...(dispositions && { dispositions }),
      ...(phones && { phones }),
    }),
  });
}

export async function removeWalkListEntries(
  walkListId: string,
  entryIds: string[]
): Promise<{ removed: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/entries/remove`, {
    method: "POST",
    body: JSON.stringify({ entry_ids: entryIds }),
  });
}

export async function excludeWalkListEntries(
  walkListId: string,
  kennitalaHashes: string[]
): Promise<{ excluded: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/entries/exclude`, {
    method: "POST",
    body: JSON.stringify({ kennitala_hashes: kennitalaHashes }),
  });
}

export async function restoreWalkListEntries(
  walkListId: string,
  kennitalaHashes: string[]
): Promise<{ restored: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/entries/restore`, {
    method: "POST",
    body: JSON.stringify({ kennitala_hashes: kennitalaHashes }),
  });
}

export async function moveWalkListEntries(
  walkListId: string,
  kennitalaHashes: string[],
  targetWalkListId: string
): Promise<{ moved: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/entries/move`, {
    method: "POST",
    body: JSON.stringify({
      kennitala_hashes: kennitalaHashes,
      target_walk_list_id: targetWalkListId,
    }),
  });
}

// Create an empty walk list in the same territory, then move the selected
// entries into it. Two-call — if the second fails the new list stays empty
// (user can delete it). Caller should handle errors and refresh queries.
export async function createWalkListAndMoveEntries(
  sourceWalkListId: string,
  territoryId: string,
  name: string,
  kennitalaHashes: string[],
): Promise<{ walkListId: string; moved: number }> {
  const list = await generateWalkList(territoryId, { territory_id: territoryId, name });
  const res = await moveWalkListEntries(sourceWalkListId, kennitalaHashes, list.id);
  return { walkListId: list.id, moved: res.moved };
}

export async function reorderWalkListEntries(
  walkListId: string,
  entryIds: string[]
): Promise<{ reordered: number }> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/entries/reorder`, {
    method: "PUT",
    body: JSON.stringify({ entry_ids: entryIds }),
  });
}

// Conflict detection & resolution

export interface ConflictEntryInfo {
  entry_id: string;
  walk_list_id: string;
  walk_list_name: string;
  claimed_by?: string;
  claimer_name?: string;
  relationship?: string;
  claim_priority?: number;
}

export interface ConflictItem {
  target: CanvassingTarget;
  entries: ConflictEntryInfo[];
}

export async function getCampaignConflicts(
  campaignId: string
): Promise<ConflictItem[]> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/conflicts`);
}

export async function resolveCampaignConflicts(
  campaignId: string,
  resolutions: Array<{ kennitala_hash: string; keep_in_walk_list_id: string }>,
  autoResolve?: boolean
): Promise<{ resolved: number; entries_removed: number }> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/resolve-conflicts`, {
    method: "POST",
    body: JSON.stringify({ resolutions, auto_resolve: autoResolve ?? false }),
  });
}

// Target claiming

export type ClaimRelationship = "family" | "close_friend" | "friend" | "acquaintance" | "knows_of";

export const RELATIONSHIP_LABELS: Record<ClaimRelationship, string> = {
  family: "Fjölskylda",
  close_friend: "Náinn vinur",
  friend: "Vinur",
  acquaintance: "Kunningi",
  knows_of: "Þekkir til",
};

export const RELATIONSHIP_PRIORITY: Record<ClaimRelationship, number> = {
  family: 5,
  close_friend: 4,
  friend: 3,
  acquaintance: 2,
  knows_of: 1,
};

export interface ClaimTargetResponse {
  walk_list_id: string;
  walk_list_name: string;
  entry_id: string;
  claim_priority: number;
  auto_resolved: number;
  /** Another member holds an equal-or-stronger claim. The claimant is never
   *  named (D2) — render the generic "Á lista hjá öðrum félaga". */
  blocked: boolean;
  /** ALWAYS null — legacy field, kept until all readers are gone. */
  blocked_by: string | null;
}

export async function claimTarget(
  campaignId: string,
  kennitalaHash: string,
  relationship: ClaimRelationship
): Promise<ClaimTargetResponse> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/claim`, {
    method: "POST",
    body: JSON.stringify({ kennitala_hash: kennitalaHash, relationship }),
  });
}

export async function unclaimTarget(
  campaignId: string,
  kennitalaHash: string
): Promise<{ removed: boolean }> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/claim/${kennitalaHash}`, {
    method: "DELETE",
  });
}

export interface BrowseClaimInfo {
  walk_list_id: string;
  walk_list_name: string;
  claimed_by: string;
  claimer_name: string;
  relationship: string;
  claim_priority: number;
}

export interface BrowseTargetItem {
  target: CanvassingTarget;
  claims: BrowseClaimInfo[];
}

export async function browseCampaignTargets(
  campaignId: string,
  search?: string,
  cursor?: number,
  limit?: number
): Promise<{ items: BrowseTargetItem[]; next_cursor: number | null; has_more: boolean }> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (cursor != null) params.set("cursor", String(cursor));
  if (limit) params.set("limit", String(limit));
  const qs = params.toString() ? `?${params}` : "";
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/browse-targets${qs}`);
}

export async function getUnassignedTargets(
  campaignId: string,
  search?: string,
  limit?: number
): Promise<CanvassingTarget[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString() ? `?${params}` : "";
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/unassigned-targets${qs}`);
}

// Action log

export interface ActionLogItem {
  id: string;
  campaign_id: string;
  actor_id: string | null;
  actor_name: string;
  action: string;
  description: string;
  details?: Record<string, unknown>;
  created_at: string;
}

export async function getCampaignActionLog(
  campaignId: string,
  after?: string
): Promise<ActionLogItem[]> {
  const params = new URLSearchParams();
  if (after) params.set("after", after);
  const qs = params.toString() ? `?${params}` : "";
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/action-log${qs}`);
}

export interface RecutListResponse {
  walk_list_id: string;
  pass_number: number;
  entry_count: number;
}

export async function recutCampaignList(
  campaignId: string,
  body: {
    max_attempts: number;
    include_dispositions: string[];
    mode: "canvass" | "call" | null;
    follow_up_category?: "revisit" | "literature" | null;
  }
): Promise<RecutListResponse> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/recut-list`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function deleteCanvassingCampaign(campaignId: string): Promise<void> {
  await fetchApi(`/api/canvassing/campaigns/${campaignId}`, { method: "DELETE" });
}

export async function getCampaignHeatMap(
  id: string
): Promise<{ zones: Array<{ postal_code: string; target_count: number; contacted_count: number; coverage_pct: number; positive_count: number; centroid_lat: number | null; centroid_lon: number | null }> }> {
  return fetchApi(`/api/canvassing/campaigns/${id}/heat-map`);
}

export async function getCampaignCanvassers(
  id: string
): Promise<{
  canvassers: Array<Record<string, unknown>>;
  unassigned_lists: Array<Record<string, unknown>>;
  /** Members excluded from `canvassers` because their only footprint in the
   *  campaign is their own personal list (e.g. a "Þrír til fimm" participant)
   *  with no volunteer row and no logged work. Reported so the panel can say
   *  so rather than silently shrinking. */
  personal_list_only: number;
  partial: boolean;
}> {
  // 45s ceiling — Reykjavík-scale campaigns (~600 walk lists + contact_logs
  // scan in member_contact_rows) can push past the 12s default, and the
  // backend already self-degrades to ``partial: true`` rather than 500ing,
  // so giving the request room to land is preferable to a client abort.
  return fetchApi(`/api/canvassing/campaigns/${id}/canvassers`, {}, 45000);
}

export async function analyzeCampaignTerritory(data: {
  svfnr: string;
  postal_codes?: string[];
  campaign_id?: string;
}): Promise<{ briefing_md: string }> {
  return fetchApi("/api/canvassing/analyze", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function analyzeCampaignResults(
  id: string
): Promise<{ briefing_md: string }> {
  return fetchApi(`/api/canvassing/campaigns/${id}/analyze`, {
    method: "POST",
  });
}

// ---------- Candidacies ----------

export async function submitCandidacy(
  formData: FormData,
): Promise<{ success: boolean; message: string }> {
  const url = `${API_BASE}/api/candidacies/submit`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 120000);

  const extraHeaders: Record<string, string> = {};
  if (getViewMode() === "user") {
    extraHeaders["X-View-As"] = "user";
  }

  try {
    const res = await fetch(url, {
      method: "POST",
      signal: controller.signal,
      credentials: "include",
      headers: extraHeaders,
      body: formData,
    });

    if (res.status === 401) {
      if (typeof window !== "undefined" && window.location.pathname !== "/login" && window.location.pathname !== "/callback") window.location.href = "/login";
      throw new ApiError(401, "Óheimil aðgangur");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
    }
    return res.json();
  } finally {
    clearTimeout(timeout);
  }
}

// Geo API functions

export async function getMunicipalities(): Promise<Municipality[]> {
  return fetchApi("/api/geo/municipalities");
}

export async function getMunicipalitiesForOrg(orgId: string): Promise<Municipality[]> {
  return fetchApi(`/api/geo/municipalities/for-org/${orgId}`);
}

export async function getOrgMunicipalities(orgId: string): Promise<{ svfnr: string; name: string }[]> {
  return fetchApi(`/api/organizations/${orgId}/municipalities`);
}

export async function setOrgMunicipalities(orgId: string, svfnr: string[]): Promise<void> {
  return fetchApi(`/api/organizations/${orgId}/municipalities`, {
    method: "PUT",
    body: JSON.stringify({ svfnr }),
  });
}

export async function getMunicipality(
  svfnr: string
): Promise<Record<string, unknown>> {
  return fetchApi(`/api/geo/municipalities/${svfnr}`);
}

// ── Municipal election history (d'Hondt / Atkvæðaskipting) ─────────────

export interface MunicipalElectionResult {
  year: number;
  list_letter: string;
  list_name: string;
  votes: number | null;
  vote_share: number | null;
  seats: number | null;
}

export interface MunicipalElectionTurnout {
  year: number;
  sex: string;
  eligible_voters: number | null;
  votes_cast: number | null;
  turnout_pct: number | null;
}

export interface MunicipalElectionVotes {
  year: number;
  total_votes: number | null;
  absentee_votes: number | null;
  valid_ballots: number | null;
  blank_ballots: number | null;
  invalid_ballots: number | null;
  population: number | null;
}

export interface MunicipalElectionHistory {
  svfnr: string;
  election_results: MunicipalElectionResult[];
  election_turnout: MunicipalElectionTurnout[];
  election_votes: MunicipalElectionVotes[];
}

export async function getMunicipalityElectionHistory(
  svfnr: string
): Promise<MunicipalElectionHistory> {
  return fetchApi(`/api/geo/municipalities/${svfnr}/election-history`);
}

// ---------- QA (Spurt og Svarað) ----------

export type QAPriority = "normal" | "urgent" | "breaking";
export type QAStatus = "draft" | "published" | "archived";
export type QAChatMode = "rebut" | "chat";

export interface QACategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  display_order: number;
}

export interface QATag {
  id: string;
  name: string;
  slug: string;
}

export interface QAMedia {
  id: string;
  file_url: string;
  file_type: string;
  alt_text: string | null;
  display_order: number;
}

export interface QAItemSummary {
  id: string;
  title: string;
  answer_short: string;
  priority: QAPriority;
  status: QAStatus;
  pinned: boolean;
  published_at: string | null;
  copy_count: number;
  has_rebuttal: boolean;
  media_count: number;
  is_bookmarked: boolean;
  category: QACategory | null;
}

export interface QAItem extends QAItemSummary {
  answer_medium: string;
  answer_long: string;
  rebuttal_claim: string | null;
  view_count: number;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  tags: QATag[];
  media: QAMedia[];
}

export interface QAConversation {
  id: string;
  title: string | null;
  mode: QAChatMode;
  created_at: string;
  updated_at: string;
}

export interface QAMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  pasted_post: string | null;
  referenced_items: string[] | null;
  model: string | null;
  created_at: string;
}

export async function getQACategories(): Promise<QACategory[]> {
  return fetchApi("/api/qa/categories");
}

export async function getQAItems(params?: {
  status?: string;
  category_id?: string;
  priority?: string;
  search?: string;
  bookmarked?: boolean;
  organization_id?: string;
  cursor_published_at?: string;
  cursor_id?: string;
  limit?: number;
}): Promise<PaginatedResponse<QAItemSummary>> {
  const sp = new URLSearchParams();
  if (params?.status) sp.set("status", params.status);
  if (params?.category_id) sp.set("category_id", params.category_id);
  if (params?.priority) sp.set("priority", params.priority);
  if (params?.search) sp.set("search", params.search);
  if (params?.bookmarked) sp.set("bookmarked", "true");
  if (params?.organization_id) sp.set("organization_id", params.organization_id);
  if (params?.cursor_published_at) sp.set("cursor_published_at", params.cursor_published_at);
  if (params?.cursor_id) sp.set("cursor_id", params.cursor_id);
  if (params?.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return fetchApi(`/api/qa${qs ? `?${qs}` : ""}`);
}

export async function getQAItem(id: string): Promise<QAItem> {
  return fetchApi(`/api/qa/${id}`);
}

export async function createQAItem(data: {
  title: string;
  answer_short: string;
  answer_medium: string;
  answer_long: string;
  rebuttal_claim?: string;
  category_id?: string;
  organization_id?: string;
  priority?: QAPriority;
  status?: QAStatus;
  pinned?: boolean;
  tag_ids?: string[];
  media?: { file_url: string; file_type: string; alt_text?: string }[];
}): Promise<QAItem> {
  return fetchApi("/api/qa", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateQAItem(
  id: string,
  data: Record<string, unknown>,
): Promise<QAItem> {
  return fetchApi(`/api/qa/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteQAItem(id: string): Promise<void> {
  return fetchApi(`/api/qa/${id}`, { method: "DELETE" });
}

export async function toggleQABookmark(id: string): Promise<{ bookmarked: boolean }> {
  return fetchApi(`/api/qa/${id}/bookmark`, { method: "POST" });
}

export async function trackQAEngagement(
  id: string,
  action: "view" | "copy_short" | "copy_medium" | "copy_long",
): Promise<void> {
  return fetchApi(`/api/qa/${id}/track`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

export async function getQAConversations(params?: {
  cursor_updated_at?: string;
  limit?: number;
}): Promise<{ items: QAConversation[]; has_more: boolean }> {
  const sp = new URLSearchParams();
  if (params?.cursor_updated_at) sp.set("cursor_updated_at", params.cursor_updated_at);
  if (params?.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return fetchApi(`/api/qa/chat/conversations${qs ? `?${qs}` : ""}`);
}

export async function getQAConversation(
  id: string,
): Promise<QAConversation & { messages: QAMessage[] }> {
  return fetchApi(`/api/qa/chat/conversations/${id}`);
}

export async function deleteQAConversation(id: string): Promise<void> {
  return fetchApi(`/api/qa/chat/conversations/${id}`, { method: "DELETE" });
}

export async function getQATags(): Promise<QATag[]> {
  return fetchApi("/api/qa/tags");
}

export interface QASearchResult {
  id: string;
  title: string;
  category: string;
  score: number;
}

export async function searchQAItems(q: string): Promise<{ results: QASearchResult[] }> {
  return fetchApi(`/api/qa/search?q=${encodeURIComponent(q)}`);
}

export async function submitQAFeedback(
  messageId: string,
  rating: boolean,
  note?: string,
): Promise<void> {
  return fetchApi(`/api/qa/chat/messages/${messageId}/feedback`, {
    method: "POST",
    body: JSON.stringify({ rating, note }),
  });
}

// ---------------------------------------------------------------------------
// Elections
// ---------------------------------------------------------------------------

export type ElectionCampaignType = "sveitarstjorn" | "althingi" | "thjodaratkvaedagreidsla" | "primary" | "internal";
export type ElectionStatus = "planning" | "active" | "completed" | "archived";

export interface Election {
  id: string;
  name: string;
  description?: string;
  election_type: ElectionCampaignType;
  election_day: string;
  status: ElectionStatus;
  target_doors: number;
  target_calls: number;
  early_voting_md?: string | null;
  kjordagsgogn_enabled: boolean;
  calling_enabled: boolean;
  /** "Kjördagshamur": the election-day screen of /call. */
  kjordagur_mode?: boolean;
  /** Location notes shown on the call page by postal code. */
  call_location_notes?: LocationNote[];
  // Onboarding clip existence flag; the actual audio plays via the 1h signed
  // URL from getElectionHelpClipUrl (private bucket, never a stored URL).
  has_help_clip?: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
  my_role?: string | null;
}

export interface ElectionDetail extends Election {
  canvassing_count: number;
  pending_campaigns: number;
}

export interface ElectionActiveItem {
  id: string;
  name: string;
  election_day: string;
  election_type: ElectionCampaignType;
}

export interface ElectionProgressCampaign {
  id: string;
  name: string;
  svfnr: string | null;
  // The election-progress endpoint does not emit scope_type today, so this is
  // optional. A national (landslisti) campaign is identified by a NULL svfnr
  // within an election (municipality campaigns always carry an svfnr); prefer
  // scope_type when a payload does include it. See isNationalCampaign().
  scope_type?: CampaignScopeType;
  // null while the per-campaign progress is still streaming in (cache
  // miss). The Sveitarfélög grid renders a spinner in count cells when
  // these are null, then swaps to real values when streamElectionProgress
  // delivers the per-campaign update line.
  targets: number | null;
  contacted: number | null;
  // responded = distinct people in this campaign's municipality whose
  // latest disposition is in the meaningful-response set. Drives the
  // Framvinda column in the election-overview Listar.
  responded: number | null;
  kjordaemi: string | null;
}

export interface ElectionProgress {
  election: {
    id: string;
    name: string;
    election_day: string;
    target_doors: number;
    target_calls: number;
  };
  campaigns: ElectionProgressCampaign[];
  // null on the streaming path until the {"totals": ...} line lands — the
  // backend defers the heavy election-wide aggregate so the Sveitarfélög
  // list paints on line 1 instead of waiting on it. The non-streaming
  // getElectionProgress always returns a populated object.
  totals: {
    target_count: number;
    contacted_count: number;
    responded_count: number;
    door_count: number;
    call_count: number;
    disposition_summary: Record<string, number>;
    // Personal-list engagement, scoped to lists explicitly activated for
    // this election. personal_list_count = number of activated lists;
    // personal_list_people = distinct voters on them; personal_list_completed
    // = those with a final disposition. % completed is derived in the UI.
    personal_list_count: number;
    personal_list_people: number;
    personal_list_completed: number;
    days_remaining: number;
  } | null;
}

export type ElectionProgressTotals = NonNullable<ElectionProgress["totals"]>;

export async function getElections(
  status?: string,
  cursor?: string,
  limit = 20,
): Promise<{ items: Election[]; next_cursor: string | null; has_more: boolean }> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (cursor) params.set("cursor", cursor);
  params.set("limit", String(limit));
  return fetchApi(`/api/elections?${params}`);
}

export async function getActiveElections(): Promise<ElectionActiveItem[]> {
  return fetchApi("/api/elections/active");
}

export async function createElection(data: {
  name: string;
  election_type: ElectionCampaignType;
  election_day: string;
  description?: string;
  target_doors?: number;
  target_calls?: number;
}): Promise<Election> {
  return fetchApi("/api/elections", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getElection(id: string): Promise<ElectionDetail> {
  return fetchApi(`/api/elections/${id}`);
}

// Onboarding help clip for the /call "Hjálp" modal. Multipart like uploadImage
// (fetchApi JSON-encodes bodies, so this goes through fetch directly).
export async function uploadElectionHelpClip(
  electionId: string,
  file: Blob,
  filename: string,
): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file, filename);

  const url = `${API_BASE}/api/elections/${electionId}/help-clip`;
  const headers: Record<string, string> = {};
  if (getViewMode() === "user") {
    headers["X-View-As"] = "user";
  }

  const res = await fetch(url, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }

  return res.json();
}

export async function deleteElectionHelpClip(electionId: string): Promise<void> {
  return fetchApi(`/api/elections/${electionId}/help-clip`, {
    method: "DELETE",
  });
}

/** 1h signed play URL for the onboarding clip (404 → ApiError when none). */
export async function getElectionHelpClipUrl(electionId: string): Promise<{ url: string }> {
  return fetchApi(`/api/elections/${electionId}/help-clip`);
}

// ---------- Calling announcements (kosningastjóri broadcast / temporary stop) ----------

export interface CallingAnnouncement {
  id: string;
  message: string | null;
  pause_calling: boolean;
  has_audio: boolean;
  /** 1h signed play URL when has_audio; null otherwise. */
  audio_url: string | null;
  created_at: string;
}

export async function getActiveAnnouncement(
  electionId: string,
): Promise<{ announcement: CallingAnnouncement | null }> {
  return fetchApi(`/api/elections/${electionId}/announcement/active`);
}

export async function createAnnouncement(
  electionId: string,
  opts: { message?: string; pauseCalling: boolean; audio?: Blob | null },
): Promise<CallingAnnouncement> {
  const formData = new FormData();
  if (opts.message) formData.append("message", opts.message);
  formData.append("pause_calling", String(opts.pauseCalling));
  if (opts.audio) {
    const isMp4 = opts.audio.type.startsWith("audio/mp4");
    formData.append("file", opts.audio, isMp4 ? "tilkynning.m4a" : "tilkynning.webm");
  }

  const url = `${API_BASE}/api/elections/${electionId}/announcement`;
  const headers: Record<string, string> = {};
  if (getViewMode() === "user") headers["X-View-As"] = "user";

  const res = await fetch(url, { method: "POST", body: formData, credentials: "include", headers });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }
  return res.json();
}

export async function clearAnnouncement(electionId: string): Promise<void> {
  return fetchApi(`/api/elections/${electionId}/announcement`, { method: "DELETE" });
}

// ---------- Telephony analytics (answer-rate / talk-time + inbound) ----------

export interface TelephonyStats {
  totals: {
    calls: number;
    answered: number;
    answer_rate: number;
    avg_talk_seconds: number;
    hangup_by: { voter: number; caller: number; system: number };
  };
  by_hour: { hour: string; calls: number; answered: number }[];
  by_volunteer: {
    member_id: string;
    name: string;
    calls: number;
    answered: number;
    avg_talk_seconds: number;
  }[];
  inbound: {
    total: number;
    by_hour: { hour: string; calls: number }[];
    recent: {
      caller_number: string;
      // Name of the person the caller number resolved to in the registry.
      caller_name: string | null;
      received_at: string;
      // Attribution to the outbound call that triggered the callback; all null
      // when the caller couldn't be matched to a person we called.
      volunteer_id: string | null;
      volunteer_name: string | null;
      list_name: string | null;
      outbound_at: string | null;
      callback_delay_seconds: number | null;
    }[];
  };
}

export async function getTelephonyStats(electionId: string): Promise<TelephonyStats> {
  return fetchApi(`/api/elections/${electionId}/telephony-stats`);
}

export async function updateElection(
  id: string,
  data: Partial<{
    name: string;
    description: string;
    election_type: ElectionCampaignType;
    election_day: string;
    status: ElectionStatus;
    target_doors: number;
    target_calls: number;
    kjordagsgogn_enabled: boolean;
    calling_enabled: boolean;
    kjordagur_mode: boolean;
    call_location_notes: LocationNote[];
  }>,
): Promise<Election> {
  return fetchApi(`/api/elections/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function requestElectionCompletion(id: string): Promise<{ status: string; approval_id: string }> {
  return fetchApi(`/api/elections/${id}/complete`, { method: "POST" });
}

// ---- Telephony (embedded WebRTC calling) ----
export interface TelephonySession {
  username: string;
  password: string;
  wss_url: string;
  sip_domain: string;
  ttl: number;
  /** STUN + ephemeral TURN creds from the box's coturn, ready for pcConfig.
   *  Absent when the box has no TURN configured (frontend falls back to STUN). */
  ice_servers?: RTCIceServer[] | null;
  /** Lease-instance token. Echoed as ?nonce= on /session/end so a stale
   *  release beacon can never free a lease a newer tab acquired. Absent on
   *  older backends (release then falls back to legacy match-by-member). */
  lease_nonce?: string | null;
  /** Which outbound source the lease is on ("xd" = the party's own fleet). The
   *  authoritative answer to "whose number will this call present". */
  source_key?: string;
  source_label?: string;
}

/** Purpose-scoped lease body. `campaign` (the /call page) stays election-scoped
 *  exactly as before; the floating-softphone purposes are election-independent
 *  (outreach carries election_id purely as RBAC context, admin is the
 *  super-admin Sími phone).
 *
 *  `walk_list_id` names the list being worked so the backend can lease from
 *  THAT list's outbound source. The client never sends a source key: which
 *  organisation a list calls as is resolved server-side, so a volunteer cannot
 *  choose it. */
export type TelephonyLeaseRequest =
  | { campaign_id: string; walk_list_id?: string }
  | { purpose: "outreach"; election_id: string }
  | { purpose: "admin" };

/** An outbound call source a list can be pinned to ("Hringt út frá"). Labels
 *  and a sample caller ID only: no hostnames, no secrets. */
export interface CallSourceOption {
  key: string;
  label: string;
  is_default: boolean;
  enabled: boolean;
  cli_sample?: string | null;
}

export async function listCallSources(): Promise<CallSourceOption[]> {
  const res = await fetchApi<{ sources: CallSourceOption[] }>(`/api/telephony/sources`);
  return res.sources ?? [];
}

/** Lease a SIP credential for the caller. Throws on any non-2xx (feature
 *  off, box down, pool full) so the caller falls back to the tel: link. The
 *  matching release is a navigator.sendBeacon("/api/telephony/session/end")
 *  from the hook cleanup, so it survives page unload (a fetch would be
 *  cancelled); see lib/telephony/use-webrtc-call.ts. */
export async function telephonySession(lease: TelephonyLeaseRequest): Promise<TelephonySession> {
  return fetchApi(`/api/telephony/session`, {
    method: "POST",
    body: JSON.stringify(lease),
  });
}

/** Announce, once at dial time, who a WebRTC call is for (call_id -> voter +
 *  list). Identity only; the box drives the answered/ended lifecycle. Best-
 *  effort — a failure just means the call may not show in the live view. */
export async function telephonyCallIntent(body: {
  call_id: string;
  campaign_id: string;
  walk_list_id?: string;
  target_kennitala_hash: string;
  to_number?: string;
  pool_slot?: string;
}): Promise<void> {
  await fetchApi(`/api/telephony/call-intent`, { method: "POST", body: JSON.stringify(body) });
}

// ---- Telephony box health (super-admin Heilsa) ----
export interface TelephonyBoxHealth {
  box_id: string;
  online: boolean;
  last_seen: string;
  seconds_since: number;
  active_calls: number;
  peak_today: number;
  registered: number;
  /** Carrier trunk reachable? null = unknown (not qualified / older agent). */
  trunk_ok: boolean | null;
  pool_size: number;
  load1: number;
  load5: number;
  load15: number;
  cores: number;
  cpu_pct: number;
  mem_used_mb: number;
  mem_total_mb: number;
  disk_pct: number;
  est_max_calls: number | null;
  headroom_pct: number | null;
}
export interface TelephonyHealthResponse {
  now: string;
  boxes: TelephonyBoxHealth[];
  fleet: { boxes: number; online: number; active_calls: number; est_max_calls: number | null };
}
export async function getTelephonyBoxHealth(): Promise<TelephonyHealthResponse> {
  return fetchApi(`/api/admin/telephony/health`);
}

// ---- Live "actually on the call" view (national kosningastjóri) ----
export interface LiveCall {
  call_id: string;
  // Volunteer identity for one-card-per-caller de-dup (a volunteer has one
  // line). Optional: older backend payloads omit it.
  member_id?: string | null;
  volunteer_name: string;
  voter_name: string;
  voter_phone: string | null;
  voter_age: number | null;
  voter_postal_code: string | null;
  voter_municipality: string | null;
  list_name: string | null;
  answered_at: string | null;
  seconds: number;
  // For national-only supervisor monitoring (ChanSpy target).
  box_host: string | null;
  pool_slot: string | null;
}
export async function getElectionLiveCalls(electionId: string): Promise<{ calls: LiveCall[] }> {
  return fetchApi(`/api/elections/${electionId}/live-calls`);
}

export interface SupervisorSession {
  wss_url: string;
  username: string;
  password: string;
  sip_domain: string;
  ttl: number;
  ice_servers: RTCIceServer[] | null;
  mode: "listen" | "whisper" | "barge";
  /** Feature-code extension for the REQUESTED mode only (55/66/77 + pool slot);
   * escalating to another mode needs a fresh, separately audited request. */
  extension: string;
}

/** Lease a supervisor SIP credential to monitor a live call (national
 * kosningastjóri only; the backend 403s otherwise). `mode` is for the audit
 * trail; the returned extensions choose listen/whisper/barge. `auto` marks a
 * lease issued by the auto-advance listen loop (vs a fresh human click) in
 * that same trail. */
export async function superviseCall(
  electionId: string,
  callId: string,
  mode: "listen" | "whisper" | "barge",
  auto = false,
): Promise<SupervisorSession> {
  return fetchApi(`/api/elections/${electionId}/supervise`, {
    method: "POST",
    body: JSON.stringify({ call_id: callId, mode, auto }),
  });
}

export async function deleteElection(id: string): Promise<void> {
  return fetchApi(`/api/elections/${id}`, { method: "DELETE" });
}

/**
 * Super-admin-only audit export: every export_consents row for the
 * election (delivered + never-downloaded), as an xlsx with autofilter
 * on every column. Returns ``{ filename, blob, rowCount? }`` so the
 * caller can save the file and toast the row count.
 *
 * Errors are surfaced via ApiError — 403 means the caller isn't a
 * global admin; 404 means the election doesn't exist.
 */
export async function downloadElectionApprovalLog(electionId: string): Promise<{
  filename: string;
  blob: Blob;
}> {
  const url = `${API_BASE}/api/elections/${electionId}/approval-log.xlsx`;
  const res = await fetch(url, {
    credentials: "include",
    headers: getViewMode() === "user" ? { "X-View-As": "user" } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `adgerdaskra-${electionId}.xlsx`;
  const blob = await res.blob();
  return { filename, blob };
}

// ---------------------------------------------------------------------------
// Election permissions
// ---------------------------------------------------------------------------

export type ElectionPermissionName =
  | "national.view" | "national.manage"
  | "municipality.view" | "municipality.manage"
  | "roles.view" | "roles.allocate"
  | "volunteers.view" | "volunteers.manage"
  | "volunteers.assign";

export type ElectionPresetKey = "kosningastjori" | "mannaudsstjori" | "vaktstjori" | "skodunaradgangur";
export type ElectionScope = "national" | "municipality";

/** Icelandic display labels for election access-level (role) presets.
 *  Mirrors ELECTION_PERMISSION_PRESETS[*].label on the backend. */
export const ELECTION_PRESET_LABELS: Record<ElectionPresetKey, string> = {
  kosningastjori: "Kosningastjóri",
  mannaudsstjori: "Mannauðsstjóri",
  vaktstjori: "Vaktstjóri",
  skodunaradgangur: "Skoðunaraðgangur",
};

export interface ElectionPermissionEntry {
  id: string;
  member_id: string;
  election_id: string;
  campaign_id: string | null;
  permission: ElectionPermissionName;
  assigned_by: string | null;
  assigned_at: string;
  member_name?: string;
  member_kennitala?: string | null;
  member_phone?: string | null;
  member_profile_image_url?: string | null;
}

export interface ElectionPermissionInfo {
  election_id: string;
  election_name: string;
  campaign_id: string | null;
  permission: string;
}

export interface ElectionPresetDefinition {
  label: string;
  description: string;
  scopes: ElectionScope[];
}

export async function getElectionPermissions(
  electionId: string, campaignId?: string,
): Promise<ElectionPermissionEntry[]> {
  const params = new URLSearchParams();
  if (campaignId) params.set("campaign_id", campaignId);
  const qs = params.toString();
  return fetchApi(`/api/elections/${electionId}/permissions${qs ? `?${qs}` : ""}`);
}

export async function applyElectionPreset(
  electionId: string,
  data: { member_id: string; preset: ElectionPresetKey; scope: ElectionScope; campaign_ids?: string[] },
): Promise<{ created: number }> {
  return fetchApi(`/api/elections/${electionId}/permissions/preset`, {
    method: "POST", body: JSON.stringify(data),
  });
}

export async function revokeAllElectionPermissions(
  electionId: string,
  data: { member_id: string; campaign_ids?: string[] },
): Promise<{ removed: number }> {
  return fetchApi(`/api/elections/${electionId}/permissions/revoke-all`, {
    method: "POST", body: JSON.stringify(data),
  });
}

export async function searchMembersForRole(
  electionId: string,
  q: string,
  campaignId?: string,
): Promise<{ id: string; name: string }[]> {
  const params = new URLSearchParams({ q });
  if (campaignId) params.set("campaign_id", campaignId);
  return fetchApi(`/api/elections/${electionId}/permissions/member-search?${params}`);
}

export async function lookupMemberForRole(
  electionId: string,
  kennitala: string,
  campaignId?: string,
): Promise<{ found: boolean; member?: { id: string; name: string } }> {
  const params = new URLSearchParams({ kennitala });
  if (campaignId) params.set("campaign_id", campaignId);
  return fetchApi(`/api/elections/${electionId}/permissions/member-lookup?${params}`);
}

export async function getElectionPresets(
  electionId: string,
  campaignOnly?: boolean,
): Promise<Record<ElectionPresetKey, ElectionPresetDefinition>> {
  const qs = campaignOnly ? "?campaign_only=true" : "";
  return fetchApi(`/api/elections/${electionId}/permissions/presets${qs}`);
}

export async function getMyElectionPermissions(
  electionId: string,
): Promise<{ national: string[]; all_campaigns: string[]; campaigns: Record<string, string[]> }> {
  return fetchApi(`/api/elections/${electionId}/permissions/me`);
}

export async function getElectionProgress(electionId: string): Promise<ElectionProgress> {
  return fetchApi(`/api/canvassing/elections/${electionId}/progress`);
}

export interface ElectionProgressCountUpdate {
  id: string;
  targets: number;
  contacted: number;
  responded: number;
}

/**
 * Stream election progress with progressive per-campaign count loading.
 * The returned Promise resolves as soon as line 1 arrives (election +
 * campaigns with cached counts + totals) so React Query can render the
 * Sveitarfélög grid immediately. Cache misses come back with null
 * targets/contacted/responded; they fill in via ``onUpdates`` as the
 * backend's concurrent fan-out resolves each per-campaign query.
 */
export async function streamElectionProgress(
  electionId: string,
  onUpdates: (updates: ElectionProgressCountUpdate[]) => void,
  onTotals?: (totals: ElectionProgressTotals) => void,
): Promise<ElectionProgress> {
  const url = `${API_BASE}/api/canvassing/elections/${electionId}/progress?stream=true`;

  const extraHeaders: Record<string, string> = {};
  if (getViewMode() === "user") {
    extraHeaders["X-View-As"] = "user";
  }

  const res = await fetch(url, {
    credentials: "include",
    headers: extraHeaders,
  });

  if (res.status === 401) {
    if (typeof window !== "undefined") {
      const p = window.location.pathname;
      if (p !== "/login" && p !== "/callback") window.location.href = "/login";
    }
    throw new ApiError(401, "Óheimil aðgangur");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // Read until line 1 (the initial election + campaigns + totals).
  // Resolve the Promise immediately so React Query renders the grid.
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      // Backend hung up before yielding line 1 — surface as an error so
      // React Query can retry rather than silently rendering nothing.
      throw new ApiError(500, "Stream lokað án gagna");
    }
    buffer += decoder.decode(value, { stream: true });

    // Skip any leading empty lines (backend never emits them, but be
    // defensive against double-newlines around the framing).
    while (buffer.startsWith("\n")) buffer = buffer.slice(1);

    const newlineIdx = buffer.indexOf("\n");
    if (newlineIdx === -1) continue; // partial line 1 — keep reading

    const firstLine = buffer.slice(0, newlineIdx);
    let carryover = buffer.slice(newlineIdx + 1);

    const data = JSON.parse(firstLine) as ElectionProgress;
    if (!data.campaigns) {
      throw new ApiError(500, "Stream byrjaði ekki á upphafsgögnum");
    }

    // Any update lines that arrived in the same TCP chunk as line 1
    // must merge into the returned data, NOT fire via onUpdates. If they
    // fired via onUpdates → setQueryData, the merge would run BEFORE
    // React Query has stored line 1 (the IIFE's microtask is queued
    // before the queryFn-resolution microtask, so its setQueryData would
    // see ``old === undefined`` and drop the update). Merging into the
    // returned data sidesteps the race entirely — React Query stores
    // the already-merged version.
    const carryoverLines = carryover.split("\n");
    carryover = carryoverLines.pop()!;
    if (carryoverLines.length > 0) {
      const updateMap = new Map<string, ElectionProgressCountUpdate>();
      for (const line of carryoverLines) {
        if (!line.trim()) continue;
        try {
          const u = JSON.parse(line);
          // A totals line that rode in the same chunk as line 1 is merged
          // into the returned data (not fired via onTotals) for the same
          // reason as the campaign updates below — onTotals → setQueryData
          // would run before React Query stored line 1.
          if (u.totals) data.totals = u.totals;
          else if (u.id) updateMap.set(u.id, u);
        } catch { /* malformed — skip */ }
      }
      if (updateMap.size > 0) {
        data.campaigns = data.campaigns.map((c) => {
          const u = updateMap.get(c.id);
          return u
            ? {
                ...c,
                targets: u.targets,
                contacted: u.contacted,
                responded: u.responded,
              }
            : c;
        });
      }
    }

    _readElectionProgressUpdates(reader, decoder, carryover, onUpdates, onTotals);
    return data;
  }
}

/** Fire-and-forget: reads remaining NDJSON lines after the initial payload. */
function _readElectionProgressUpdates(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
  buffer: string,
  onUpdates: (updates: ElectionProgressCountUpdate[]) => void,
  onTotals?: (totals: ElectionProgressTotals) => void,
) {
  (async () => {
    let buf = buffer;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop()!;
        const batch: ElectionProgressCountUpdate[] = [];
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const data = JSON.parse(line);
            if (data.totals) onTotals?.(data.totals);
            else if (data.id) batch.push(data);
          } catch {
            /* malformed line — skip, don't tear down the whole reader */
          }
        }
        if (batch.length > 0) onUpdates(batch);
      }
      if (buf.trim()) {
        try {
          const data = JSON.parse(buf);
          if (data.totals) onTotals?.(data.totals);
          else if (data.id) onUpdates([data]);
        } catch { /* partial line — ignore */ }
      }
    } catch {
      /* stream error — counts stale until next refetch */
    }
  })();
}

export interface ElectionBoundary {
  svfnr: string;
  name: string;
  geojson: string;
  xd_vote_share: number | null;
  election_year: number | null;
  turnout_pct: number | null;
  member_count: number;
}

/**
 * Per-address marker properties returned by the election map endpoint.
 * Each feature is one geocoded address (`heinum`); apartment buildings
 * collapse multiple voters into one feature with aggregate counts.
 *  - a: address text
 *  - vc: voter count at this address
 *  - mc: members at this address
 *  - cc: voters at this address with a logged disposition
 *  - dc: {disposition: count} histogram across the address
 *  - cn: campaign name owning the svfnr
 */
export interface ElectionMapFeatureProps {
  a: string | null;
  vc: number;
  mc: number;
  cc: number;
  dc: Record<string, number>;
  cn: string;
}

export interface ElectionMapData {
  targets: GeoJSON.FeatureCollection<GeoJSON.Point, ElectionMapFeatureProps>;
  boundaries: ElectionBoundary[];
  target_count: number;
  campaigns: Array<{ id: string; name: string; svfnr?: string }>;
}

export async function getElectionMapData(electionId: string): Promise<ElectionMapData> {
  return fetchApi(`/api/canvassing/elections/${electionId}/map-data`);
}

export interface HistoricalComparison {
  name: string;
  kjordaemi: string | null;
  contacted: number;
  current_positive_pct: number;
  last_xd_vote_share: number;
  last_election_year: number | null;
  last_turnout_pct: number | null;
  trend: number;
}

export async function getElectionHistorical(electionId: string): Promise<HistoricalComparison[]> {
  return fetchApi(`/api/canvassing/elections/${electionId}/historical`);
}

// ---------- Approvals ----------

/** One row of a board_update beiðni's entries, as projected into the approvals
 *  list (safe fields only: the KEK-encrypted kennitala snapshot is never sent). */
export interface BoardChangeEntryDetail {
  kennitala_hash: string | null;
  name_snapshot: string | null;
  role: string;
  title?: string | null;
  started_on?: string | null;
  /** true when the hash did not resolve in Þjóðskrá at submit time. */
  unverified: boolean;
}

export interface ApprovalItem {
  id: string;
  request_type: "claim_batch" | "due_generation" | "campaign_send" | "event_announce" | "election_deletion" | "election_completion" | "board_update";
  entity_id: string;
  organization_id: string;
  organization_name: string | null;
  organization_code: string | null;
  requested_by: string;
  requester_name: string | null;
  status: string;
  notes: string | null;
  created_at: string | null;
  // Extended details (populated for claim_batch and due_generation)
  org_member_count?: number;
  total_count?: number;
  amount?: number;
  year?: number | null;
  due_date?: string | null;
  // Extended details (populated for election_deletion)
  election_name?: string;
  election_day?: string | null;
  election_status?: string;
  // Extended details (populated for board_update: aðalfundur handoff)
  entry_count?: number;
  meeting_date?: string | null;
  replace_board?: boolean;
  entries?: BoardChangeEntryDetail[];
}

export interface ApprovalCounts {
  claim_batch: number;
  due_generation: number;
  campaign_send: number;
  event_announce: number;
  election_deletion: number;
  election_completion: number;
  board_update: number;
  total: number;
}

export async function getApprovals(params?: { type?: string; status?: string }): Promise<{ items: ApprovalItem[] }> {
  const sp = new URLSearchParams();
  if (params?.type) sp.set("request_type", params.type);
  if (params?.status) sp.set("status", params.status);
  const qs = sp.toString();
  return fetchApi(`/api/approvals${qs ? `?${qs}` : ""}`);
}

export async function getApprovalCounts(): Promise<ApprovalCounts> {
  return fetchApi("/api/approvals/counts");
}

export async function approveApproval(id: string): Promise<{ id: string; status: string; request_type: string }> {
  return fetchApi(`/api/approvals/${id}/approve`, { method: "POST" });
}

export async function rejectApproval(id: string, notes?: string): Promise<{ id: string; status: string; request_type: string }> {
  return fetchApi(`/api/approvals/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ notes: notes || null }),
  });
}

// ---------- Claim Batches ----------

export interface ClaimBatchItem {
  id: string;
  organization_name: string | null;
  due_period_id: string | null;
  total_count: number;
  sent_count: number;
  amount_per_claim: number;
  due_date: string | null;
  status: string;
  creator_name: string | null;
  created_at: string | null;
  year: number | null;
  batch_type: "mandatory" | "voluntary";
}

export async function createClaimBatch(params: {
  organization_id: string;
  due_period_id?: string;
  member_ids?: string[];
  amount_per_claim: number;
  due_date: string;
}): Promise<{ batch_id: string; approval_id: string; status: string; total_count: number }> {
  return fetchApi("/api/payments/claim-batches", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function getClaimBatches(organizationId?: string): Promise<{ items: ClaimBatchItem[] }> {
  const qs = organizationId ? `?organization_id=${organizationId}` : "";
  return fetchApi(`/api/payments/claim-batches${qs}`);
}

export interface OrgClaimItem {
  id: string;
  member_id: string | null;
  member_name: string | null;
  kennitala?: string;
  organization_id: string;
  amount: number;
  status: string;
  claim_type: string;
  description: string | null;
  due_date: string | null;
  created_at: string | null;
}

export async function getOrgClaims(organizationId: string, status?: string): Promise<{ items: OrgClaimItem[] }> {
  const sp = new URLSearchParams({ organization_id: organizationId });
  if (status) sp.set("status", status);
  return fetchApi(`/api/claims/by-org?${sp}`);
}

export interface OrgPaymentItem {
  id: string;
  member_name: string | null;
  amount: number;
  method: string;
  status: string;
  description: string | null;
  reference: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export async function getPaymentHistory(organizationId: string, cursor?: string, limit = 30): Promise<{ items: OrgPaymentItem[]; has_more: boolean; next_cursor: string | null }> {
  const sp = new URLSearchParams({ organization_id: organizationId, limit: String(limit) });
  if (cursor) sp.set("cursor", cursor);
  return fetchApi(`/api/payments/history?${sp}`);
}

export async function createClaimByKennitala(data: {
  kennitala: string;
  organization_id: string;
  amount: number;
  description: string;
  due_date: string;
  final_due_date: string;
}): Promise<{ id: string; status: string }> {
  return fetchApi("/api/claims/create-by-kennitala", { method: "POST", body: JSON.stringify(data) });
}

// ---------------------------------------------------------------------------
// Query Builder (for walk list filter splitting)
// ---------------------------------------------------------------------------

export interface QueryRule {
  field: string;
  operator: string;
  value: string | number | boolean | string[] | number[] | null;
  // Optional issue_tag tag_key for action-response special fields
  // (contact_action_response, contact_action_response_missing).
  tag_key?: string;
  // Optional election scope for the has_issue_tag field in herferðir (the
  // campaign path binds no ambient election). Stamped by the campaign payload
  // normalizer, not held in builder state (the RQB round-trip drops it).
  election_id?: string;
}

export interface QueryGroup {
  combinator: "and" | "or";
  rules: (QueryRule | QueryGroup)[];
  not?: boolean;
}

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldDefinition {
  name: string;
  label: string;
  group: string;
  input_type: string;
  operators: string[];
  options: FieldOption[] | null;
}

export async function getFilterFields(campaignId: string): Promise<FieldDefinition[]> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/filter-fields`);
}

export async function previewFilterCount(
  campaignId: string,
  filterJson: QueryGroup,
  parentWalkListId?: string,
  walkListId?: string,
  includePersonalMembers?: boolean,
  includeNonVoters?: boolean,
): Promise<{ count: number }> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/filter-count`, {
    method: "POST",
    body: JSON.stringify({
      filter_json: filterJson,
      parent_walk_list_id: parentWalkListId ?? null,
      // Id of the list being edited, so walk_list_count / on_personal_list
      // exclude it from their own subquery (matches saved-list membership).
      walk_list_id: walkListId ?? null,
      // Firewall-bypass toggle ("Hafa með fólk af persónulegum listum").
      // Null = resolve from the edited list's stored flag, so callers that
      // don't render the toggle keep the saved behavior.
      include_personal_members: includePersonalMembers ?? null,
      // Kjörskrá/institution bypass toggle ("Hafa með fólk utan
      // kosningakerfis og á stofnunum"), same tri-state contract.
      include_non_voters: includeNonVoters ?? null,
    }),
  });
}

export interface TranslateNlQueryResponse {
  query: QueryGroup;
  explanation: string;
  warnings: string[];
}

export async function translateNaturalLanguageQuery(
  campaignId: string,
  prompt: string,
): Promise<TranslateNlQueryResponse> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/translate-nl`, {
    method: "POST",
    body: JSON.stringify({ prompt }),
  }, 60000); // LLM calls can take longer than the 12s default
}


// ---------------------------------------------------------------------------
// Kosning (member-facing election)
// ---------------------------------------------------------------------------

export type VolunteerCategory =
  | "calling"
  | "door_knocking"
  | "election_day_calling"
  | "election_day_driving"
  | "financial_support"
  | "other";

export interface VolunteerSignupItem {
  // "unspecified" is the backend's no-category signup sentinel; it counts as
  // a signup (my_signups.length checks) but is never a pickable category.
  category: VolunteerCategory | "unspecified";
  scope: string;
}

// ── Universal (election-agnostic) volunteer profile — the Sjálfboðaliðastarf tab ──

export interface VolunteerProfile {
  is_active: boolean;
  categories: VolunteerCategory[];
  regions: string[];
  scope: string;
  availability: string[];
  notes: string | null;
  home_svfnr: string | null;
  home_municipality_name: string | null;
}

export interface VolunteerProfileUpdate {
  is_active: boolean;
  categories: VolunteerCategory[];
  regions?: string[] | null;
  scope?: string;
  availability?: string[] | null;
  notes?: string | null;
}

export interface VolunteerSelfStatus {
  active_election_id: string | null;
  active_election_name: string | null;
  active_election_day: string | null;
  vetting_status: string | null;
  vetting_label: string | null;
  my_campaign_id: string | null;
  my_campaign_name: string | null;
  role: string | null;
  personal_list_id: string | null;
  personal_list_total: number;
  personal_list_completed: number;
  total_calls: number;
  total_contacts: number;
  pending_callout_id: string | null;
  pending_callout_status: string | null;
  pending_callout_message: string | null;
  pending_callout_work_at: string | null;
  pending_callout_confirm_url: string | null;
}

/** The logged-in member's universal volunteer profile (create-on-read default). */
export async function getVolunteerProfile(): Promise<VolunteerProfile> {
  return fetchApi("/api/volunteer/me");
}

/** Upsert the universal profile. Writes volunteer_profiles + reconciles intents. */
export async function updateVolunteerProfile(body: VolunteerProfileUpdate): Promise<VolunteerProfile> {
  return fetchApi("/api/volunteer/me", { method: "PUT", body: JSON.stringify(body) });
}

/** Per-election standing for the logged-in member (empty between elections). */
export async function getVolunteerSelfStatus(): Promise<VolunteerSelfStatus> {
  return fetchApi("/api/volunteer/status");
}

// ── Super-admin roster: everyone in the universal volunteer registry ──

export interface VolunteerRosterItem {
  /** Stable key — the registry is keyed by kennitala_hash so non-members are
   *  first-class entries. */
  kennitala_hash: string;
  /** False for volunteers who are not party members. */
  is_member: boolean;
  name: string;
  categories: VolunteerCategory[];
  regions: string[];
  scope: string;
  availability: string[];
  notes: string | null;
  municipality: string | null;
  postal_code: string | null;
  phone: string | null;
  /** volunteer_profiles.created_at — when they registered ("Skráð"). */
  signed_up_at: string | null;
  updated_at: string | null;
}

/** Super-admin only — full volunteer registry roster. */
export async function getVolunteerRoster(): Promise<{ items: VolunteerRosterItem[]; total: number }> {
  return fetchApi("/api/volunteer/roster");
}

export interface SpezListItem {
  list_id: string;
  label: string | null;
  municipality_name: string | null;
  total: number;
  completed: number;
}

export interface KosningOverview {
  election_id: string;
  election_name: string;
  /** thjodaratkvaedagreidsla hides the personal-list section: the
   *  referendum's list surface is /saman and activation 403s. */
  election_type?: string;
  election_day: string;
  my_campaign_id: string | null;
  my_campaign_name: string | null;
  my_svfnr: string | null;
  my_signups: VolunteerSignupItem[];
  my_personal_list_id: string | null;
  /** True only after the member explicitly pressed "Virkja listann í
   *  kosningunni". A list id WITHOUT this flag is an auto-/admin-created
   *  projection — show the activate CTA, not the projection view. */
  my_personal_list_activated: boolean;
  my_personal_list_total: number;
  my_personal_list_completed: number;
  // One SPEZ follow-up list per municipality where a voter tagged this person.
  my_spez_lists: SpezListItem[];
  // Singular fields mirror the first (highest-priority) SPEZ list — back-compat.
  my_spez_list_id: string | null;
  my_spez_list_total: number;
  my_spez_list_completed: number;
  my_spez_list_label: string | null;
  manager_image_url: string | null;
  manager_message: string | null;
  volunteer_count: number;
}

export interface PersonSearchItem {
  kennitala_hash: string;
  kennitala: string | null;
  name: string;
  address: string | null;
  postal_code: string | null;
  town: string | null;
  age: number | null;
  municipality: string | null;
}

export async function getKosningOverview(electionId?: string): Promise<KosningOverview> {
  const qs = electionId ? `?election_id=${electionId}` : "";
  return fetchApi(`/api/kosning/overview${qs}`);
}

/** REFERENDUM-only: create (or return) the member's empty personal list in
 *  the election so the builder can start from zero. Regular elections keep
 *  the explicit Virkja import instead (the backend 403s there). */
export async function ensureKosningPersonalList(electionId: string): Promise<{ walk_list_id: string }> {
  return fetchApi(`/api/kosning/${electionId}/ensure-personal-list`, { method: "POST" });
}

export async function searchKosningPeople(
  q: string,
  postalCode?: string | null,
  address?: string | null,
  age?: number | null,
): Promise<PersonSearchItem[]> {
  const sp = new URLSearchParams();
  // q is optional on the backend — omit when empty so address-only searches
  // don't trip the (no longer required) min_length validator.
  if (q) sp.set("q", q);
  // Icelandic postal codes are 3 digits.
  if (postalCode && /^\d{3}$/.test(postalCode)) {
    sp.set("postal_code", postalCode);
  }
  if (address) sp.set("address", address);
  // Server-side ±5-year filter (kennitala lookups are exempt backend-side).
  // The backend validates 16–120 — omit anything outside that range.
  if (age != null && age >= 16 && age <= 120) sp.set("age", String(age));
  return fetchApi(`/api/kosning/search?${sp}`);
}

// ── Election-admin omni person finder ("Finna einstakling") ───────────────
// /api/elections/{id}/person-admin — kosningastjóri fix-it/lookup tool.

export interface ElectionPersonSearchItem {
  kennitala_hash: string;
  kennitala: string | null;
  name: string;
  address: string | null;
  postal_code: string | null;
  town: string | null;
  age: number | null;
  municipality: string | null;
  is_member: boolean;
  contact_attempts: number;
  last_contacted_at: string | null;
  last_disposition: string | null;
  final_disposition: string | null;
  do_not_contact: boolean;
}

export interface ElectionPersonContact {
  id: string;
  contact_type: string;
  disposition: string | null;
  source: string;
  notes: string | null;
  issue_tags: string[];
  contacted_at: string;
  contacted_by_name: string | null;
  campaign_name: string | null;
  walk_list_name: string | null;
}

export interface ElectionPersonState {
  contact_attempts: number;
  last_contacted_at: string | null;
  last_contact_type: string | null;
  last_disposition: string | null;
  final_disposition: string | null;
  final_dispositioned_at: string | null;
  do_not_contact: boolean;
}

export interface ElectionPersonProfile {
  person: {
    kennitala_hash: string;
    kennitala: string | null;
    name: string;
    address: string | null;
    postal_code: string | null;
    town: string | null;
    age: number | null;
    municipality: string | null;
    is_voter: boolean;
    is_member: boolean;
    phone_number: string | null;
    phone_secondary: string | null;
  };
  member: {
    id: string;
    name: string;
    join_date: string | null;
    is_active: boolean;
    profile_image_url: string | null;
  } | null;
  state: ElectionPersonState | null;
  contacts: ElectionPersonContact[];
  call_attempts: {
    id: string;
    status: string;
    disposition: string | null;
    started_at: string | null;
    duration_seconds: number | null;
    caller_name: string | null;
    campaign_name: string | null;
    created_at: string;
  }[];
  walk_lists: {
    walk_list_id: string;
    walk_list_name: string;
    list_type: string;
    mode: string | null;
    campaign_id: string;
    campaign_name: string;
    excluded: boolean;
    claim_relation: string | null;
    claimed_by_name: string | null;
  }[];
  donation_offers: {
    id: string;
    method: string;
    status: string;
    suggested_amount: number | null;
    created_at: string;
    completed_at: string | null;
    contacted_by_name: string | null;
    campaign_name: string | null;
  }[];
  volunteer_signups: {
    id: string;
    category: string;
    scope: string;
    svfnr: string | null;
    signed_up_at: string;
    campaign_name: string | null;
    role: string | null;
    status: string | null;
    vetting_status: string | null;
    total_calls: number;
  }[];
  volunteer_referrals: {
    id: string;
    source: string;
    status: string;
    created_at: string;
    confirmed_at: string | null;
    contacted_by_name: string | null;
  }[];
  candidacies: { id: string; status: string; municipality_name: string; created_at: string }[];
  endorsements: { id: string; candidate_name: string; created_at: string }[];
  messages: {
    id: string;
    title: string;
    campaign_type: string;
    status: string;
    delivered_at: string | null;
    sent_at: string | null;
  }[];
  // null = caller lacks crm_finance (render a locked note, not "empty").
  donations:
    | {
        id: string;
        amount: number;
        donation_date: string;
        method: string | null;
        source_type: string | null;
        earmarked: boolean;
      }[]
    | null;
  has_finance_access: boolean;
}

// campaignId switches the endpoints into campaign-scoped mode: opens them to
// that campaign's managers (municipality.manage) but pins search + access to
// the campaign's municipality (whole country for national campaigns).
function personAdminQuery(campaignId?: string): string {
  return campaignId ? `?campaign_id=${campaignId}` : "";
}

export async function searchElectionPeopleAdmin(
  electionId: string,
  q: string,
  campaignId?: string,
): Promise<ElectionPersonSearchItem[]> {
  const sp = new URLSearchParams({ q });
  if (campaignId) sp.set("campaign_id", campaignId);
  return fetchApi(`/api/elections/${electionId}/person-admin/search?${sp}`);
}

export async function getElectionPersonAdminProfile(
  electionId: string,
  kennitalaHash: string,
  campaignId?: string,
): Promise<ElectionPersonProfile> {
  return fetchApi(`/api/elections/${electionId}/person-admin/${kennitalaHash}${personAdminQuery(campaignId)}`);
}

export async function relabelElectionContactLog(
  electionId: string,
  logId: string,
  disposition: Disposition | null,
  campaignId?: string,
): Promise<{ log_id: string; previous_disposition: string | null; disposition: string | null }> {
  return fetchApi(`/api/elections/${electionId}/person-admin/contact-logs/${logId}${personAdminQuery(campaignId)}`, {
    method: "PATCH",
    body: JSON.stringify({ disposition }),
  });
}

export async function setElectionPersonDoNotContact(
  electionId: string,
  kennitalaHash: string,
  doNotContact: boolean,
  campaignId?: string,
): Promise<{ do_not_contact: boolean }> {
  return fetchApi(
    `/api/elections/${electionId}/person-admin/${kennitalaHash}/do-not-contact${personAdminQuery(campaignId)}`,
    {
      method: "POST",
      body: JSON.stringify({ do_not_contact: doNotContact }),
    },
  );
}

export async function setElectionPersonFinalDisposition(
  electionId: string,
  kennitalaHash: string,
  finalDisposition: Disposition | null,
  campaignId?: string,
): Promise<{ final_disposition: string | null }> {
  return fetchApi(
    `/api/elections/${electionId}/person-admin/${kennitalaHash}/final-disposition${personAdminQuery(campaignId)}`,
    {
      method: "POST",
      body: JSON.stringify({ final_disposition: finalDisposition }),
    },
  );
}

// ── Match candidates — staging-grid kennitala matching workspace ──────────

export interface MatchCandidatePayload {
  row_id: string;
  kennitala?: string | null;
  name?: string | null;
  phone?: string | null;
  postal_code?: string | null;
}

export type MatchStatus = "matched" | "ambiguous" | "too_broad" | "unmatched";

export interface MatchCandidateResult {
  row_id: string;
  status: MatchStatus;
  locked: PersonSearchItem | null;
  alternatives: PersonSearchItem[];
  total_count: number;
  needs_postal: boolean;
}

async function matchCandidatesAt(
  path: string,
  candidates: MatchCandidatePayload[],
): Promise<MatchCandidateResult[]> {
  const res = await fetchApi<{ results: MatchCandidateResult[] }>(path, {
    method: "POST",
    body: JSON.stringify({ candidates }),
  });
  return res.results;
}

export function matchKosningCandidates(
  candidates: MatchCandidatePayload[],
): Promise<MatchCandidateResult[]> {
  return matchCandidatesAt("/api/kosning/match-candidates", candidates);
}

export function matchWalkListCandidates(
  walkListId: string,
  candidates: MatchCandidatePayload[],
): Promise<MatchCandidateResult[]> {
  return matchCandidatesAt(
    `/api/canvassing/walk-lists/${walkListId}/match-candidates`,
    candidates,
  );
}

export async function addToPersonalList(
  listId: string,
  kennitalaHash: string,
): Promise<{
  entry_id: string;
  kennitala_hash: string;
  name: string;
  address: string | null;
  co_claimants: CoClaimant[];
}> {
  return fetchApi(`/api/kosning/personal-list/${listId}/add`, {
    method: "POST",
    body: JSON.stringify({ kennitala_hash: kennitalaHash }),
  });
}

export async function removeFromPersonalList(listId: string, entryId: string): Promise<void> {
  await fetchApi(`/api/kosning/personal-list/${listId}/entry/${entryId}`, {
    method: "DELETE",
  });
}

export async function updatePersonalListEntryRelationship(
  listId: string,
  entryId: string,
  relationship: string,
): Promise<void> {
  await fetchApi(`/api/kosning/personal-list/${listId}/entry/${entryId}/relationship`, {
    method: "PATCH",
    body: JSON.stringify({ relationship }),
  });
}

// Personal list entries (standalone kosning endpoint)

/** Global collision state for a member-owned personal-list entry.
 *  'active'     -> this member holds the winning claim (may edit the marking)
 *  'peer'       -> tied at the winner's priority: may contact, not edit
 *  'superseded' -> strictly weaker claim: grayed-out, inert */
export type ClaimState = "active" | "peer" | "superseded";

/** Another member who also holds this person on a personal list. Name + phone
 *  are deliberately exposed so volunteers can resolve collisions among
 *  themselves (product decision 2026-07-02). */
export interface CoClaimant {
  member_id: string;
  member_name: string | null;
  phone: string | null;
  relation: string | null;
  relation_priority: number;
  is_winner: boolean;
}

export interface PersonalListEntry {
  id: string;
  sort_order: number;
  kennitala_hash: string;
  claim_relation: string | null;
  name: string | null;
  address: string | null;
  postal_code: string | null;
  phone_number: string | null;
  contact_score: number | null;
  is_member: boolean;
  latest_disposition: string | null;
  /** Authoritative shared marking (the owner's terminal outcome), or null. */
  final_disposition?: string | null;
  /** Opted out for this election: render the row action-less. */
  do_not_contact?: boolean;
  /** General-list collision state. Null/undefined on election-projection rows. */
  claim_state?: ClaimState | null;
  /** True only for the winning claim — the sole authority to write the marking. */
  can_edit?: boolean;
  /** Other members who also hold this person (name + phone + relation). */
  co_claimants?: CoClaimant[];
  /** ALWAYS null (D2): the winning claimant is never named — render the
   *  generic "Á lista hjá öðrum félaga" for superseded rows. */
  blocked_by?: string | null;
  /** Human-readable relation reason (e.g. "Maki", "Sama heimili"). */
  relation_reason?: string | null;
}

export async function getPersonalListEntries(
  listId: string,
): Promise<{ entries: PersonalListEntry[] }> {
  return fetchApi(`/api/kosning/personal-list/${listId}/entries`);
}

// ── Personal-list reclaim (municipality admin safety net) ──────────────────

export interface PersonalListSummary {
  walk_list_id: string;
  owner_member_id: string | null;
  owner_name: string | null;
  entry_count: number;
  worked_count: number;
  reclaimed_at: string | null;
}

export async function getCampaignPersonalLists(
  campaignId: string,
): Promise<{ lists: PersonalListSummary[] }> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/personal-lists`);
}

export async function previewPersonalList(
  campaignId: string,
  walkListId: string,
): Promise<{
  reclaimed_at: string | null;
  people: { kennitala_hash: string; name: string | null; final_disposition: string | null }[];
}> {
  return fetchApi(
    `/api/canvassing/campaigns/${campaignId}/personal-lists/${walkListId}/preview`,
  );
}

export async function reclaimPersonalList(
  campaignId: string,
  walkListId: string,
  released: boolean,
): Promise<{ walk_list_id: string; reclaimed_at: string | null }> {
  return fetchApi(
    `/api/canvassing/campaigns/${campaignId}/personal-lists/${walkListId}/reclaim`,
    { method: "POST", body: JSON.stringify({ released }) },
  );
}

// ── General member-owned personal list — /api/personal-list/* ──────────────
// Election-independent builder. The list is derived from the authenticated
// member, so these paths carry NO listId (unlike the kosning projection above).

/** Wire shape of a general personal-list entry. The general router names the
 *  relationship field `relation`; we normalise it to `claim_relation` so the
 *  shared grid/cards components work unchanged across both surfaces. */
interface GeneralPersonalListEntryWire {
  id: string;
  kennitala_hash: string;
  relation: string | null;
  relation_reason: string | null;
  sort_order: number;
  name: string | null;
  address: string | null;
  postal_code: string | null;
  phone_number: string | null;
  contact_score: number | null;
  is_member: boolean;
  claim_state: ClaimState | null;
  /** ALWAYS null (D2) — the winning claimant is never named. */
  blocked_by: string | null;
}

/** Set when the member has imported their list into an active election. While
 *  present, the general list is read-only (writes 409) and the /listi page
 *  collapses to a pointer at this election's /kosning page. */
export interface ManagedByElection {
  election_id: string;
  election_name: string;
}

/** An active election with a campaign matched to the member's municipality —
 *  set independently of managed state, so /listi can show the "Það er kosning
 *  í gangi" banner before the member activates (D4). */
export interface ActiveElectionInfo {
  election_id: string;
  election_name: string;
}

export interface MyPersonalListResponse {
  list_id: string;
  entries: PersonalListEntry[];
  managed_by_election: ManagedByElection | null;
  active_election: ActiveElectionInfo | null;
}

export async function getMyPersonalList(): Promise<MyPersonalListResponse> {
  const res = await fetchApi<{
    list_id: string;
    entries: GeneralPersonalListEntryWire[];
    managed_by_election: ManagedByElection | null;
    active_election: ActiveElectionInfo | null;
  }>(`/api/personal-list/`);
  return {
    list_id: res.list_id,
    managed_by_election: res.managed_by_election ?? null,
    active_election: res.active_election ?? null,
    entries: res.entries.map((e) => ({
      id: e.id,
      sort_order: e.sort_order,
      kennitala_hash: e.kennitala_hash,
      claim_relation: e.relation,
      name: e.name,
      address: e.address,
      postal_code: e.postal_code,
      phone_number: e.phone_number,
      contact_score: e.contact_score,
      is_member: e.is_member,
      // General list has no outreach; disposition is always inert here.
      latest_disposition: null,
      claim_state: e.claim_state,
      blocked_by: e.blocked_by,
      relation_reason: e.relation_reason,
    })),
  };
}

export function matchPersonalListCandidates(
  candidates: MatchCandidatePayload[],
): Promise<MatchCandidateResult[]> {
  return matchCandidatesAt("/api/personal-list/match", candidates);
}

export async function batchAddPersonalList(
  entries: BatchAddEntry[],
): Promise<BatchAddResult> {
  return fetchApi(`/api/personal-list/batch-add`, {
    method: "POST",
    body: JSON.stringify({ entries }),
  });
}

export async function removePersonalListEntry(entryId: string): Promise<void> {
  await fetchApi(`/api/personal-list/entry/${entryId}`, { method: "DELETE" });
}

export async function updateGeneralListRelationship(
  entryId: string,
  relationship: string,
): Promise<void> {
  await fetchApi(`/api/personal-list/entry/${entryId}/relationship`, {
    method: "PATCH",
    body: JSON.stringify({ relationship }),
  });
}

export async function getPersonalListSuggestions(
  limit = 20,
): Promise<SuggestedContactsResponse> {
  const sp = new URLSearchParams({ limit: String(limit) });
  return fetchApi(`/api/personal-list/suggestions?${sp}`);
}

// ── Election projection — convert the general list into a live election ─────

export interface ActivatePersonalListResult {
  walk_list_id: string;
  projected: number;
  already_present: number;
}

export async function activatePersonalListForElection(
  electionId: string,
): Promise<ActivatePersonalListResult> {
  return fetchApi(`/api/kosning/${electionId}/activate-personal-list`, {
    method: "POST",
  });
}

// Batch import

export interface BatchSearchResult {
  query_name: string;
  matches: PersonSearchItem[];
}

export async function batchSearchKosningPeople(
  names: string[],
): Promise<BatchSearchResult[]> {
  return fetchApi("/api/kosning/batch-search", {
    method: "POST",
    body: JSON.stringify({ names }),
  });
}

export interface BatchAddEntry {
  kennitala_hash: string;
  relationship: ClaimRelationship;
  phone?: string | null;
}

export interface BatchAddItemResult {
  name: string;
  /** "blocked" = INSERTED but superseded — the row IS on the list, grayed
   *  ("í góðum höndum"). added + blocked = rows that actually landed. */
  status: "added" | "skipped" | "blocked" | "not_found";
  /** ALWAYS null (D2) — the winning claimant is never named. */
  blocked_by: string | null;
  /** Echoed so the staging grid can key row-flips on identity, not position. */
  kennitala_hash?: string | null;
}

export interface BatchAddResult {
  added: number;
  skipped: number;
  blocked: number;
  results: BatchAddItemResult[];
}

export async function batchAddToPersonalList(
  listId: string,
  entries: BatchAddEntry[],
): Promise<BatchAddResult> {
  return fetchApi(`/api/kosning/personal-list/${listId}/batch-add`, {
    method: "POST",
    body: JSON.stringify({ entries }),
  });
}


// ---------------------------------------------------------------------------
// Simple uploads (no media gallery, no permissions)
// ---------------------------------------------------------------------------

export async function uploadImage(file: File): Promise<{ path: string }> {
  const formData = new FormData();
  formData.append("file", file);

  const url = `${API_BASE}/api/uploads/image`;
  const headers: Record<string, string> = {};
  if (getViewMode() === "user") {
    headers["X-View-As"] = "user";
  }

  const res = await fetch(url, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }

  return res.json();
}

/**
 * Upload a newsletter body image. Unlike {@link uploadImage} (which returns a
 * private, auth-proxied path), this returns a PERMANENT PUBLIC https URL that
 * loads in an email inbox without a session.
 */
export async function uploadNewsletterImage(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);

  const url = `${API_BASE}/api/campaigns/upload-image`;
  const headers: Record<string, string> = {};
  if (getViewMode() === "user") {
    headers["X-View-As"] = "user";
  }

  const res = await fetch(url, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }

  return res.json();
}

// ---------------------------------------------------------------------------
// Call Center
// ---------------------------------------------------------------------------

export interface CallScript {
  id: string;
  campaign_id: string;
  name: string;
  description: string | null;
  version: number;
  status: string;
  /** The campaign's Aðalhandrit: what lists without an override resolve to. */
  is_campaign_default?: boolean;
  /** Manager-panel context, populated by the campaign scripts listing only. */
  node_count?: number | null;
  attached_list_count?: number | null;
  start_node_id: string | null;
  is_template: boolean;
  template_id: string | null;
  svfnr: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScriptNode {
  id: string;
  script_id: string;
  node_type: string;
  title: string;
  prompt_text: string;
  prompt_notes: string | null;
  response_type: string | null;
  response_options: { key: string; label: string; next_node_id: string | null }[] | null;
  sort_order: number;
  is_terminal: boolean;
  disposition_on_complete: string | null;
  source_node_id: string | null;
  is_modified: boolean;
}

export interface ScriptEdge {
  id: string;
  script_id: string;
  from_node_id: string;
  to_node_id: string;
  condition_key: string | null;
  condition_value: string | null;
  label: string | null;
  sort_order: number;
}

export interface CallScriptWithGraph extends CallScript {
  nodes: ScriptNode[];
  edges: ScriptEdge[];
}

export interface CallSession {
  id: string;
  campaign_id: string;
  kennitala_hash: string;
  caller_id: string;
  script_id: string | null;
  status: string;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  disposition: string | null;
  script_responses: Record<string, unknown> | null;
  ai_summary: Record<string, unknown> | null;
  contact_log_id: string | null;
  created_at: string;
}

export interface CampaignVolunteer {
  id: string;
  campaign_id: string;
  member_id: string;
  member_name: string | null;
  role: string;
  status: string;
  joined_at: string;
  training_completed_at: string | null;
  total_calls: number;
  total_talk_minutes: number;
}

export interface JoinResult {
  campaign_id: string;
  campaign_name: string;
  volunteer_id: string;
  role: string;
  status: string;
  vetting_status: string | null;
}

// Scripts

export async function createCallScript(
  campaignId: string,
  data: { name: string; description?: string; is_template?: boolean; svfnr?: string; nodes?: Record<string, unknown>[] },
): Promise<CallScript> {
  return fetchApi(`/api/call-center/campaigns/${campaignId}/scripts`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function listCallScripts(campaignId: string): Promise<CallScript[]> {
  return fetchApi(`/api/call-center/campaigns/${campaignId}/scripts`);
}

export async function getCampaignActiveScript(
  campaignId: string,
): Promise<CallScriptWithGraph | null> {
  return fetchApi(`/api/call-center/campaigns/${campaignId}/active-script`);
}

/** Script a caller on this list should see: the list's call_script_id
 *  override when set (and not archived), else the campaign's active script. */
export async function getWalkListActiveScript(
  walkListId: string,
): Promise<CallScriptWithGraph | null> {
  return fetchApi(`/api/call-center/walk-lists/${walkListId}/active-script`);
}

export async function getCallScript(scriptId: string): Promise<CallScriptWithGraph> {
  return fetchApi(`/api/call-center/scripts/${scriptId}`);
}

export async function updateCallScript(
  scriptId: string,
  data: Record<string, unknown>,
): Promise<CallScriptWithGraph> {
  return fetchApi(`/api/call-center/scripts/${scriptId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function publishCallScript(scriptId: string): Promise<CallScript> {
  return fetchApi(`/api/call-center/scripts/${scriptId}/publish`, { method: "POST" });
}

/** Make the script the campaign's Aðalhandrit (publishes it if still a draft).
 *  The previous Aðalhandrit stays active, it just stops being the default. */
export async function makeCallScriptDefault(scriptId: string): Promise<CallScript> {
  return fetchApi(`/api/call-center/scripts/${scriptId}/make-default`, { method: "POST" });
}

export async function cloneCallScript(scriptId: string, svfnr?: string): Promise<CallScript> {
  // With svfnr: template clone for a sveitarfélag. Without: plain in-campaign
  // "Afrita" duplicate (draft named "{name} (afrit)").
  const qs = svfnr ? `?svfnr=${encodeURIComponent(svfnr)}` : "";
  return fetchApi(`/api/call-center/scripts/${scriptId}/clone${qs}`, {
    method: "POST",
  });
}

export async function archiveCallScript(scriptId: string): Promise<CallScript> {
  return fetchApi(`/api/call-center/scripts/${scriptId}/archive`, { method: "POST" });
}

export interface ScriptAttachedList {
  id: string;
  name: string;
  campaign_id: string;
  campaign_name: string;
}

/** Walk lists currently carrying this script as their override. */
export async function getScriptAttachedLists(
  scriptId: string,
): Promise<ScriptAttachedList[]> {
  return fetchApi(`/api/call-center/scripts/${scriptId}/lists`);
}

/** Bulk-manage the per-list script override: attach the first set, detach the
 *  second (only where this script is the current override). */
export async function attachScriptToLists(
  scriptId: string,
  walkListIds: string[],
  detachWalkListIds: string[] = [],
): Promise<{ attached: number; detached: number; requested: number }> {
  return fetchApi(`/api/call-center/scripts/${scriptId}/lists`, {
    method: "PUT",
    body: JSON.stringify({
      walk_list_ids: walkListIds,
      detach_walk_list_ids: detachWalkListIds,
    }),
  });
}

/** Lightweight [{id, name}] of an election's campaigns for admin pickers. */
export async function getElectionCampaignsLite(
  electionId: string,
): Promise<{ id: string; name: string }[]> {
  return fetchApi(`/api/canvassing/elections/${electionId}/campaigns-lite`);
}

export async function deleteCallScript(scriptId: string): Promise<void> {
  return fetchApi(`/api/call-center/scripts/${scriptId}`, { method: "DELETE" });
}

// Sessions
// (/sessions/start and /sessions/complete were retired in the 2026-04
// simplification — the /call page now uses POST /next-caller for both
// log-previous + acquire-next in one round-trip. ``skipCallSession`` and
// its defer counterpart stay for manual session control.)

export async function skipCallSession(
  sessionId: string,
  reason?: string,
): Promise<CallSession> {
  return fetchApi(`/api/call-center/sessions/${sessionId}/skip`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function deferCallSession(
  sessionId: string,
  deferUntil: string,
): Promise<CallSession> {
  return fetchApi(`/api/call-center/sessions/${sessionId}/defer`, {
    method: "POST",
    body: JSON.stringify({ defer_until: deferUntil }),
  });
}

// Volunteers

export async function joinCallCenterCampaign(token: string): Promise<JoinResult> {
  return fetchApi(`/api/call-center/join/${token}`, { method: "POST" });
}

export async function getMyCallCenterStatus(campaignId: string): Promise<CampaignVolunteer> {
  return fetchApi(`/api/call-center/campaigns/${campaignId}/my-status`);
}

export interface MyCallCampaign {
  campaign_id: string;
  campaign_name: string;
  campaign_status: string;
  svfnr: string | null;
  role: string | null;
  status: string | null;
  total_calls: number;
  joined_at: string | null;
}

export async function getMyCallCampaigns(electionId?: string): Promise<MyCallCampaign[]> {
  const qs = electionId ? `?election_id=${electionId}` : "";
  return fetchApi(`/api/call-center/my-campaigns${qs}`);
}

export async function listCallCenterVolunteers(campaignId: string): Promise<CampaignVolunteer[]> {
  return fetchApi(`/api/call-center/campaigns/${campaignId}/volunteers`);
}

export async function updateCallCenterVolunteer(
  volunteerId: string,
  data: { role?: string; status?: string },
): Promise<CampaignVolunteer> {
  return fetchApi(`/api/call-center/volunteers/${volunteerId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// Volunteer Search & Direct Add

export type MemberSearchResult = {
  id: string;
  /** Stable key for the add-volunteers flow — populated for members and
   *  non-members alike (the search now returns both). */
  kennitala_hash: string;
  /** False for people who are not party members (badge them in the UI). */
  is_member: boolean;
  /** True when the person is already a volunteer in this scope (election
   *  intent volunteer or campaign assignee): the dialog offers to open
   *  their drawer instead of adding them again. */
  is_volunteer?: boolean;
  name: string;
  kennitala: string | null;
  address: string | null;
  postal_code: string | null;
  municipality: string | null;
};

export async function searchMembersForCampaign(
  campaignId: string,
  q: string,
): Promise<MemberSearchResult[]> {
  const sp = new URLSearchParams({ q });
  return fetchApi(`/api/call-center/campaigns/${campaignId}/member-search?${sp}`);
}

export async function addVolunteerToCampaign(
  campaignId: string,
  data: { kennitala_hash: string; role: string },
): Promise<CampaignVolunteer> {
  return fetchApi(`/api/call-center/campaigns/${campaignId}/add-volunteer`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Supervisor

export async function getCallCenterSupervisorStats(campaignId: string): Promise<Record<string, unknown>> {
  return fetchApi(`/api/call-center/campaigns/${campaignId}/supervisor-stats`);
}

export async function listCallSessions(
  campaignId: string,
  params?: { disposition?: string; caller_id?: string; cursor?: string; limit?: number },
): Promise<{ items: CallSession[]; next_cursor: string | null; has_more: boolean }> {
  const searchParams = new URLSearchParams();
  if (params?.disposition) searchParams.set("disposition", params.disposition);
  if (params?.caller_id) searchParams.set("caller_id", params.caller_id);
  if (params?.cursor) searchParams.set("cursor", params.cursor);
  if (params?.limit) searchParams.set("limit", String(params.limit));
  const qs = searchParams.toString();
  return fetchApi(`/api/call-center/campaigns/${campaignId}/sessions${qs ? `?${qs}` : ""}`);
}

// AI Assistance

export async function generateAIBriefing(sessionId: string): Promise<{ briefing: string; talking_points: string[] }> {
  return fetchApi(`/api/call-center/sessions/${sessionId}/briefing`, { method: "POST" }, 60000);
}

export async function getAIObjectionResponse(
  sessionId: string,
  objectionText: string,
): Promise<{ response: string }> {
  return fetchApi(`/api/call-center/sessions/${sessionId}/objection`, {
    method: "POST",
    body: JSON.stringify({ objection_text: objectionText }),
  });
}

export async function summarizeCallSession(sessionId: string): Promise<Record<string, unknown>> {
  return fetchApi(`/api/call-center/sessions/${sessionId}/summarize`, { method: "POST" }, 60000);
}

export async function generateCallScript(
  campaignId: string,
  goalDescription: string,
  language: string = "is",
): Promise<{ nodes: Record<string, unknown>[]; edges: unknown[] }> {
  return fetchApi(`/api/call-center/scripts/generate?campaign_id=${campaignId}`, {
    method: "POST",
    body: JSON.stringify({ goal_description: goalDescription, language }),
  }, 60000);
}

/** Convert an uploaded .docx handrit into a branching script (AI, faithful).
 *  Same response shape as generateCallScript; the returned nodes carry minted
 *  ids with branch links already resolved. */
export async function generateCallScriptFromDocument(
  campaignId: string,
  file: File,
  notes: string = "",
): Promise<{ nodes: Record<string, unknown>[]; edges: unknown[] }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("notes", notes);

  const url = `${API_BASE}/api/call-center/scripts/generate-from-document?campaign_id=${campaignId}`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 90000); // AI conversion

  const extraHeaders: Record<string, string> = {};
  if (getViewMode() === "user") {
    extraHeaders["X-View-As"] = "user";
  }

  const doUpload = () =>
    fetch(url, {
      method: "POST",
      signal: controller.signal,
      credentials: "include",
      headers: extraHeaders,
      // Do NOT set Content-Type - browser sets it with boundary for multipart
      body: formData,
    });

  try {
    let res = await doUpload();

    // Route a 401 through the same one-shot transparent refresh as fetchApi /
    // uploadMedia: a hard redirect here would destroy the dialog, the uploaded
    // file and the typed notes on a merely-expired access token.
    if (res.status === 401) {
      const outcome = await refreshAccessToken();
      if (outcome === "ok") {
        res = await doUpload();
      } else if (outcome === "network") {
        throw new ApiError(0, "Nettenging töpuð — reyndu aftur");
      }
      // "rejected" falls through to the redirect below.
    }

    if (res.status === 401) {
      redirectToLogin();
      throw new ApiError(401, "Óheimil aðgangur");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(res.status, formatErrorDetail(body?.detail, res.status), body);
    }
    return res.json();
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (typeof err === "object" && err !== null && (err as { name?: string }).name === "AbortError") {
      throw new ApiError(0, "AI umbreyting rann út á tíma, reyndu aftur");
    }
    throw new ApiError(0, "Nettenging töpuð, reyndu aftur");
  } finally {
    clearTimeout(timeout);
  }
}

export async function generateCoachingNotes(scriptId: string): Promise<{ updated_nodes: number }> {
  return fetchApi(`/api/call-center/scripts/${scriptId}/generate-coaching`, { method: "POST" }, 60000);
}

export async function simulateCallScript(
  scriptId: string,
  numSimulations: number = 3,
): Promise<Record<string, unknown>> {
  return fetchApi(`/api/call-center/scripts/${scriptId}/simulate?num_simulations=${numSimulations}`, {
    method: "POST",
  }, 60000);
}

// ---------------------------------------------------------------------------
// War Room Analytics
// ---------------------------------------------------------------------------

export interface WarRoomVelocityPoint {
  bucket: string;
  door_count: number;
  call_count: number;
}

export interface WarRoomVelocityResponse {
  points: WarRoomVelocityPoint[];
  total_doors: number;
  total_calls: number;
}

export interface WarRoomMunicipalityPacing {
  // null for national-scope campaigns (no single municipality)
  svfnr: string | null;
  name: string | null;
  kjordaemi: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  target_count: number;
  contacted_count: number;
  // responded_count = people whose latest disposition is in the meaningful-
  // response set. Drives the kjordaemi-radial Framvinda chart.
  responded_count: number;
  contacted_pct: number;
  responded_pct: number;
  door_count: number;
  call_count: number;
  daily_target: number;
  daily_actual: number;
  contacts_per_hour: number;
  active_volunteers: number;
  traffic_light: "green" | "yellow" | "red";
  sparkline_data: number[];
}

export interface WarRoomPacingInfo {
  days_remaining: number | null;
  total_daily_target: number;
  total_daily_actual: number;
  overall: "green" | "yellow" | "red";
}

export interface WarRoomElectionPacing {
  pacing: WarRoomPacingInfo;
  municipalities: WarRoomMunicipalityPacing[];
}

export interface WarRoomMomentumDay {
  day: string;
  positive: number;
  negative: number;
  net: number;
}

export interface WarRoomMomentumResponse {
  days: WarRoomMomentumDay[];
  total_net: number;
}

export interface WarRoomSankeyFlow {
  from_disposition: string;
  to_disposition: string;
  size: number;
}

export interface WarRoomPersuasionResponse {
  flows: WarRoomSankeyFlow[];
  total_moved: number;
}

export interface WarRoomHeatmapCell {
  day_of_week: number;
  hour: number;
  contacts: number;
  positive: number;
}

export interface WarRoomHeatmapResponse {
  cells: WarRoomHeatmapCell[];
}

export interface WarRoomDurationQuartiles {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

export interface WarRoomPracticeProfile {
  empathy: number;
  active_listening: number;
  narrative_exchange: number;
  autonomy_respect: number;
  script_adherence: number;
  tone: number;
}

export interface WarRoomCanvasserStats {
  member_id: string;
  member_name: string;
  total_contacts: number;
  total_calls: number;
  total_talk_minutes: number;
  supporters_found: number;
  responded: number;
  duration_quartiles: WarRoomDurationQuartiles | null;
  current_streak: number;
  best_streak: number;
  practice_profile: WarRoomPracticeProfile | null;
  daily_trend: number[];
}

export interface WarRoomCanvasserStatsResponse {
  canvassers: WarRoomCanvasserStats[];
}

export interface WarRoomVolunteerFunnelStage {
  stage: string;
  label: string;
  count: number;
}

export interface WarRoomVolunteerFunnelResponse {
  stages: WarRoomVolunteerFunnelStage[];
}

export interface WarRoomLeaderboardEntry {
  rank: number;
  member_id: string;
  member_name: string;
  total_calls: number;
  total_talk_minutes: number;
  supporters_found: number;
  best_streak: number;
  achievement_count: number;
  campaign_names: string[];
  daily_trend: number[];
}

export interface WarRoomLeaderboardResponse {
  entries: WarRoomLeaderboardEntry[];
}

export interface WarRoomAlertItem {
  alert_type: string;
  severity: "green" | "yellow" | "red";
  title: string;
  description: string;
  svfnr: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
}

export interface WarRoomAlertsResponse {
  alerts: WarRoomAlertItem[];
}

export interface WarRoomGOTVStats {
  total_supporters: number;
  reached_today: number;
  outstanding: number;
  reach_pct: number;
  active_callers: number;
  hourly_velocity: WarRoomVelocityPoint[];
}

// Election-level endpoints

export async function getWarRoomVelocity(electionId: string, hours = 24): Promise<WarRoomVelocityResponse> {
  return fetchApi(`/api/war-room/elections/${electionId}/velocity?hours=${hours}`);
}

export async function getWarRoomPacing(electionId: string): Promise<WarRoomElectionPacing> {
  return fetchApi(`/api/war-room/elections/${electionId}/pacing`);
}

export async function getWarRoomMomentum(electionId: string, days = 14): Promise<WarRoomMomentumResponse> {
  return fetchApi(`/api/war-room/elections/${electionId}/momentum?days=${days}`);
}

export async function getWarRoomLeaderboard(electionId: string): Promise<WarRoomLeaderboardResponse> {
  return fetchApi(`/api/war-room/elections/${electionId}/leaderboard`);
}

export async function getWarRoomVolunteerFunnel(electionId: string): Promise<WarRoomVolunteerFunnelResponse> {
  return fetchApi(`/api/war-room/elections/${electionId}/volunteer-funnel`);
}

export async function getWarRoomAlerts(electionId: string): Promise<WarRoomAlertsResponse> {
  return fetchApi(`/api/war-room/elections/${electionId}/alerts`);
}

export async function getWarRoomGOTV(electionId: string): Promise<WarRoomGOTVStats> {
  return fetchApi(`/api/war-room/elections/${electionId}/gotv`);
}

export async function generateGOTVChaseList(electionId: string): Promise<{ walk_list_id: string }> {
  return fetchApi(`/api/war-room/elections/${electionId}/gotv/chase-list`, { method: "POST" });
}

// Campaign-level endpoints

export async function getWarRoomCampaignVelocity(campaignId: string, electionId: string, hours = 24): Promise<WarRoomVelocityResponse> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/velocity?election_id=${electionId}&hours=${hours}`);
}

export async function getWarRoomCampaignPacing(campaignId: string, electionId: string): Promise<WarRoomMunicipalityPacing> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/pacing?election_id=${electionId}`);
}

export async function getWarRoomCampaignPersuasion(campaignId: string, electionId: string): Promise<WarRoomPersuasionResponse> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/persuasion?election_id=${electionId}`);
}

export async function getWarRoomCampaignHeatmap(campaignId: string, electionId: string): Promise<WarRoomHeatmapResponse> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/heatmap?election_id=${electionId}`);
}

export async function getWarRoomCampaignCanvasserStats(campaignId: string, electionId: string): Promise<WarRoomCanvasserStatsResponse> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/canvasser-stats?election_id=${electionId}`);
}

// ── Post-election reports ──
// Frozen, PII-free aggregate snapshots captured before Kosningalög deletion of
// the voter data. See backend post_election_report_service.

export interface PostElectionReportSummary {
  svfnr: string | null; // null = the election-wide rollup
  municipality_name: string | null;
  generated_at: string | null;
  pdf_url: string | null;
}

export interface PostElectionReportListResponse {
  items: PostElectionReportSummary[];
}

export interface PostElectionMunicipalitySnapshot {
  schema_version: number;
  frozen_at: string;
  election_id: string;
  svfnr: string;
  privacy: { voter_pii_free: boolean; contains_volunteer_identities: boolean; min_cell: number; note: string };
  sections: {
    context: {
      svfnr: string;
      municipality_name: string;
      population: number | null;
      area_km2: number | null;
      kjordaemi: string | null;
      municipality_type: string | null;
      election: { name: string | null; type: string | null; election_day: string | null; status: string | null };
      campaigns: { name: string; type: string; status: string; goal_contacts: number; actual_contacts: number; has_manager_message: boolean; has_manager_image: boolean }[];
    };
    electorate: {
      available: boolean;
      eligible_voters: number;
      members_in_electorate?: number;
      member_penetration_pct?: number | null;
      gender?: Record<string, number | null>;
      citizenship?: Record<string, number | null>;
      voting_districts?: Record<string, number | null>;
      polling_places?: Record<string, number | null>;
      voting_district_count?: number;
      polling_place_count?: number;
    };
    historical_turnout: { available: boolean; source?: string; by_year: { year: number; eligible_voters: number | null; votes_cast: number | null; turnout_pct: number | null }[] };
    outreach: {
      voter_pool: number;
      total_contacts: number;
      unique_people_contacted: number;
      coverage_pct: number | null;
      by_contact_type: Record<string, number | null>;
      disposition_breakdown: Record<string, number | null>;
      responded: number;
      support: number;
      support_rate_pct: number | null;
      member_reached: number;
      touch_distribution: { one: number | null; two: number | null; three_plus: number | null };
    };
    ground_game: {
      walk_lists_total: number;
      walk_lists_completed: number;
      walk_list_completion_pct: number | null;
      km_walked: number;
      route_hours: number;
      canvassing_sessions: number;
      field_hours: number;
      session_contacts_logged: number;
      personal_lists: number;
      personal_list_participants: number;
      claim_relations: Record<string, number | null>;
    };
    volunteers: {
      signed_up: number;
      trained: number;
      active: number;
      by_category: Record<string, number>;
      total_calls: number;
      total_talk_hours: number;
      contacts_distribution: {
        min: number;
        q1: number | null;
        median: number | null;
        q3: number | null;
        mean: number | null;
        max: number;
      } | null;
      daily_active?: { day: string; active: number }[];
      leaderboard: {
        rank: number;
        name: string;
        calls: number;
        talk_minutes: number;
        contacts: number;
        supporters: number;
        conversion_pct: number | null;
        avg_priority: number | null;
        active: boolean;
        trained: boolean;
      }[];
    };
    timeseries: {
      daily: { day: string; total: number; door: number; phone: number; sms: number; responded: number; supporters: number }[];
      by_weekday: { weekday: number; total: number; positive_rate_pct: number | null }[];
      by_hour: { hour: number; total: number; positive_rate_pct: number | null }[];
      weekday_hour?: { weekday: number; hour: number; total: number; positive_rate_pct: number | null }[];
      channel_by_hour?: { channel: string; hour: number; total: number; positive_rate_pct: number | null }[];
    };
    lists: {
      summary_by_type: { list_type: string; lists: number; total: number; responded: number; support: number; response_rate_pct: number | null; support_rate_pct: number | null }[];
      lists: { name: string; list_type: string; total: number; responded: number; support: number; response_rate_pct: number | null; support_rate_pct: number | null }[];
      truncated: boolean;
    };
    effectiveness: {
      by_channel: { channel: string; contacts: number; support_rate_pct: number | null }[];
      touch_lift: { single_support_rate_pct: number | null; multi_support_rate_pct: number | null };
      member_support_rate_pct: number | null;
      non_member_support_rate_pct: number | null;
    };
    what_worked: { key: string; title: string; detail: string; sentiment: string }[];
    // v3 — optional so older v2 snapshots still render.
    targeting?: {
      available: boolean;
      has_score_bands: boolean;
      by_score_band: { band: number; pool: number; coverage_pct: number | null; support_rate_pct: number | null }[];
      by_priority?: { priority: number; pool: number; untouched: number; response_rate_pct: number | null }[];
      by_segment: Record<string, { value: string; responded: number | null; support_rate_pct: number | null }[]>;
      untapped_high_score: number;
    };
    saturation?: {
      by_day: { day: string; new_people: number; cumulative: number }[];
    };
    peer_comparison?: {
      available: boolean;
      cohort_label?: string;
      municipality_count?: number;
      coverage?: { this: number; median: number; rank: number; count: number } | null;
      support_rate?: { this: number; median: number; rank: number; count: number } | null;
      cohort_coverage?: { cohort: string; avg_coverage_pct: number; count: number }[];
    };
    issues: {
      top_topics: { tag_key: string; label: string; count: number | null }[];
      action_responses: { needs_ride: number | null; volunteer_interest: number | null; candidate_callback: number | null };
    };
    gotv: {
      supporters_identified: number;
      reached_on_election_day: number;
      reach_pct: number | null;
      rides_requested: number | null;
      rides_delivered: number | null;
    };
    results: {
      available: boolean;
      source?: string;
      election_year?: number | null;
      current?: { year: number; votes: number | null; vote_share: number | null; seats: number | null } | null;
      previous?: { year: number; votes: number | null; vote_share: number | null; seats: number | null } | null;
      change_pts?: number | null;
      xd_by_year: { year: number; votes: number | null; vote_share: number | null; seats: number | null }[];
      ballot_totals_by_year: { year: number; total_votes: number | null; absentee_votes: number | null }[];
    };
    system_highlights: {
      people_reached: number;
      doors_knocked: number;
      km_walked: number;
      volunteer_hours: number;
      active_volunteers: number;
      contacts_per_volunteer_hour: number | null;
      coverage_pct: number | null;
    };
  };
}

export interface PostElectionRollup {
  schema_version: number;
  frozen_at: string;
  election_id: string;
  svfnr: null;
  is_rollup: true;
  municipality_count: number;
  totals: {
    eligible_voters: number;
    total_contacts: number;
    people_reached: number;
    support: number;
    doors_knocked: number;
    km_walked: number;
    volunteer_hours: number;
    active_volunteers: number;
    supporters_identified: number;
    reached_on_election_day: number;
    coverage_pct: number | null;
  };
  per_municipality: {
    svfnr: string | null;
    municipality_name: string | null;
    eligible_voters: number | null;
    people_reached: number | null;
    coverage_pct: number | null;
    support: number | null;
    support_rate_pct: number | null;
    volunteer_hours: number | null;
    active_volunteers: number | null;
  }[];
}

export interface PostElectionMunicipalityDetail {
  data_snapshot: PostElectionMunicipalitySnapshot | null;
  content_markdown: string;
  pdf_url: string | null;
  generated_at: string | null;
}

export interface PostElectionRollupDetail {
  data_snapshot: PostElectionRollup | null;
  content_markdown: string;
  pdf_url: string | null;
  generated_at: string | null;
}

export async function getPostElectionReports(electionId: string): Promise<PostElectionReportListResponse> {
  return fetchApi(`/api/war-room/elections/${electionId}/post-election/reports`);
}

export async function getPostElectionReport(electionId: string, svfnr: string): Promise<PostElectionMunicipalityDetail> {
  return fetchApi(`/api/war-room/elections/${electionId}/post-election/reports/${encodeURIComponent(svfnr)}`);
}

export async function getPostElectionRollup(electionId: string): Promise<PostElectionRollupDetail> {
  return fetchApi(`/api/war-room/elections/${electionId}/post-election/rollup`);
}

export interface PostElectionGenerateResult {
  election_id: string;
  svfnr: string;
  report_url: string;
}

// Freeze this campaign's municipality snapshot on demand; returns the report URL.
// Aggregates only (fast) — the AI narrative is fetched separately by the report
// page. Still allow a generous timeout for a large municipality's build.
export async function generateCampaignPostElectionReport(campaignId: string): Promise<PostElectionGenerateResult> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/post-election/generate`, { method: "POST" }, 120000);
}

export interface PostElectionFreezeStatus {
  total: number;
  done: number;
  remaining: number;
  frozen_now: number;
}

// Freeze up to `limit` municipalities' snapshots that aren't current yet, and
// report progress. Call repeatedly until `remaining === 0` before bundling the
// PDFs — chunked so each request stays well under the function timeout.
export async function freezeAllPostElectionReports(
  electionId: string,
  limit = 8,
): Promise<PostElectionFreezeStatus> {
  return fetchApi(
    `/api/war-room/elections/${electionId}/post-election/freeze-all?limit=${limit}`,
    { method: "POST" },
    120000,
  );
}

// Drop every frozen snapshot so the next freeze pass rebuilds from current
// data — backs the "Endurgera" (regenerate) action.
export async function resetPostElectionReports(electionId: string): Promise<{ deleted: number }> {
  return fetchApi(`/api/war-room/elections/${electionId}/post-election/reset`, { method: "POST" });
}

// Download every frozen municipality report as one .zip. This hits the FRONTEND
// Node route (headless-Chrome render of all municipalities), NOT the API_BASE
// backend — so fetch it relative to the app origin with the httpOnly xd_token
// cookie. Caller should run freezeAllPostElectionReports to completion first.
export async function downloadAllPostElectionReportsZip(
  electionId: string,
): Promise<{ filename: string; blob: Blob }> {
  const res = await fetch(`/api/kosning/skyrslur/${electionId}/zip`, { credentials: "include" });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, body || `Villa ${res.status}`, {});
  }
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `kosningaskyrslur-${electionId}.zip`;
  const blob = await res.blob();
  return { filename, blob };
}

export interface PostElectionNarrativeResult {
  content_markdown: string;
}

// Lazily generate (or fetch the cached) AI narrative for a municipality report.
// The LLM call can take ~60s, so this is its own request with a long timeout;
// the report page shows a loading state for just this section.
export async function generatePostElectionNarrative(
  electionId: string,
  svfnr: string,
): Promise<PostElectionNarrativeResult> {
  return fetchApi(
    `/api/war-room/elections/${electionId}/post-election/reports/${encodeURIComponent(svfnr)}/narrative`,
    { method: "POST" },
    180000,
  );
}

// ── Election Volunteer Vetting ──

export interface ElectionVolunteerDetail {
  /** null ONLY for rows synthesized by `eligible_only` (eligibility candidates
   *  with no election_volunteers row). Key rows on `kennitala_hash`, not `id`. */
  id: string | null;
  /** Machine-readable marker for those synthesized rows (id is null iff true).
   *  Roster surfaces must skip these; buildVolunteerRows enforces it. */
  synthesized?: boolean;
  election_id: string;
  member_id: string;
  member_name: string;
  member_phone?: string | null;
  profile_image_url?: string | null;
  /** Today's Vaktin standing: "arrived" | "coming" | null. */
  attendance_status?: string | null;
  /** Full kennitala (decrypted), null if missing or decrypt failed. */
  kennitala?: string | null;
  /**
   * Stable selection key for the Úthluta dialog. Always populated for
   * volunteer rows; matches `AssignablePerson.kennitala_hash` for sensitive.people
   * search results so the dialog can dedupe across both sources.
   */
  kennitala_hash?: string | null;
  /** Raw gender value, typically "male" / "female". */
  gender?: string | null;
  /** Birth year for age-cohort filtering in the assignment dialog. */
  birth_year?: number | null;
  /**
   * True when this volunteer is already assigned to the walk-list passed
   * via the `walk_list_id` query param on `getElectionVolunteers`.
   */
  is_assigned?: boolean;
  /** Count of this volunteer's active (non-archived) walk-list assignments in
   *  the election. 0 → "no list yet". */
  assigned_list_count?: number;
  /** The same assignments by name, sorted — one entry per list, so two lists
   *  sharing a name stay two entries and the length matches the count. Fills
   *  the roster's "Listar" column. */
  assigned_list_names?: string[];
  category: string;
  svfnr?: string | null;
  /**
   * The volunteer's registered home-address svfnr from `sensitive.people`.
   * Distinct from `svfnr` above (which is the campaign scope copied onto the
   * row when assigned). Used by the Úthluta dialog's muni-only filter to
   * narrow the grid to volunteers who live in the campaign's municipality.
   */
  home_municipality_code?: string | null;
  /** Human-readable name for `home_municipality_code` (resolved server-side). */
  member_municipality?: string | null;
  /** Registered home postal code, rendered as the "105 · Reykjavík" location chip. */
  postal_code?: string | null;
  /** The owning mannauðsstjóri (universal, explicit-only assignment). */
  owner_id?: string | null;
  owner_name?: string | null;
  scope: string;
  /** Availability windows ("Laus tími") from the universal profile — shared
   *  across the person's rows. Editable in the drawer at parity with categories. */
  availability?: string[];
  vetting_status?: string | null;
  vetted_by_name?: string | null;
  vetted_at?: string | null;
  vetting_notes?: string | null;
  signed_up_at: string;
  /** Durable registry signup date (volunteer_profiles.created_at) — the roster
   *  "Skráð" date. signed_up_at is only the per-election intent-row creation time. */
  registered_at?: string | null;
  campaign_id?: string | null;
  campaign_name?: string | null;
  role?: string | null;
  status?: string | null;
  /** Provenance for auto-created rows: "list_assignment" (enrolled when a
   *  manager assigned them a walk list) or "activity" (backfilled from
   *  historical work). null = explicit signup / manager add. */
  enrolled_via?: string | null;
  total_calls: number;
  total_talk_minutes: number;
  training_enabled_at?: string | null;
  training_completed_at?: string | null;
  total_contacts: number;
  supporters_found: number;
  conversion_pct: number;
  worked_today?: boolean;
  streak?: number;
}

export interface VolunteerMomentum {
  today_total: number;
  today_calls: number;
  today_doors: number;
  active_today: number;
  to_date: number;
  goal: number;
}

/** Today's team momentum (contacts/calls/doors, active volunteers, progress). */
export async function getVolunteerMomentum(electionId: string): Promise<VolunteerMomentum> {
  return fetchApi(`/api/elections/${electionId}/volunteers/momentum`);
}

export interface VolunteerLiveStatus {
  member_id: string;
  /** Display name, so the "Unnu í gær" report can render workers with no roster row. */
  member_name: string | null;
  /** Person key for Boða targeting when the member is not on the roster. */
  kennitala_hash: string | null;
  worked_today: boolean;
  /** Contact logs this volunteer has recorded today (0 when only other signals exist). */
  calls_today: number;
  /** Of calls_today, how many got a real answer (callback_yes / callback_no / already_voted). */
  reached_today: number;
  /** Logged work yesterday — feeds the "Unnu í gær" column. */
  worked_yesterday: boolean;
  /** Contact logs this volunteer recorded yesterday. */
  calls_yesterday: number;
  /** 'coming' | 'arrived' | 'cant' | null */
  attendance_status: string | null;
  /** First open of TODAY's still-unanswered callout link (ISO), else null. */
  opened_at: string | null;
  /** Said no for the day: declined today's callout or ring "Kemst ekki í dag". */
  cant_today: boolean;
}

/** Fast-changing Vaktin signals (worked_today + attendance) for live polling. */
export async function getVolunteerLiveStatus(electionId: string): Promise<VolunteerLiveStatus[]> {
  return fetchApi(`/api/elections/${electionId}/volunteers/live-status`);
}

/** Mark a volunteer's attendance for today on the Vaktin board ('cleared' =
 *  explicitly benched, overriding derived signals; null deletes the row). */
export async function setVolunteerAttendance(
  electionId: string,
  memberId: string,
  status: "coming" | "arrived" | "cant" | "cleared" | null,
): Promise<{ status: string | null }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/${memberId}/attendance`, {
    method: "POST",
    body: JSON.stringify({ status }),
  });
}

/** Change which categories a volunteer signed up for (writes through the profile). */
export async function setVolunteerCategories(
  electionId: string,
  memberId: string,
  categories: string[],
): Promise<{ categories: string[] }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/${memberId}/categories`, {
    method: "POST",
    body: JSON.stringify({ categories }),
  });
}

/** Change a volunteer's availability windows ("Laus tími") (writes through the profile). */
export async function setVolunteerAvailability(
  electionId: string,
  memberId: string,
  availability: string[],
): Promise<{ availability: string[] }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/${memberId}/availability`, {
    method: "POST",
    body: JSON.stringify({ availability }),
  });
}

/** Pipeline stage counts for the dashboard summary card. Mirrors the
 *  workspace's client-side bucketing (buildVolunteerRows + stageOf) so the
 *  card can keep its 60s live polling without pulling the full roster. */
export interface VolunteerPipelineSummary {
  total: number;
  new: number;
  contacted: number;
  approved: number;
  ready: number;
  rejected: number;
}

export async function getElectionVolunteersSummary(electionId: string): Promise<VolunteerPipelineSummary> {
  return fetchApi(`/api/elections/${electionId}/volunteers/summary`);
}

export async function getElectionVolunteers(
  electionId: string,
  params?: {
    vetting_status?: string;
    category?: string;
    walk_list_id?: string;
    campaign_id?: string;
    /** Narrow to vetting-approved volunteers + proven callers (non-personal
     *  lists, this election) + current assignees of `walk_list_id`. */
    eligible_only?: boolean;
  }
): Promise<ElectionVolunteerDetail[]> {
  const qs = new URLSearchParams();
  if (params?.vetting_status) qs.set("vetting_status", params.vetting_status);
  if (params?.category) qs.set("category", params.category);
  if (params?.walk_list_id) qs.set("walk_list_id", params.walk_list_id);
  if (params?.campaign_id) qs.set("campaign_id", params.campaign_id);
  if (params?.eligible_only) qs.set("eligible_only", "true");
  const suffix = qs.toString() ? `?${qs}` : "";
  return fetchApi(`/api/elections/${electionId}/volunteers${suffix}`);
}

/**
 * A person from `sensitive.people` who can be added to a walk-list via the
 * Úthluta dialog's name/kennitala search. The fields here mirror the
 * backend's hardcoded projection — adding new columns to `sensitive.people`
 * does not automatically expose them through this endpoint.
 *
 * `ineligible_reason` is only populated on exact-kennitala lookups when the
 * row fails an assignment filter — lets the UI show a distinct "person
 * found but cannot be added" state.
 */
export interface AssignablePerson {
  kennitala_hash: string;
  name: string;
  birth_year: number | null;
  is_assigned: boolean;
  ineligible_reason?: "banned" | "underage" | "implausible_age" | "unknown" | null;
  postal_code?: string | null;
  municipality_name?: string | null;
  age?: number | null;
}

/**
 * Search sensitive.people for assignment candidates. The backend hardcodes
 * the projection to four fields and filters out minors, bannmerki rows, and
 * implausibly old records.
 *
 * ``walkListId`` is optional — omit it when searching before the list
 * exists (e.g. the "create personal list for a person" flow). In that
 * case ``is_assigned`` is always ``false``.
 */
export async function searchPeopleForAssignment(
  electionId: string,
  q: string,
  walkListId?: string,
  limit = 15,
): Promise<AssignablePerson[]> {
  const sp = new URLSearchParams({ q, limit: String(limit) });
  if (walkListId) sp.set("walk_list_id", walkListId);
  return fetchApi(`/api/elections/${electionId}/volunteers/people-search?${sp}`);
}

/**
 * Manager action: create a personal walk list for any eligible person in
 * ``sensitive.people``. Idempotent — returns the existing list if one
 * already exists for this ``(campaign_id, kennitala_hash)``.
 */
export async function createPersonalListForPerson(
  campaignId: string,
  kennitalaHash: string,
): Promise<WalkList> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/personal-list-for-person`, {
    method: "POST",
    body: JSON.stringify({ kennitala_hash: kennitalaHash }),
  });
}

export async function vetVolunteer(
  electionId: string,
  volunteerId: string,
  data: { status: string; notes?: string },
  campaignId?: string,
): Promise<ElectionVolunteerDetail> {
  const qs = campaignId ? `?campaign_id=${campaignId}` : "";
  return fetchApi(`/api/elections/${electionId}/volunteers/${volunteerId}/vet${qs}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function assignVolunteer(
  electionId: string,
  volunteerId: string,
  data: { campaign_id: string; role?: string }
): Promise<ElectionVolunteerDetail> {
  return fetchApi(`/api/elections/${electionId}/volunteers/${volunteerId}/assign`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function setVolunteerTraining(
  electionId: string,
  volunteerId: string,
  data: { enable?: boolean; complete?: boolean },
  campaignId?: string,
): Promise<ElectionVolunteerDetail> {
  const qs = campaignId ? `?campaign_id=${campaignId}` : "";
  return fetchApi(`/api/elections/${electionId}/volunteers/${volunteerId}/training${qs}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ── Volunteer attendance callouts (reminder SMS at a campaign roster) ──

export interface VolunteerCalloutSummary {
  id: string;
  body: string;
  work_at: string | null;
  /** Whether the SMS carried the /maeting confirm link. Drives the mæting tally:
   *  a link-less info blast shows no confirm/decline counts. */
  include_confirm_link: boolean;
  total_sent: number;
  /** How many people it was actually sent to (response rows) — accurate even if delivery failed. */
  recipient_count: number;
  sent_at: string | null;
  created_at: string;
  confirmed: number;
  declined: number;
  no_response: number;
  /** How many recipients opened the link (real opens, not SMS prefetch). */
  opened: number;
}

/**
 * Manager action: fire a one-way reminder SMS at volunteers, each getting a
 * single-use link to the public /maeting page to confirm. Sending happens in a
 * background worker.
 *
 * ``campaign_id`` set ⇒ that campaign's active roster. Omitted ⇒ an
 * election-wide broadcast whose recipients are the segment described by the
 * remaining filters (national managers only).
 */
export interface VolunteerCalloutInput {
  campaign_id?: string | null;
  body: string;
  work_at?: string | null;
  /** Include the /maeting confirm link ("Hlekkur með mætingaskráningu"). Omit or
   *  true ⇒ recipients get a link to answer "Ég mæti"; false ⇒ a plain blast. */
  include_confirm_link?: boolean;
  categories?: string[] | null;
  vetting_statuses?: string[] | null;
  trained_only?: boolean | null;
  svfnr?: string[] | null;
  /** Explicit recipient selection (from the roster's "Senda skilaboð"),
   *  keyed by kennitala_hash so non-member volunteers are addressable too. */
  kennitala_hashes?: string[] | null;
  /** Saved segment ("Hópur") targeting: resolved fresh server-side at create
   *  time; provenance stored in the audience snapshot. */
  segment_id?: string | null;
}

export async function createVolunteerCallout(
  electionId: string,
  data: VolunteerCalloutInput,
): Promise<{ id: string; status: string }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/callouts`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/**
 * History of reminder callouts, with response tallies. Pass ``campaignId`` for
 * one campaign's reminders; omit it for the election's broadcasts.
 */
export async function getVolunteerCallouts(
  electionId: string,
  campaignId?: string,
): Promise<VolunteerCalloutSummary[]> {
  const qs = campaignId ? `?campaign_id=${campaignId}` : "";
  return fetchApi(`/api/elections/${electionId}/volunteers/callouts${qs}`);
}

export interface VolunteerCalloutRecipient {
  member_id: string;
  member_name: string;
  profile_image_url: string | null;
  municipality?: string | null;
  postal_code?: string | null;
  /** "sent" (no response) | "confirmed" | "declined". */
  status: string;
  responded_at: string | null;
  opened_at: string | null;
}

/** Who a given message was sent to, with each person's response status. */
export async function getVolunteerCalloutRecipients(
  electionId: string,
  calloutId: string,
): Promise<VolunteerCalloutRecipient[]> {
  return fetchApi(`/api/elections/${electionId}/volunteers/callouts/${calloutId}/recipients`);
}

// ── Móttökusímtöl (welcome-call flow) ─────────────────────────────────

export interface OutreachCard {
  kennitala_hash: string;
  name: string;
  birth_year: number | null;
  is_member: boolean;
  municipality: string | null;
  postal_code: string | null;
  phone: string | null;
  categories: string[];
  availability: string[];
  profile_notes: string | null;
  owner_id: string | null;
  owner_name: string | null;
  signed_up_at: string | null;
  hours_since_signup: number | null;
  attempts: number;
  /** "reached" | "no_answer" | "callback" | "declined" | "wrong_number" | null */
  last_outcome: string | null;
  /** Queue tier: 0 = due callback, 1 = new signup, 2 = recycle,
   *  3 = re-signup re-engage / segment re-ring. */
  bucket: number;
  claim_ttl_minutes: number;
}

export interface OutreachStats {
  callbacks_due: number;
  fresh: number;
  recycle_due: number;
  /** Talked-to before but pending again (re-signups): callable, served last. */
  re_engage: number;
  overdue: number;
  cooling: number;
  snoozed: number;
  no_phone: number;
  pool_total: number;
  callable_now: number;
}

export async function getOutreachStats(electionId: string): Promise<OutreachStats> {
  return fetchApi(`/api/elections/${electionId}/volunteers/outreach/stats`);
}

export async function claimNextOutreachCall(
  electionId: string,
  data: { segment_id?: string | null; skip?: string[] },
): Promise<{ done: boolean; card?: OutreachCard; stats?: OutreachStats }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/outreach/claim-next`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface OutreachLogInput {
  kennitala_hash: string;
  outcome: string;
  notes?: string | null;
  callback_at?: string | null;
  approve?: boolean;
  /** Explicit adoption only: send the caller's own id when "Ég tek þennan" is ticked. */
  owner_id?: string | null;
  categories?: string[] | null;
  availability?: string[] | null;
  /** SIP Call-ID when the call was dialed via the web softphone (server merges
   *  the box's staged CDR duration onto the outreach log). tel: calls omit. */
  call_id?: string | null;
  telephony_source?: string | null;
}

export async function logOutreachCall(
  electionId: string,
  data: OutreachLogInput,
): Promise<{ outcome: string; attempt_no: number }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/outreach/log`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function releaseOutreachClaim(electionId: string, kennitalaHash: string): Promise<void> {
  return fetchApi(`/api/elections/${electionId}/volunteers/outreach/release`, {
    method: "POST",
    body: JSON.stringify({ kennitala_hash: kennitalaHash }),
  });
}

// ── Hópar (saved volunteer segments) ──────────────────────────────────

export interface VolunteerSegment {
  id: string;
  name: string;
  description: string | null;
  filter_json: QueryGroup;
  count?: number;
  created_at: string | null;
  updated_at: string | null;
}

export async function getVolunteerSegments(electionId: string): Promise<VolunteerSegment[]> {
  return fetchApi(`/api/elections/${electionId}/volunteers/segments`);
}

export async function getVolunteerSegmentFields(electionId: string): Promise<FieldDefinition[]> {
  return fetchApi(`/api/elections/${electionId}/volunteers/segments/filter-fields`);
}

export async function previewVolunteerSegmentCount(
  electionId: string,
  filterJson: QueryGroup,
): Promise<{ count: number }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/segments/preview-count`, {
    method: "POST",
    body: JSON.stringify({ filter_json: filterJson }),
  });
}

export async function createVolunteerSegment(
  electionId: string,
  data: { name: string; description?: string | null; filter_json: QueryGroup },
): Promise<VolunteerSegment> {
  return fetchApi(`/api/elections/${electionId}/volunteers/segments`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateVolunteerSegment(
  electionId: string,
  segmentId: string,
  data: { name?: string; description?: string | null; filter_json?: QueryGroup },
): Promise<VolunteerSegment> {
  return fetchApi(`/api/elections/${electionId}/volunteers/segments/${segmentId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function archiveVolunteerSegment(electionId: string, segmentId: string): Promise<void> {
  return fetchApi(`/api/elections/${electionId}/volunteers/segments/${segmentId}`, { method: "DELETE" });
}

export async function getVolunteerSegmentMembers(
  electionId: string,
  segmentId: string,
): Promise<{ kennitala_hashes: string[]; count: number }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/segments/${segmentId}/members`);
}

// ── Insights (attention strip + funnel + goals + leaderboard) ─────────

export interface AttentionBucket {
  count: number;
  kennitala_hashes: string[];
}

/** The five weekly Vikumarkmið metrics. */
export type GoalMetric = "new_signups" | "welcome_calls" | "reached" | "activated" | "worked";

export interface GoalMetricEntry {
  metric: GoalMetric;
  value: number;
  target: number | null;
  /** True when the target is carried forward from an earlier week (or legacy). */
  inherited?: boolean;
}

export interface VolunteerGoals {
  /** Monday of the current ISO week. */
  week_start: string;
  days_left: number;
  metrics: GoalMetricEntry[];
  /** Previous weeks, oldest first. */
  history: { week_start: string; metrics: GoalMetricEntry[] }[];
}

export interface VolunteerInsights {
  attention: {
    overdue_first_touch: AttentionBucket;
    callbacks_due: AttentionBucket;
    unreachable: AttentionBucket;
    approved_never_worked: AttentionBucket;
    cooling: AttentionBucket;
    unowned: AttentionBucket;
    no_phone: AttentionBucket;
  };
  funnel: {
    signed: number;
    contacted: number;
    approved: number;
    worked_once: number;
    worked_repeat: number;
    rejected: number;
  };
  time_to_first_touch: {
    median_hours: number | null;
    buckets: { lt24: number; h24_48: number; gt48: number };
  };
  goals: VolunteerGoals;
  first_work_today: { kennitala_hash: string; name: string; member_id: string | null; owner_id: string | null }[];
  leaderboard: {
    member_id: string;
    name: string;
    calls_week: number;
    reached_week: number;
    owned: number;
    owned_active: number;
  }[];
  cooldown_hours: number;
}

export async function getVolunteerInsights(electionId: string): Promise<VolunteerInsights> {
  return fetchApi(`/api/elections/${electionId}/volunteers/insights`);
}

/** Set the current week's Vikumarkmið per metric (null clears a metric). */
export async function setVolunteerWeeklyGoals(
  electionId: string,
  targets: Partial<Record<GoalMetric, number | null>>,
): Promise<{ ok: boolean }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/insights/goals`, {
    method: "PUT",
    body: JSON.stringify({ targets }),
  });
}

// ── Dagskrá dagsins (day plans behind the /maeting pull) ─────────────

export interface VolunteerDayPlan {
  day: string;
  lunch: string | null;
  dinner: string | null;
  schedule: DayPlanScheduleItem[];
  notes: string | null;
  updated_at: string | null;
}

export async function getVolunteerDayPlans(
  electionId: string,
  from: string,
  days = 7,
): Promise<VolunteerDayPlan[]> {
  return fetchApi(`/api/elections/${electionId}/volunteers/day-plans?from=${from}&days=${days}`);
}

export async function upsertVolunteerDayPlan(
  electionId: string,
  data: { day: string; lunch?: string | null; dinner?: string | null; schedule?: DayPlanScheduleItem[]; notes?: string | null },
): Promise<VolunteerDayPlan> {
  return fetchApi(`/api/elections/${electionId}/volunteers/day-plans`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteVolunteerDayPlan(electionId: string, day: string): Promise<void> {
  return fetchApi(`/api/elections/${electionId}/volunteers/day-plans/${day}`, { method: "DELETE" });
}

// ── Ownership (Umsjónarmaður) ─────────────────────────────────────────

export interface VolunteerOwnerOption {
  member_id: string;
  name: string;
  profile_image_url: string | null;
  /** How many of this election's volunteers they currently own. */
  owned: number;
  /** False for stale owners without a manage grant: display-only, POST /owner
   *  would reject them, so the picker must not offer them as a target. */
  eligible: boolean;
}

/** Eligible owners for the Umsjónarmaður picker (managers + current owners). */
export async function getVolunteerOwners(electionId: string): Promise<VolunteerOwnerOption[]> {
  return fetchApi(`/api/elections/${electionId}/volunteers/owners`);
}

/** Assign (owner_id) or clear (owner_id: null) the owning mannauðsstjóri on a batch. */
export async function setVolunteerOwner(
  electionId: string,
  data: { kennitala_hashes: string[]; owner_id: string | null },
): Promise<{ updated: number; skipped: number }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/owner`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ── Bulk manager actions ──────────────────────────────────────────────

function bulkQs(campaignId?: string): string {
  return campaignId ? `?campaign_id=${campaignId}` : "";
}

/** Set vetting status on many volunteer rows at once. Returns rows updated. */
export async function bulkVetVolunteers(
  electionId: string,
  data: { volunteer_ids: string[]; status: string; notes?: string | null },
  campaignId?: string,
): Promise<{ updated: number }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/bulk-vet${bulkQs(campaignId)}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Enable/complete training on many volunteer rows at once. */
export async function bulkSetVolunteerTraining(
  electionId: string,
  data: { volunteer_ids: string[]; enable?: boolean; complete?: boolean },
  campaignId?: string,
): Promise<{ updated: number }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/bulk-training${bulkQs(campaignId)}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Delete many volunteer rows at once. Walk-list assignments are untouched. */
export async function bulkRemoveVolunteers(
  electionId: string,
  volunteerIds: string[],
  campaignId?: string,
): Promise<{ removed: number }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/bulk-remove${bulkQs(campaignId)}`, {
    method: "POST",
    body: JSON.stringify({ volunteer_ids: volunteerIds }),
  });
}

/** Assign many approved intent rows to one campaign. */
export async function bulkAssignVolunteers(
  electionId: string,
  data: { volunteer_ids: string[]; campaign_id: string; role?: string },
): Promise<{ assigned: number }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/bulk-assign`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Add many people (members or non-members) as volunteers at once, keyed by
 *  kennitala_hash so non-members with no member row can be added too. */
export async function bulkAddVolunteers(
  electionId: string,
  data: { kennitala_hashes: string[]; categories?: string[]; campaign_id?: string },
): Promise<{ added: number }> {
  return fetchApi(`/api/elections/${electionId}/volunteers/bulk-add`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ── Per-member workspace drawer timeline ──────────────────────────────

export interface VolunteerTimelineEvent {
  kind: "callout" | "message" | "action" | "call";
  at: string;
  title: string;
  status?: string | null;
  work_at?: string | null;
  campaign_name?: string | null;
  notif_type?: string | null;
  /** For kind "action" / "call": which manager performed / made it. */
  actor?: string | null;
  /** For kind "call": the manager's Móttökusímtal note (athugasemd). */
  note?: string | null;
}

export interface VolunteerTimelineDay {
  date: string;
  worked: boolean;
  calls: number;
  doors: number;
  talk_minutes: number;
  events: VolunteerTimelineEvent[];
}

export interface VolunteerTimelineSignup {
  id: string;
  category: string | null;
  campaign_name: string | null;
  vetting_status: string | null;
  signed_up_at: string;
}

export interface VolunteerTimeline {
  days: VolunteerTimelineDay[];
  signups: VolunteerTimelineSignup[];
}

/** Day-by-day workview timeline for one volunteer (activity + messages). */
export async function getVolunteerTimeline(
  electionId: string,
  memberId: string,
  campaignId?: string,
): Promise<VolunteerTimeline> {
  return fetchApi(`/api/elections/${electionId}/volunteers/${memberId}/timeline${bulkQs(campaignId)}`);
}

export interface VolunteerSystemMessage {
  type: string;
  count: number;
  last_at: string | null;
}

/** Automatic/system messages sent to this election's volunteers, grouped by type. */
export async function getVolunteerSystemMessages(
  electionId: string,
  campaignId?: string,
): Promise<VolunteerSystemMessage[]> {
  return fetchApi(`/api/elections/${electionId}/volunteers/system-messages${bulkQs(campaignId)}`);
}

// ── Public attendance confirmation (no auth — token is the secret) ──

export interface DayPlanScheduleItem {
  time: string;
  title: string;
  description?: string;
}

/** Dagskrá dagsins as rendered on the public confirm page. */
export interface DayPlanInfo {
  day: string;
  lunch: string | null;
  dinner: string | null;
  schedule: DayPlanScheduleItem[];
  notes: string | null;
}

export interface CalloutConfirmInfo {
  status: "sent" | "confirmed" | "declined";
  given_name: string | null;
  /** Gendered greeting word: "Sæll" (male), "Sæl" (female), "Sæl/sæll" (unknown). */
  greeting?: string;
  campaign_name: string | null;
  work_at: string | null;
  message: string;
  already_responded: boolean;
  day_plan: DayPlanInfo | null;
  /** Only present when >= 3 recipients have confirmed (social pull). */
  confirmed_count: number | null;
}

/** Public: resolve a callout token for the /maeting confirmation page. */
export async function getCalloutByToken(token: string): Promise<CalloutConfirmInfo> {
  const res = await fetch(`${API_BASE}/api/volunteer-callouts/${encodeURIComponent(token)}`);
  if (!res.ok) throw new Error("Tengillinn er ógildur eða útrunninn");
  return res.json();
}

/** Public: record a volunteer's attendance answer. */
export async function respondToCallout(
  token: string,
  status: "confirmed" | "declined",
): Promise<CalloutConfirmInfo> {
  const res = await fetch(`${API_BASE}/api/volunteer-callouts/${encodeURIComponent(token)}/respond`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error("Ekki tókst að skrá svar");
  return res.json();
}

export async function searchMembersForElection(
  electionId: string,
  q: string,
): Promise<MemberSearchResult[]> {
  const sp = new URLSearchParams({ q });
  return fetchApi(`/api/elections/${electionId}/volunteers/member-search?${sp}`);
}

/**
 * Resolve a raw kennitala to its kennitala_hash, but only if that person is a
 * volunteer in this election. Backs the kennitala branch of the workspace
 * header omni search (name/phone match client-side over the loaded roster;
 * only kennitala needs the server-side HMAC hash). Returns
 * `{ kennitala_hash: null }` when the kennitala isn't a volunteer here.
 */
export async function lookupVolunteerByKennitala(
  electionId: string,
  kennitala: string,
): Promise<{ kennitala_hash: string | null }> {
  const sp = new URLSearchParams({ kennitala });
  return fetchApi(`/api/elections/${electionId}/volunteers/kennitala-lookup?${sp}`);
}

export async function addElectionVolunteer(
  electionId: string,
  data: { member_id: string; category?: string; campaign_id?: string },
): Promise<ElectionVolunteerDetail> {
  return fetchApi(`/api/elections/${electionId}/volunteers`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function removeElectionVolunteer(
  electionId: string,
  volunteerId: string,
  campaignId?: string,
): Promise<void> {
  const qs = campaignId ? `?campaign_id=${campaignId}` : "";
  return fetchApi(`/api/elections/${electionId}/volunteers/${volunteerId}${qs}`, {
    method: "DELETE",
  });
}

/**
 * Download a campaign's volunteer roster as an XLSX file. Streams the
 * binary back from the backend and triggers a browser save via a blob
 * URL — resolves once the download has kicked off.
 *
 * No consent dialog: this is internal admin data (names, phone numbers,
 * vetting status of canvassers), not voter data, so the walk-list
 * consent gate doesn't apply. RBAC is checked server-side — national
 * managers or the campaign's kosningastjóri.
 */
export async function exportCampaignVolunteers(
  electionId: string,
  campaignId: string,
): Promise<void> {
  const url = `${API_BASE}/api/elections/${electionId}/volunteers/export`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(getViewMode() === "user" ? { "X-View-As": "user" } : {}),
    },
    body: JSON.stringify({ campaign_id: campaignId }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }

  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || "sjalfbodalidar.xlsx";

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }, 100);
}

// ── Volunteer Activity ──

export interface VolunteerActivity {
  total_calls: number;
  total_doors: number;
  total_contacts: number;
  total_talk_minutes: number;
  disposition_breakdown: Record<string, number>;
  campaigns: Array<{ campaign_id: string; campaign_name: string; svfnr?: string; calls: number; doors: number }>;
  last_active: string | null;
}

export async function getMyActivity(electionId: string): Promise<VolunteerActivity> {
  return fetchApi(`/api/kosning/my-activity?election_id=${electionId}`);
}

// ── Kosningaryfirlit — personal overview ──

export interface DailyActivity {
  date: string;
  count: number;
}

export interface MilestoneInfo {
  threshold: number;
  label: string;
  achieved: boolean;
  achieved_at: string | null;
}

export interface PossiblePhone {
  phone_number: string;
  provided_by_name: string;
  source: string;
}

export interface NextContactData {
  list_id: string | null;
  campaign_id: string | null;
  entry_id: string | null;
  kennitala_hash: string | null;
  name: string | null;
  address: string | null;
  postal_code: string | null;
  town: string | null;
  phone_number: string | null;
  possible_phones: PossiblePhone[] | null;
  last_disposition: string | null;
  notes: string | null;
  remaining: number;
  total: number;
  /** Entries merely deferred (skip_until in the future, no final disposition).
   *  Lets the hero tell "Þú hefur tæmt listann!" (all final) apart from
   *  "allir sem eftir eru bíða" (some snoozed). */
  snoozed: number;
}

export interface MyOverviewData {
  total_contacts_all_time: number;
  total_contacts_election: number;
  total_doors: number;
  total_calls: number;
  disposition_breakdown: Record<string, number>;
  conversations_count: number;
  supporters_count: number;
  conversion_rate: number;
  total_talk_minutes: number;
  daily_activity: DailyActivity[];
  milestones: MilestoneInfo[];
  next_milestone: number;
  doors_to_next: number;
  current_streak: number;
  best_streak: number;
  daily_actions_today: number;
  daily_goal: number;
  personal_list_total: number;
  personal_list_next_contact: NextContactData | null;
  campaigns: Array<{ campaign_id: string; campaign_name: string; svfnr?: string; calls: number; doors: number }>;
  member_name: string;
  election_name: string;
  last_active: string | null;
}

export async function getMyOverview(electionId?: string): Promise<MyOverviewData> {
  const qs = electionId ? `?election_id=${electionId}` : "";
  return fetchApi(`/api/kosning/my-overview${qs}`);
}

// ── Suggested contacts / next contact / personal contact log ──

export interface SuggestedContact {
  kennitala_hash: string;
  /** Disambiguation only — the plaintext kennitala of non-consenting
   *  relatives/neighbours never leaves the backend (D3). */
  birth_year: number | null;
  name: string;
  address: string | null;
  postal_code: string | null;
  town: string | null;
  /** One of: "Maki" | "Fjölskyldumeðlimur" | "Sama íbúð" | "Sama hús" | "Sama gata" */
  reason: string;
}

export interface SuggestedContactsResponse {
  family: SuggestedContact[];
  neighbors: SuggestedContact[];
}

export async function getSuggestedContacts(
  electionId?: string,
  limit = 20,
): Promise<SuggestedContactsResponse> {
  const sp = new URLSearchParams({ limit: String(limit) });
  if (electionId) sp.set("election_id", electionId);
  return fetchApi(`/api/kosning/suggested-contacts?${sp}`);
}

export async function getNextContact(electionId?: string): Promise<NextContactData> {
  const qs = electionId ? `?election_id=${electionId}` : "";
  return fetchApi(`/api/kosning/next-contact${qs}`);
}

export interface PersonalLogResponse {
  id: string;
  entry_id: string;
  kennitala_hash: string;
  disposition: string;
  contacted_at: string;
}

export interface PersonalLogResult {
  log: PersonalLogResponse;
  next: NextContactData;
}

export async function logPersonalContact(
  listId: string,
  entryId: string,
  disposition: string,
  notes?: string | null,
  /** Client-pinned ISO timestamp (the idempotency anchor): reuse the same
   *  value on a retry so the backend dedups instead of double-counting. */
  contactedAt?: string | null,
): Promise<PersonalLogResult> {
  return fetchApi(`/api/kosning/personal-list/${listId}/entry/${entryId}/log`, {
    method: "POST",
    body: JSON.stringify({
      disposition,
      notes: notes ?? null,
      contacted_at: contactedAt ?? null,
    }),
  });
}

export async function skipPersonalEntry(
  listId: string,
  entryId: string,
): Promise<NextContactData> {
  return fetchApi(`/api/kosning/personal-list/${listId}/entry/${entryId}/skip`, {
    method: "POST",
  });
}

/** Undo the caller's latest logged contact for this entry ("Afturkalla").
 *  Recomputes person_contact_state and lifts the 8h retry snooze so the
 *  person returns to the rotation immediately. 403 when the latest log
 *  isn't the caller's; 404 when there is nothing to clear. */
export async function clearPersonalContact(
  listId: string,
  entryId: string,
): Promise<{ cleared: boolean }> {
  return fetchApi(`/api/kosning/personal-list/${listId}/entry/${entryId}/contact`, {
    method: "DELETE",
  });
}

// ── Issue Tags ──

export interface ActionOption {
  key: string;
  label: string;
  color: string;
  // Only set on candidate_select options (SPEZ people): the admin's one-line
  // reason, rendered as "Name — reason" in the /call dropdown.
  description?: string | null;
}

export interface IssueTag {
  id: string;
  campaign_id: string;
  tag_key: string;
  label: string;
  tag_type: "topic" | "action" | "text_input" | "candidate_select";
  options: ActionOption[] | null;
  side_effect: string | null;
  is_standard: boolean;
  is_internal: boolean;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  // ── Election-catalog projection (Málefni og merki) ──
  // catalog_id != null  ⇒ projected from the election-level catalog (managed
  //   centrally on the election page); this campaign row is read-only here.
  // catalog_id == null  ⇒ a local per-campaign tag, still editable in place.
  catalog_id: string | null;
  is_list_scoped: boolean;
  is_call_pinned: boolean;
}

export interface IssueTagSummary {
  id: string;
  tag_key: string;
  label: string;
  tag_type: "topic" | "action" | "text_input" | "candidate_select";
  options: ActionOption[] | null;
  sort_order: number;
}

export async function getCampaignIssueTags(
  campaignId: string,
  includeInactive = false,
  includeInternal = false,
): Promise<IssueTag[]> {
  const params = new URLSearchParams();
  if (includeInactive) params.set("include_inactive", "true");
  if (includeInternal) params.set("include_internal", "true");
  const qs = params.toString() ? `?${params}` : "";
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/issue-tags${qs}`);
}

// ── Walk list tag attachment (admin tag picker) ──

export async function getWalkListTags(walkListId: string): Promise<IssueTagSummary[]> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/tags`);
}

export async function setWalkListTags(
  walkListId: string,
  issueTagIds: string[],
): Promise<IssueTagSummary[]> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/tags`, {
    method: "PUT",
    body: JSON.stringify({ issue_tag_ids: issueTagIds }),
  });
}

export async function addWalkListTag(walkListId: string, issueTagId: string): Promise<IssueTagSummary[]> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/tags/${issueTagId}`, {
    method: "POST",
  });
}

export async function removeWalkListTag(walkListId: string, issueTagId: string): Promise<void> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/tags/${issueTagId}`, {
    method: "DELETE",
  });
}

// ── Inherited tags (lazy per-target) ──

export async function getInheritedTags(
  campaignId: string,
  targetId: string,
): Promise<IssueTagSummary[]> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/targets/${targetId}/inherited-tags`);
}

// ── SPEZ candidates ──

export interface CandidateOption {
  // `key` IS the kennitala_hash — matches ActionOption shape so the same JSONB
  // option array can be parsed by IssueTagResponse and rendered by the existing
  // candidate_select <option value> binding.
  key: string;
  label: string;
  color: string;
  // One-line reason an admin gives for why this person should call — shown
  // as "Name — reason" in the /call dropdown.
  description?: string | null;
}

export async function listCandidates(campaignId: string): Promise<CandidateOption[]> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/candidates`);
}

export async function addCandidate(campaignId: string, kennitala: string, description?: string): Promise<CandidateOption> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/candidates`, {
    method: "POST",
    body: JSON.stringify({ kennitala, description: description || null }),
  });
}

export async function removeCandidate(campaignId: string, candidateKey: string): Promise<void> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/candidates/${candidateKey}`, {
    method: "DELETE",
  });
}

export async function reorderCandidates(campaignId: string, keys: string[]): Promise<void> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/candidates/reorder`, {
    method: "PUT",
    body: JSON.stringify({ keys }),
  });
}

// ── Election-level follow-up people (central SPEZ) ──
// Registers/removes a person across EVERY municipality campaign of the election
// at once. Used by the election management page for þjóðaratkvæðagreiðsla.

export async function listElectionCandidates(electionId: string): Promise<CandidateOption[]> {
  return fetchApi(`/api/canvassing/elections/${electionId}/candidates`);
}

export async function addElectionCandidate(electionId: string, kennitala: string, description?: string): Promise<CandidateOption> {
  return fetchApi(`/api/canvassing/elections/${electionId}/candidates`, {
    method: "POST",
    body: JSON.stringify({ kennitala, description: description || null }),
  });
}

export async function removeElectionCandidate(electionId: string, candidateKey: string): Promise<void> {
  return fetchApi(`/api/canvassing/elections/${electionId}/candidates/${candidateKey}`, {
    method: "DELETE",
  });
}

// ── Targeted action_response removal ──

export async function removeTargetActionResponse(
  kennitalaHash: string,
  campaignId: string,
  tagKey: string,
  value?: string,
): Promise<void> {
  const sp = new URLSearchParams({ campaign_id: campaignId });
  if (value) sp.set("value", value);
  return fetchApi(
    `/api/canvassing/targets/${kennitalaHash}/action-responses/${encodeURIComponent(tagKey)}?${sp.toString()}`,
    { method: "DELETE" },
  );
}

export async function createIssueTag(
  campaignId: string,
  data: { tag_key: string; label: string; tag_type?: string; options?: ActionOption[]; side_effect?: string; sort_order?: number }
): Promise<IssueTag> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/issue-tags`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateIssueTag(
  tagId: string,
  data: { label?: string; options?: ActionOption[]; sort_order?: number; is_active?: boolean }
): Promise<IssueTag> {
  return fetchApi(`/api/canvassing/issue-tags/${tagId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ── Election-level issue-tag catalog (Málefni og merki) ──
// The kosningastjóri control plane: one place to define the whole election's
// tag vocabulary. Catalog tags project down into every municipality campaign;
// per-list scoping is driven from here too. (Retargeting is manual: filter by
// "Merkt með málefni" / has_issue_tag in the query builder.)

export interface ElectionIssueTag {
  id: string;
  election_id: string;
  tag_key: string;
  label: string;
  tag_type: "topic" | "action" | "text_input" | "candidate_select";
  options: ActionOption[] | null;
  side_effect: string | null;
  is_standard: boolean;
  is_internal: boolean;
  is_list_scoped: boolean;
  is_call_pinned: boolean;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  // How many municipality campaigns this catalog tag is currently projected into.
  projected_campaigns: number;
}

/** A tag available to a volunteer working a specific walk list.
 *  `injected: true` = explicitly injected into THIS list (the prominent
 *  "Áhrifa merki" strip); the rest are the campaign-wide standard vocabulary.
 *  List-scoped tags appear here ONLY on lists they were injected into. */
export interface AvailableTag {
  id: string;
  tag_key: string;
  label: string;
  tag_type: "topic" | "action" | "text_input" | "candidate_select";
  options?: ActionOption[] | null;
  is_list_scoped: boolean;
  is_call_pinned: boolean;
  sort_order: number;
  injected: boolean;
}

export async function listElectionIssueTags(
  electionId: string,
  opts?: { includeInactive?: boolean; includeInternal?: boolean },
): Promise<ElectionIssueTag[]> {
  const params = new URLSearchParams();
  if (opts?.includeInactive) params.set("include_inactive", "true");
  if (opts?.includeInternal) params.set("include_internal", "true");
  const qs = params.toString() ? `?${params}` : "";
  return fetchApi(`/api/elections/${electionId}/issue-tags${qs}`);
}

export async function createElectionIssueTag(
  electionId: string,
  body: {
    tag_key: string;
    label: string;
    tag_type: "topic" | "action" | "text_input" | "candidate_select";
    options?: ActionOption[];
    side_effect?: string | null;
    is_internal?: boolean;
    is_list_scoped?: boolean;
    is_call_pinned?: boolean;
    sort_order?: number;
  },
): Promise<ElectionIssueTag> {
  return fetchApi(`/api/elections/${electionId}/issue-tags`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateElectionIssueTag(
  electionId: string,
  catalogId: string,
  body: {
    label?: string;
    options?: ActionOption[];
    side_effect?: string | null;
    is_internal?: boolean;
    is_list_scoped?: boolean;
    is_call_pinned?: boolean;
    sort_order?: number;
    is_active?: boolean;
  },
): Promise<ElectionIssueTag> {
  return fetchApi(`/api/elections/${electionId}/issue-tags/${catalogId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function deleteElectionIssueTag(
  electionId: string,
  catalogId: string,
): Promise<void> {
  return fetchApi(`/api/elections/${electionId}/issue-tags/${catalogId}`, {
    method: "DELETE",
  });
}

/** Walk lists this catalog tag is already injected into (this election only). */
export async function getElectionIssueTagLists(
  electionId: string,
  catalogId: string,
): Promise<string[]> {
  return fetchApi(`/api/elections/${electionId}/issue-tags/${catalogId}/lists`);
}

export async function attachElectionIssueTagToLists(
  electionId: string,
  catalogId: string,
  walkListIds: string[],
): Promise<{ attached: number; already: number; requested: number }> {
  return fetchApi(`/api/elections/${electionId}/issue-tags/${catalogId}/lists`, {
    method: "PUT",
    body: JSON.stringify({ walk_list_ids: walkListIds }),
  });
}

export async function getWalkListAvailableTags(walkListId: string): Promise<AvailableTag[]> {
  return fetchApi(`/api/canvassing/walk-lists/${walkListId}/available-tags`);
}

// ── Issue Aggregation (War Room) ──

export interface IssueCount {
  tag_key: string;
  label: string;
  count: number;
}

export interface IssueTrend {
  tag_key: string;
  label: string;
  daily_counts: number[];
}

export interface IssuePostalBreakdown {
  postal_code: string;
  counts: IssueCount[];
}

export interface IssueAggregationResponse {
  totals: IssueCount[];
  trends: IssueTrend[];
  by_postal_code: IssuePostalBreakdown[];
}

export async function getWarRoomCampaignIssues(
  campaignId: string,
  electionId: string
): Promise<IssueAggregationResponse> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/issues?election_id=${electionId}`);
}

export async function getWarRoomElectionIssues(
  electionId: string
): Promise<IssueAggregationResponse> {
  return fetchApi(`/api/war-room/elections/${electionId}/issues`);
}

// ── Cross-Campaign Comparison ──

export interface WarRoomCampaignComparison {
  campaign_id: string;
  campaign_name: string;
  // null for national-scope campaigns (no single municipality)
  svfnr: string | null;
  municipality_name: string | null;
  contact_rate_pct: number;
  support_rate_pct: number;
  conversion_rate_pct: number;
  volunteer_efficiency: number;
  positive_contact_pct: number;
  member_household_hit_rate_pct: number;
}

export interface WarRoomComparisonResponse {
  campaigns: WarRoomCampaignComparison[];
}

export async function getWarRoomComparison(
  electionId: string
): Promise<WarRoomComparisonResponse> {
  return fetchApi(`/api/war-room/elections/${electionId}/comparison`);
}

// ── LLM Analysis ──

export interface ElectionAnalysis {
  id: string;
  election_id: string;
  content_markdown: string;
  generated_at: string;
  model: string | null;
}

export interface ElectionAnalysisListResponse {
  items: ElectionAnalysis[];
  total: number;
  has_enough_data: boolean;
}

export async function getElectionAnalyses(
  electionId: string,
  limit = 10
): Promise<ElectionAnalysisListResponse> {
  return fetchApi(`/api/war-room/elections/${electionId}/analyses?limit=${limit}`);
}

export async function generateElectionAnalysis(
  electionId: string
): Promise<ElectionAnalysis> {
  return fetchApi(`/api/war-room/elections/${electionId}/analyses/generate`, {
    method: "POST",
  }, 60000);
}

// ── Feedback ──

export interface FeedbackItem {
  id: string;
  member_id: string;
  member_name: string | null;
  member_email: string | null;
  category: string;
  subject: string;
  description: string;
  screenshot_path: string | null;
  page_url: string | null;
  sentry_event_id: string | null;
  sentry_replay_id: string | null;
  status: string;
  admin_note: string | null;
  created_at: string;
}

export interface FeedbackListResponse {
  items: FeedbackItem[];
  has_more: boolean;
  next_cursor: string | null;
}

export async function submitFeedback(data: {
  category: string;
  subject: string;
  description: string;
  screenshot_path?: string;
  page_url?: string;
  sentry_event_id?: string;
  sentry_replay_id?: string;
}): Promise<{ id: string; status: string; created_at: string }> {
  return fetchApi("/api/feedback", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getFeedbackList(params: {
  status?: string;
  category?: string;
  cursor?: string;
  limit?: number;
}): Promise<FeedbackListResponse> {
  const sp = new URLSearchParams();
  if (params.status) sp.set("status", params.status);
  if (params.category) sp.set("category", params.category);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  return fetchApi(`/api/feedback?${sp.toString()}`);
}

export async function getFeedbackItem(id: string): Promise<FeedbackItem> {
  return fetchApi(`/api/feedback/${id}`);
}

export async function updateFeedback(
  id: string,
  data: { status?: string; admin_note?: string }
): Promise<void> {
  return fetchApi(`/api/feedback/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function replyToFeedback(
  id: string,
  message: string
): Promise<void> {
  return fetchApi(`/api/feedback/${id}/reply`, {
    method: "POST",
    body: JSON.stringify({ message }),
  });
}

export interface SentryContext {
  event: {
    issue_id: string;
    title: string;
    culprit: string;
    level: string;
    status: string;
    first_seen: string;
    last_seen: string;
    count: string;
    platform: string;
    permalink: string;
    short_id: string;
    metadata: Record<string, string>;
  } | null;
  replay: {
    id: string;
    duration: number;
    count_errors: number;
    count_segments: number;
    started_at: string;
    finished_at: string;
    urls: string[];
    browser: string | null;
    os: string | null;
    device: string | null;
  } | null;
}

export async function getFeedbackSentry(id: string): Promise<SentryContext> {
  return fetchApi(`/api/feedback/${id}/sentry`);
}

// ── Campaign Advisor (Research-backed widgets + AI chat) ──

export interface ContactSaturationData {
  buckets: { bucket: string; target_count: number; positive_count: number; negative_count: number }[];
  total_targets: number;
  pct_at_2_plus: number;
}

export interface GOTVReadinessData {
  supporters_identified: number;
  gotv_contacted: number;
  gotv_pct: number;
  days_remaining: number | null;
  election_day: string | null;
  phase: "audkenning" | "sannfaering" | "utkall" | "unknown";
}

export interface VolunteerQualityData {
  volunteers: {
    volunteer_id: string;
    total_contacts: number;
    avg_duration_sec: number | null;
    positive_count: number;
    conversion_pct: number;
  }[];
}

export interface NotHomeAnalysisData {
  by_hour: {
    hour: number;
    total: number;
    not_home: number;
    not_home_pct: number;
    not_home_door_pct: number;
    not_home_phone_pct: number;
  }[];
  revisit_coverage: { not_home_targets: number; revisited: number };
}

export interface RelationalImpactData {
  by_relation: {
    relation: string;
    total: number;
    contacted: number;
    positive: number;
    conversion_pct: number;
  }[];
}

export interface FollowUpQueueData {
  revisit_count: number;
  literature_count: number;
  revisit_completed: number;
  literature_completed: number;
  total_pending: number;
}

export interface CampaignInsight {
  id: string;
  campaign_id: string;
  insight_type: string;
  content_markdown: string;
  generated_at: string;
  model: string | null;
  status: "pending" | "generating" | "complete" | "failed";
  error_message: string | null;
}

export interface CampaignInsightListResponse {
  items: CampaignInsight[];
  total: number;
  has_enough_data: boolean;
}

export interface CampaignChatMessage {
  id: string;
  role: string;
  content: string;
  tool_calls: Record<string, unknown> | null;
  model: string | null;
  created_at: string;
}

export interface CampaignConversation {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

// Widget endpoints
export async function getContactSaturation(campaignId: string, electionId: string): Promise<ContactSaturationData> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/endurtekin-samskipti?election_id=${electionId}`);
}

export async function getGOTVReadiness(campaignId: string, electionId: string): Promise<GOTVReadinessData> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/gotv-readiness?election_id=${electionId}`);
}

export async function getVolunteerQuality(campaignId: string, electionId: string): Promise<VolunteerQualityData> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/volunteer-quality?election_id=${electionId}`);
}

export async function getNotHomeAnalysis(campaignId: string, electionId: string): Promise<NotHomeAnalysisData> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/not-home-analysis?election_id=${electionId}`);
}

export async function getFollowUpQueue(campaignId: string, electionId: string): Promise<FollowUpQueueData> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/follow-up-queue?election_id=${electionId}`);
}

export async function getRelationalImpact(campaignId: string, electionId: string): Promise<RelationalImpactData> {
  return fetchApi(`/api/war-room/campaigns/${campaignId}/relational-impact?election_id=${electionId}`);
}

// Insights endpoints
export async function listCampaignInsights(
  campaignId: string,
  electionId: string,
  limit = 10
): Promise<CampaignInsightListResponse> {
  return fetchApi(`/api/campaign-chat/campaigns/${campaignId}/insights/list?election_id=${electionId}&limit=${limit}`);
}

export async function getCampaignInsight(campaignId: string, electionId: string): Promise<CampaignInsight | null> {
  return fetchApi(`/api/campaign-chat/campaigns/${campaignId}/insights?election_id=${electionId}`);
}

export async function generateCampaignInsight(campaignId: string, electionId: string): Promise<CampaignInsight> {
  // Enqueue: the backend returns a pending CampaignInsight (202) and
  // dispatches a QStash worker that runs Opus under worker_session.
  // We poll GET /insights/{id} every 2s until status is complete|failed.
  const pending = await fetchApi<CampaignInsight>(
    `/api/campaign-chat/campaigns/${campaignId}/insights/generate?election_id=${electionId}`,
    { method: "POST" }
  );

  const POLL_INTERVAL_MS = 2000;
  const MAX_WAIT_MS = 3 * 60 * 1000; // 3 min — well above Opus's typical 30–60s
  const deadline = Date.now() + MAX_WAIT_MS;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    const current = await fetchApi<CampaignInsight>(
      `/api/campaign-chat/campaigns/${campaignId}/insights/${pending.id}?election_id=${electionId}`
    );
    if (current.status === "complete") return current;
    if (current.status === "failed") {
      throw new ApiError(500, current.error_message || "Greining mistókst");
    }
  }

  throw new ApiError(504, "Greining tók of langan tíma");
}

export async function sendInsightToManagers(campaignId: string, electionId: string): Promise<{ sent_to: number }> {
  return fetchApi(`/api/campaign-chat/campaigns/${campaignId}/insights/send?election_id=${electionId}`, {
    method: "POST",
  });
}

// Conversation endpoints
export async function getCampaignChatConversations(campaignId: string): Promise<{ items: CampaignConversation[] }> {
  return fetchApi(`/api/campaign-chat/conversations?campaign_id=${campaignId}`);
}

export async function getCampaignChatConversation(conversationId: string): Promise<CampaignConversation & { messages: CampaignChatMessage[] }> {
  return fetchApi(`/api/campaign-chat/conversations/${conversationId}`);
}

export async function deleteCampaignChatConversation(conversationId: string): Promise<void> {
  return fetchApi(`/api/campaign-chat/conversations/${conversationId}`, { method: "DELETE" });
}

// Social content
export interface SocialContentItem {
  id: string;
  organization_id: string;
  campaign_id: string | null;
  image_url: string;
  caption: string;
  sort_order: number;
  has_transparency: boolean;
  created_at: string | null;
  election_name: string | null;
}

export async function getSocialContent(campaignId?: string): Promise<SocialContentItem[]> {
  const qs = campaignId ? `?campaign_id=${campaignId}` : "";
  return fetchApi(`/api/social-content${qs}`);
}

export async function uploadSocialContent(
  file: File,
  organizationId: string,
  campaignId: string,
  caption: string,
): Promise<SocialContentItem> {
  const form = new FormData();
  form.append("file", file);
  form.append("organization_id", organizationId);
  form.append("campaign_id", campaignId);
  form.append("caption", caption);

  const headers: Record<string, string> = {};
  if (getViewMode() === "user") headers["X-View-As"] = "user";

  const res = await fetch(`${API_BASE}/api/social-content`, {
    method: "POST",
    body: form,
    credentials: "include",
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }
  return res.json();
}

export async function deleteSocialContent(id: string): Promise<void> {
  return fetchApi(`/api/social-content/${id}`, { method: "DELETE" });
}

// ---------- CRM: Companies (Fyrirtæki) ----------

export interface Company {
  kennitala: string;
  kennitala_hash: string;
  name: string;
  business_type: string | null;
  address_street: string | null;
  postal_code: string | null;
  svfnr: string | null;
  is_active: boolean;
}

/**
 * List CRM companies. Requires the `crm.view` permission (403 otherwise).
 *
 * The backend returns `{ items, next_cursor }`; we derive `has_more` from
 * `next_cursor` so the result satisfies `PaginatedResponse<Company>` and
 * plugs straight into `usePaginatedList`.
 */
export async function listCompanies(params?: {
  search?: string;
  active?: boolean;
  cursor?: string;
  limit?: number;
  // ag-grid SSRM: JSON-encoded sortModel / filterModel. The companies grid
  // always sends them (even "[]" / "{}") so the backend uses OFFSET pagination
  // (cursor becomes a numeric row index) and returns a real total_count.
  sortModel?: string;
  filterModel?: string;
}): Promise<PaginatedResponse<Company>> {
  const sp = new URLSearchParams();
  if (params?.search) sp.set("search", params.search);
  if (params?.active !== undefined) sp.set("active", String(params.active));
  if (params?.cursor) sp.set("cursor", params.cursor);
  sp.set("limit", String(params?.limit ?? 20));
  if (params?.sortModel) sp.set("sort_model", params.sortModel);
  if (params?.filterModel) sp.set("filter_model", params.filterModel);

  const res = await fetchApi<{
    items: Company[];
    next_cursor: string | null;
    has_more: boolean;
    total_count: number | null;
  }>(`/api/crm/companies?${sp.toString()}`);

  return {
    items: res.items,
    next_cursor: res.next_cursor ?? undefined,
    has_more: res.has_more,
    total_count: res.total_count ?? undefined,
  };
}

/** Every app-safe register field for one company + officer names resolved
 *  from the hashed kennitölur (chairman / board member to /people, parent to
 *  /companies). Requires `crm.view`. */
export interface CompanyDetail {
  kennitala: string;
  kennitala_hash: string;
  full_name: string | null;
  short_name: string | null;
  alt_foreign_name: string | null;
  business_type: string | null;
  business_activity: string | null;
  address_street: string | null;
  postal_code: string | null;
  svfnr: string | null;
  country: string | null;
  currency: string | null;
  share_capital: number | null;
  date_established: string | null;
  registered_at: string | null;
  deregistered: boolean;
  banned: boolean;
  is_active: boolean;
  receiver_name: string | null;
  receiver_kennitala_hash: string | null;
  receiver_kind: string | null;
  chairman_hash: string | null;
  chairman_name: string | null;
  board_member_hash: string | null;
  board_member_name: string | null;
  parent_company_kennitala: string | null;
  parent_company_kennitala_hash: string | null;
  parent_company_name: string | null;
}

/** Fetch the full company record by opaque `kennitala_hash`. Requires `crm.view`. */
export async function getCompanyDetail(kennitalaHash: string): Promise<CompanyDetail> {
  return fetchApi(`/api/crm/companies/${encodeURIComponent(kennitalaHash)}`);
}

// ---------- CRM: Contact 360° profile ----------

export interface ContactProfile {
  subject: {
    kennitala_hash: string;
    kind: "person" | "company";
    name: string | null;
    svfnr: string | null;
    address_street: string | null;
    postal_code: string | null;
    is_active: boolean;
    /** Party join date ("Í flokknum síðan"); null for non-members and companies. */
    join_date: string | null;
  };
  engagement: {
    engagement_score: number;
    engagement_state: string;
    last_interaction_at: string | null;
    interactions_12mo: number;
  } | null;
  timeline: {
    kind: string;
    id: string;
    at: string | null;
    label: string | null;
    detail: string | null;
    direction: string | null;
  }[];
  relationships: {
    relation_type: string;
    role: string | null;
    source: string; // "derived" | "manual"
    direction: "in" | "out";
    other_kh: string;
    other_name: string | null;
    other_kind: "company" | "person" | null;
  }[];
  flows: {
    id: string;
    stage_key: string;
    status: string;
    expected_amount: number | null;
    entry_reason: string | null;
    flow_id: string;
    flow_name: string;
    flow_key: string;
  }[];
  tags: { id: string; name: string }[];
}

/** Tag a CRM subject by name (resolves/creates the tag). Requires `crm.manage`. */
export async function addSubjectTag(kennitalaHash: string, name: string): Promise<{ id: string; name: string }> {
  return fetchApi(`/api/crm/contacts/${encodeURIComponent(kennitalaHash)}/tags`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

/** Remove a tag from a CRM subject. Requires `crm.manage`. */
export async function removeSubjectTag(kennitalaHash: string, tagId: string): Promise<void> {
  return fetchApi(`/api/crm/contacts/${encodeURIComponent(kennitalaHash)}/tags/${encodeURIComponent(tagId)}`, {
    method: "DELETE",
  });
}

// ---------- CRM: Subject follow-up tasks ----------

/** A follow-up task attached to a CRM subject (a task with kennitala_hash set). */
export interface SubjectTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  kennitala_hash: string | null;
  created_at: string | null;
}

/** Open follow-ups attached to a subject. Requires `crm.view`. */
export async function listSubjectTasks(kennitalaHash: string): Promise<SubjectTask[]> {
  return fetchApi(`/api/crm/contacts/${encodeURIComponent(kennitalaHash)}/tasks`);
}

/** Create a follow-up for a subject (defaults to the caller as assignee). Requires `crm.log`. */
export async function createSubjectTask(
  kennitalaHash: string,
  input: { title: string; due_date?: string | null; assigned_to?: string | null },
): Promise<SubjectTask> {
  return fetchApi(`/api/crm/contacts/${encodeURIComponent(kennitalaHash)}/tasks`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** A row in the caller's "Eftirfylgni" list — their open CRM follow-ups. */
export interface MyFollowUp {
  id: string;
  title: string;
  due_date: string | null;
  kennitala_hash: string | null;
  subject_kind: string;
  status: string;
}

/** The caller's open CRM follow-up tasks (for the hub). Requires `crm.view`. */
export async function listMyFollowUps(): Promise<MyFollowUp[]> {
  return fetchApi("/api/crm/my-follow-ups");
}

// ---------- CRM: Connection paths ("how is the party connected to X") ----------

/** A shortest path from a subject to an active party member (≤3 hops). `chain`
 *  is the ordered list of hops, subject → … → member. */
export interface ConnectionPath {
  member_kh: string;
  member_name: string | null;
  depth: number;
  chain: { kh: string; name: string | null }[];
}

/** Paths from a subject to active party members over the relationship graph.
 *  Requires `crm.view`. */
export async function getConnections(kennitalaHash: string): Promise<ConnectionPath[]> {
  return fetchApi(`/api/crm/contacts/${encodeURIComponent(kennitalaHash)}/connections`);
}

/** A person hit from the CRM relationship picker (name search over
 *  sensitive.people). Requires `crm.view`. */
export interface PersonSearchResult {
  kennitala_hash: string;
  name: string | null;
  address_street: string | null;
  postal_code: string | null;
}

/** Name-search people for the relationship picker (companies use listCompanies). */
export async function searchPeople(q: string, limit = 10): Promise<PersonSearchResult[]> {
  const sp = new URLSearchParams({ q });
  sp.set("limit", String(limit));
  return fetchApi(`/api/crm/contacts?${sp.toString()}`);
}

/**
 * Fetch the CRM 360° profile for a single subject (person or company),
 * keyed by opaque `kennitala_hash`. Requires the `crm.view` permission
 * (403 otherwise). The hash is URL-encoded before being placed in the
 * path so non-path-safe characters survive.
 */
export async function getContactProfile(
  kennitalaHash: string,
): Promise<ContactProfile> {
  return fetchApi(`/api/crm/contacts/${encodeURIComponent(kennitalaHash)}`);
}

// ---------- CRM: Flows (Ferlar / lifecycle flows) ----------

/** One stage in a flow. `order` drives column ordering on the Kanban
 *  board; `sla_days` is the soft deadline for an item to sit in this stage
 *  (null = no SLA). */
export interface FlowStage {
  key: string;
  label: string;
  order: number;
  sla_days: number | null;
}

/** A CRM lifecycle flow (e.g. a major-donor cultivation flow). `kind`
 *  distinguishes flow templates (donation, volunteer, …); `scope_strategy`
 *  drives how on-call assignments are scoped (national/municipality/postal);
 *  `stages` is the ordered set of columns. */
export interface Flow {
  id: string;
  name: string;
  flow_key: string;
  kind: string;
  scope_strategy: string;
  stages: FlowStage[];
  is_active: boolean;
}

/** A single card moving through a flow, keyed to a subject by opaque
 *  `kennitala_hash`. `subject_name` is the display label; monetary fields are
 *  raw numbers (format with `formatCurrency`). */
export interface FlowItem {
  id: string;
  kennitala_hash: string;
  subject_kind: string;
  stage_key: string;
  owner_id: string | null;
  owner_name: string | null;
  expected_amount: number | null;
  probability: number | null;
  entered_stage_at: string | null;
  next_action_at: string | null;
  status: string;
  won_amount: number | null;
  entry_reason: string | null;
  svfnr: string | null;
  subject_name: string | null;
}

/** Mutable fields on a flow item. All optional — send only what changes.
 *  Mirrors the backend PATCH body. */
export interface FlowItemPatch {
  stage_key?: string;
  owner_id?: string | null;
  expected_amount?: number | null;
  probability?: number | null;
  status?: string;
  won_amount?: number | null;
  next_action_at?: string | null;
  lost_reason?: string | null;
}

/** A row in the caller's own portfolio ("Mín verkefni") — the flow items
 *  they own across every flow. */
export interface MyFlowItem {
  id: string;
  flow_id: string;
  flow_name: string;
  flow_key: string;
  kennitala_hash: string;
  subject_kind: string;
  stage_key: string;
  expected_amount: number | null;
  status: string;
  next_action_at: string | null;
  entered_stage_at: string | null;
  entry_reason: string | null;
  subject_name: string | null;
}

/** A high-level summary row per flow for the manager overview. */
export interface FlowOverview {
  id: string;
  flow_key: string;
  name: string;
  scope_strategy: string;
  assignee_count: number;
  open_count: number;
  won_30d: number;
  lost_30d: number;
  oldest_open_days: number | null;
}

/** A member match for the flow-roster picker (typeahead by name). */
export interface FlowMemberResult {
  id: string;
  name: string;
  municipality: string | null;
}

/** Typeahead over active members to add to a flow's on-call roster. Requires `crm.manage`. */
export async function searchFlowMembers(q: string): Promise<FlowMemberResult[]> {
  return fetchApi(`/api/crm/flow-members/search?q=${encodeURIComponent(q)}`);
}

/** Per-municipality coverage for a flow — how many open items sit in each
 *  svfnr and whether anyone is on call there. */
export interface FlowCoverageRow {
  svfnr: string;
  open_count: number;
  has_oncall: boolean;
}

/** An on-call roster assignment on a flow. `scope_type` ∈ national |
 *  municipality | postal; `scope_value` is the svfnr/postal code (null for
 *  national). */
export interface FlowAssignment {
  id: string;
  member_id: string;
  member_name: string | null;
  scope_type: string;
  scope_value: string | null;
  status: string;
  created_at: string | null;
}

/** A flow the caller is currently on call for (Verkefni surface). */
export interface OncallFlow {
  id: string;
  flow_key: string;
  name: string;
  scope_strategy: string;
}

/** A single retrospective verb the worker can log against an item. `key` is
 *  the value to send to `logVerkefni`; `disposition`/`sets` are backend hints. */
export interface VerkefniVerb {
  key: string;
  label: string;
  disposition: string | null;
  sets: Record<string, unknown> | null;
}

/** The current single item served to a worker on the Verkefni surface. */
export interface VerkefniItem {
  id: string;
  kennitala_hash: string;
  subject_kind: string;
  stage_key: string;
  entry_reason: string | null;
  svfnr: string | null;
  postal_code: string | null;
  entered_stage_at: string | null;
  subject_name: string | null;
  address_street: string | null;
  person_postal: string | null;
  contact_score: number | null;
}

/** Response from `nextVerkefni` — the next item (or null when done) plus
 *  progress counters, the available verbs, and the flow header. */
export interface NextVerkefniResponse {
  item: VerkefniItem | null;
  done: boolean;
  remaining: number;
  completed_today: number;
  total: number;
  verbs: VerkefniVerb[];
  flow: { id: string; flow_key: string; name: string };
}

/** List all CRM flows with their stage definitions. Requires `crm.view`. */
export async function listFlows(): Promise<Flow[]> {
  return fetchApi("/api/crm/flows");
}

/** Manager overview: one summary row per flow. Requires `crm.view`. */
export async function flowsOverview(): Promise<FlowOverview[]> {
  return fetchApi("/api/crm/flows/overview");
}

/** Per-municipality coverage for a flow. Requires `crm.view`. */
export async function flowCoverage(flowId: string): Promise<FlowCoverageRow[]> {
  return fetchApi(`/api/crm/flows/${encodeURIComponent(flowId)}/coverage`);
}

/** List the items (cards) in a single flow. Requires `crm.view`. */
export async function listFlowItems(flowId: string): Promise<FlowItem[]> {
  return fetchApi(`/api/crm/flows/${encodeURIComponent(flowId)}/items`);
}

/** Patch a single flow item (move stage, reassign owner, mark won/lost,
 *  …). Send only the fields that change. Requires `crm.view`. */
export async function updateFlowItem(
  itemId: string,
  patch: FlowItemPatch,
): Promise<FlowItem> {
  return fetchApi(`/api/crm/flow-items/${encodeURIComponent(itemId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/** List the caller's own flow items across every flow ("Mín verkefni").
 *  Requires `crm.view`. */
export async function listMyPortfolio(): Promise<MyFlowItem[]> {
  return fetchApi("/api/crm/flow-items/mine");
}

/** List the on-call roster for a flow. Requires `crm.view`. */
export async function listFlowAssignments(flowId: string): Promise<FlowAssignment[]> {
  return fetchApi(`/api/crm/flows/${encodeURIComponent(flowId)}/assignments`);
}

/** Add an on-call assignment to a flow. Requires `crm.manage`. */
export async function addFlowAssignment(
  flowId: string,
  body: { member_id: string; scope_type: string; scope_value?: string | null },
): Promise<{ id: string }> {
  return fetchApi(`/api/crm/flows/${encodeURIComponent(flowId)}/assignments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Remove an on-call assignment. Requires `crm.manage`. */
export async function deleteFlowAssignment(assignmentId: string): Promise<void> {
  return fetchApi(`/api/crm/flow-assignments/${encodeURIComponent(assignmentId)}`, {
    method: "DELETE",
  });
}

/** Flows the caller is currently on call for (drives the Verkefni picker).
 *  Authorized by an on-call assignment OR `crm.manage`. */
export async function myOncallFlows(): Promise<OncallFlow[]> {
  return fetchApi("/api/crm/flows/mine/oncall");
}

/** Fetch the next single item for the worker on a flow. Pass `previousId`
 *  after logging the previous item to advance. Authorized by an on-call
 *  assignment OR `crm.manage`. */
export async function nextVerkefni(
  flowKey: string,
  previousId?: string,
): Promise<NextVerkefniResponse> {
  return fetchApi(`/api/crm/flows/${encodeURIComponent(flowKey)}/next`, {
    method: "POST",
    body: JSON.stringify(previousId ? { previous_id: previousId } : {}),
  });
}

/** Log a retrospective verb against a flow item (Verkefni surface).
 *  `verb` ∈ talk | message | done | skip. */
export async function logVerkefni(
  itemId: string,
  verb: string,
  note?: string,
): Promise<{ id: string; status: string; stage_key: string }> {
  return fetchApi(`/api/crm/flow-items/${encodeURIComponent(itemId)}/log`, {
    method: "POST",
    body: JSON.stringify(note ? { verb, note } : { verb }),
  });
}

// ---------- CRM: "Going-cold" dashboard (CRM yfirlit) ----------

/** One subject whose engagement is decaying — surfaced on the CRM dashboard
 *  so an owner can re-engage before they fully lapse. Keyed by opaque
 *  `kennitala_hash`; `engagement_state` is one of the keys in
 *  `engagement_state_counts`. */
export interface AtRiskSubject {
  kennitala_hash: string;
  subject_kind: string;
  engagement_state: string;
  engagement_score: number;
  last_interaction_at: string | null;
  name: string | null;
}

/** A flow item that has overstayed its stage SLA. `days_in_stage` is the
 *  number of days since `entered_stage_at`, pre-computed by the backend. */
export interface StaleFlowItem {
  id: string;
  kennitala_hash: string;
  subject_kind: string;
  stage_key: string;
  entered_stage_at: string | null;
  flow_name: string;
  owner_name: string | null;
  subject_name: string | null;
  days_in_stage: number;
}

/** Payload for the CRM "going-cold" dashboard. `engagement_state_counts` is a
 *  histogram keyed by engagement state (active|cooling|at_risk|lapsed|dormant);
 *  missing keys should render as 0. Requires the `crm.view` permission. */
export interface CrmDashboard {
  engagement_state_counts: Record<string, number>;
  at_risk_subjects: AtRiskSubject[];
  stale_flow_items: StaleFlowItem[];
}

/** Fetch the CRM "going-cold" dashboard (engagement histogram + at-risk
 *  contacts + SLA-breached flow items). Requires `crm.view`. */
export async function getCrmDashboard(): Promise<CrmDashboard> {
  return fetchApi("/api/crm/dashboard");
}

// ---------- CRM: Automation rules (sjálfvirkni) ----------

/** A nightly automation rule. `trigger_type` ∈ engagement_decay | stage_sla |
 *  dues_overdue | recurring_lapsed | cap_approaching; `action` ∈ notify |
 *  create_task. `params`/`action_params` are free-form (e.g. params.threshold_pct
 *  for cap_approaching, action_params.notify_member_id for the recipient). */
export interface AutomationRule {
  id: string;
  name: string;
  trigger_type: string;
  action: string;
  params: Record<string, unknown>;
  action_params: Record<string, unknown>;
  is_active: boolean;
  last_run_at: string | null;
}

/** List automation rules. Requires `crm.view`. */
export async function listAutomationRules(): Promise<AutomationRule[]> {
  return fetchApi("/api/crm/automation/rules");
}

/** Create an automation rule. Requires `crm.admin`. */
export async function createAutomationRule(input: {
  name: string;
  trigger_type: string;
  action?: string;
  params?: Record<string, unknown>;
  action_params?: Record<string, unknown>;
}): Promise<AutomationRule> {
  return fetchApi("/api/crm/automation/rules", { method: "POST", body: JSON.stringify(input) });
}

/** Enable/disable a rule. Requires `crm.admin`. */
export async function updateAutomationRule(id: string, isActive: boolean): Promise<AutomationRule> {
  return fetchApi(`/api/crm/automation/rules/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ is_active: isActive }),
  });
}

/** Delete a rule. Requires `crm.admin`. */
export async function deleteAutomationRule(id: string): Promise<void> {
  return fetchApi(`/api/crm/automation/rules/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ---------- CRM: Segments (saved audiences over sensitive.people) ----------

/** A saved audience — a named filter_json (QueryGroup) over people. */
export interface Segment {
  id: string;
  name: string;
  description: string | null;
  organization_id: string | null;
  filter_json: QueryGroup;
}

export interface SegmentMember {
  kennitala_hash: string;
  name: string | null;
  address_street: string | null;
  postal_code: string | null;
}

export interface SegmentMembersPage {
  items: SegmentMember[];
  next_cursor: string | null;
  has_more: boolean;
  total_count: number | null;
}

/** Campaign-agnostic field catalogue for the segment query builder. `crm.view`. */
export async function getSegmentFields(): Promise<FieldDefinition[]> {
  return fetchApi("/api/crm/segments/fields");
}

/** Live count of people matching a filter while building a segment. `crm.view`. */
export async function previewSegmentCount(filterJson: QueryGroup): Promise<{ count: number }> {
  return fetchApi("/api/crm/segments/preview-count", {
    method: "POST",
    body: JSON.stringify({ filter_json: filterJson }),
  });
}

/** List saved segments. `crm.view`. */
export async function listSegments(): Promise<Segment[]> {
  return fetchApi("/api/crm/segments");
}

/** Fetch one segment. `crm.view`. */
export async function getSegment(id: string): Promise<Segment> {
  return fetchApi(`/api/crm/segments/${encodeURIComponent(id)}`);
}

/** A keyset page of a segment's people + total count (count only on page 1). `crm.view`. */
export async function getSegmentMembers(
  id: string,
  opts?: { cursor?: string; limit?: number },
): Promise<SegmentMembersPage> {
  const sp = new URLSearchParams();
  if (opts?.cursor) sp.set("cursor", opts.cursor);
  if (opts?.limit) sp.set("limit", String(opts.limit));
  const qs = sp.toString();
  return fetchApi(`/api/crm/segments/${encodeURIComponent(id)}/members${qs ? `?${qs}` : ""}`);
}

/** Create a segment. `crm.manage`. */
export async function createSegment(input: {
  name: string;
  filter_json: QueryGroup;
  description?: string | null;
}): Promise<Segment> {
  return fetchApi("/api/crm/segments", { method: "POST", body: JSON.stringify(input) });
}

/** Update a segment (name / description / filter_json). `crm.manage`. */
export async function updateSegment(
  id: string,
  input: { name?: string; description?: string | null; filter_json?: QueryGroup },
): Promise<Segment> {
  return fetchApi(`/api/crm/segments/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(input) });
}

/** Delete a segment. `crm.manage`. */
export async function deleteSegment(id: string): Promise<void> {
  return fetchApi(`/api/crm/segments/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ---------- CRM: Interactions (samskipti) ----------

/** A logged interaction. `channel` ∈ call|email|sms|meeting|letter|note|door|
 *  event|other; `direction` ∈ inbound|outbound|internal. */
export interface Interaction {
  id: string;
  kennitala_hash: string;
  subject_kind: string;
  channel: string;
  direction: string | null;
  summary: string | null;
  body: string | null;
  occurred_at: string;
  logged_by: string | null;
  created_at: string;
}

export interface InteractionInput {
  kennitala_hash: string;
  subject_kind?: "person" | "company";
  channel: string;
  direction?: "inbound" | "outbound" | "internal" | null;
  summary?: string | null;
  body?: string | null;
  occurred_at?: string | null;
}

/** Log a CRM interaction against a subject. Requires `crm.log`. */
export async function logInteraction(input: InteractionInput): Promise<Interaction> {
  return fetchApi("/api/crm/interactions", { method: "POST", body: JSON.stringify(input) });
}

/** Keyset list of interactions for one subject (newest first). Requires `crm.view`. */
export async function listInteractions(params: {
  subject: string;
  cursor?: string;
  limit?: number;
}): Promise<PaginatedResponse<Interaction>> {
  const sp = new URLSearchParams();
  sp.set("subject", params.subject);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  return fetchApi(`/api/crm/interactions?${sp.toString()}`);
}

// ---------- CRM: Flow placement ----------

/** Place a subject into a flow at a stage. Requires `crm.manage`. The
 *  backend 409s (ApiError) if the subject is already in that flow. */
export async function addFlowItem(
  flowId: string,
  input: {
    kennitala_hash: string;
    stage_key: string;
    subject_kind?: string;
    owner_id?: string | null;
    expected_amount?: number | null;
    entry_reason?: string | null;
  },
): Promise<{ id: string }> {
  return fetchApi(`/api/crm/flows/${encodeURIComponent(flowId)}/items`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// ---------- CRM: Relationships (tengsl) ----------

/** Manually link two subjects. Requires `crm.manage`. `relation_type` ∈
 *  {steward, knows, employee, colleague}. Returns whether a new edge was made
 *  (false if it already existed). */
export async function createRelationship(input: {
  from_kh: string;
  to_kh: string;
  relation_type: string;
  role?: string | null;
}): Promise<{ created: boolean }> {
  return fetchApi("/api/crm/relationships", { method: "POST", body: JSON.stringify(input) });
}

// ---------- CRM: Donor compliance + pledges (crm_finance) ----------

/** Donor giving + annual-cap assessment + prohibited-source flags
 *  (lög nr. 162/2006). Behind the separate `crm_finance.view` gate. */
export interface DonorCompliance {
  assessment: {
    year: number;
    year_total: number;
    projected_total: number;
    cap: number;
    within_cap: boolean;
    remaining: number;
    over_by: number;
    donation_count: number;
  };
  flags: string[];
  lifetime_giving: number;
  recent: {
    donation_date: string | null;
    amount: number;
    source_type: string | null;
    method: string | null;
  }[];
  /** Bank claims (kröfur) addressed to this subject by kennitala. */
  claims?: {
    id: string;
    amount: number;
    status: string;
    description: string | null;
    due_date: string | null;
    final_due_date: string | null;
    created_at: string | null;
  }[];
  claims_summary?: {
    outstanding: number;
    paid: number;
    outstanding_count: number;
  };
}

export async function getDonorCompliance(kennitalaHash: string): Promise<DonorCompliance> {
  return fetchApi(`/api/crm/compliance/donor/${encodeURIComponent(kennitalaHash)}`);
}

/** Ríkisendurskoðun annual disclosure: legal-entity (company) donors + totals.
 *  Behind `crm_finance.manage` (bulk donor PII). */
export interface AnnualLegalEntityReport {
  year: number;
  count: number;
  total: number;
  legal_entity_donors: {
    kennitala_hash: string;
    name: string | null;
    kennitala: string | null;
    total: number;
    donation_count: number;
  }[];
}

export async function getAnnualLegalEntityReport(year?: number): Promise<AnnualLegalEntityReport> {
  const qs = year ? `?year=${year}` : "";
  return fetchApi(`/api/crm/compliance/annual-report${qs}`);
}

/** A giving commitment (Moves Management), reconciled against donations. */
export interface Pledge {
  id: string;
  kennitala_hash: string;
  organization_id: string | null;
  amount: number;
  currency: string;
  pledged_at: string;
  due_at: string | null;
  status: string;
  fulfilled_donation_id: string | null;
  notes: string | null;
}

/** List a subject's pledges. Requires `crm_finance.view`. */
export async function listPledges(subject: string): Promise<Pledge[]> {
  return fetchApi(`/api/crm/pledges?subject=${encodeURIComponent(subject)}`);
}

/** Record a pledge. Requires `crm_finance.manage`. */
export async function createPledge(input: {
  kennitala_hash: string;
  amount: number;
  due_at?: string | null;
  notes?: string | null;
}): Promise<Pledge> {
  return fetchApi("/api/crm/pledges", { method: "POST", body: JSON.stringify(input) });
}

/** Update a pledge's status (open|fulfilled|cancelled|lapsed). Requires
 *  `crm_finance.manage`. */
export async function updatePledge(
  pledgeId: string,
  input: { status: string; fulfilled_donation_id?: string | null },
): Promise<Pledge> {
  return fetchApi(`/api/crm/pledges/${encodeURIComponent(pledgeId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

// ---------------------------------------------------------------------------
// Finance console (Styrkir og greiðslur admin) — /api/finance
// ---------------------------------------------------------------------------

/** Shared scope for every finance query: an org + whether to roll up its subtree. */
export interface FinanceScope {
  organization_id: string;
  /** Include the org's ltree descendants (default true). */
  subtree?: boolean;
  /** Calendar year filter; omit for all-time. */
  year?: number | null;
}

export interface FinanceSummary {
  total_amount: number;
  total_count: number;
  donation_count: number;
  unique_donors: number;
  mrr: number;
  by_method: { method: string; amount: number; count: number }[];
  by_source_type: { source_type: string; amount: number; count: number }[];
  by_month: {
    month: string;
    amount: number;
    count: number;
    /** Slice of the month's cash from subscription-billed donations. */
    recurring_amount?: number;
  }[];
  recurring_by_month: { month: string; amount: number }[];
  by_org: { organization_id: string; organization_name: string | null; amount: number; count: number }[];
}

export interface FinanceLedgerRow {
  id: string;
  date: string | null;
  donor_name: string | null;
  kennitala: string | null;
  amount: number | null;
  currency: string;
  method: string | null;
  type: "styrkur" | "felagsgjald" | "askrift";
  source_type: string | null;
  frequency: string | null;
  status: string | null;
  organization_name: string | null;
  reference: string | null;
}

export interface FinanceSubscription {
  id: string;
  member_id: string | null;
  donor_id: string | null;
  member_name: string | null;
  kennitala: string | null;
  organization_id: string;
  organization_name: string | null;
  amount: number;
  currency: string;
  status: string;
  collection_method: string;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  created_at: string | null;
}

export interface ComplianceDonor {
  donor_id: string;
  donor_name: string | null;
  kennitala: string | null;
  is_anonymous: boolean;
  by_source_type: Record<string, number>;
  total_amount: number;
  membership_fee_amount: number;
  non_exempt_amount: number;
  over_cap: boolean;
}

export interface FinanceComplianceReport {
  year: number;
  cap_isk: number;
  dues_exempt_isk: number;
  donors: ComplianceDonor[];
  totals: {
    donor_count: number;
    over_cap_count: number;
    total_amount: number;
    non_exempt_amount: number;
  };
}

export type FinanceDataset = "ledger" | "dues" | "subscriptions" | "compliance";

/** Build a finance query string from a scope (+ extra params), dropping empties. */
function financeQuery(scope: FinanceScope, extra: Record<string, string | number | undefined> = {}): string {
  const params = new URLSearchParams();
  params.set("organization_id", scope.organization_id);
  if (scope.subtree === false) params.set("subtree", "false");
  if (scope.year != null) params.set("year", String(scope.year));
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== "") params.set(k, String(v));
  }
  return params.toString();
}

export async function getFinanceSummary(scope: FinanceScope): Promise<FinanceSummary> {
  return fetchApi(`/api/finance/summary?${financeQuery(scope)}`);
}

export async function getFinanceSubscriptions(scope: FinanceScope): Promise<{ items: FinanceSubscription[] }> {
  return fetchApi(`/api/finance/subscriptions?${financeQuery(scope)}`);
}

export async function getFinanceCompliance(scope: FinanceScope & { year: number }): Promise<FinanceComplianceReport> {
  return fetchApi(`/api/finance/compliance?${financeQuery(scope)}`);
}

export async function getFinanceLedger(
  scope: FinanceScope,
  page: { start_row: number; end_row: number; sort_model: string; filter_model: string },
): Promise<{ items: FinanceLedgerRow[]; has_more: boolean }> {
  const qs = financeQuery(scope, {
    start_row: page.start_row,
    end_row: page.end_row,
    sort_model: page.sort_model,
    filter_model: page.filter_model,
  });
  return fetchApi(`/api/finance/ledger?${qs}`);
}

/**
 * Member-dues for one org + year on the ag-grid server-side row model. Always
 * single-org (subtree is forced off — dues are managed per org); ``search`` is a
 * server-side name match. Returns rows shaped like ``MemberDueStatus``.
 */
export async function getFinanceDues(params: {
  organization_id: string;
  year: number;
  search?: string;
  start_row: number;
  end_row: number;
  sort_model: string;
  filter_model: string;
}): Promise<{ items: MemberDueStatus[]; has_more: boolean }> {
  const qs = financeQuery(
    { organization_id: params.organization_id, subtree: false, year: params.year },
    {
      search: params.search,
      start_row: params.start_row,
      end_row: params.end_row,
      sort_model: params.sort_model,
      filter_model: params.filter_model,
    },
  );
  return fetchApi(`/api/finance/dues?${qs}`);
}

/**
 * Download a finance dataset as a server-generated .xlsx. Mirrors the
 * walk-list export download: authenticated blob fetch → object-URL click.
 * ``extra`` carries the ledger's sort_model/filter_model so the file matches
 * what's on screen, or the compliance/dues ``year``.
 */
async function downloadAuthedFile(url: string, fallbackName: string, init: RequestInit = {}): Promise<void> {
  const res = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      ...(getViewMode() === "user" ? { "X-View-As": "user" } : {}),
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body?.detail || `Villa ${res.status}`, body);
  }
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || fallbackName;
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }, 100);
}

export async function downloadFinanceExport(
  dataset: FinanceDataset,
  scope: FinanceScope,
  extra: Record<string, string | number | undefined> = {},
): Promise<void> {
  const url = `${API_BASE}/api/finance/${dataset}/export?${financeQuery(scope, extra)}`;
  return downloadAuthedFile(url, `${dataset}.xlsx`);
}

// ── Fjáröflun: org-scoped people targeting (query builder) ──

export async function getFundraisingTargetFields(organizationId: string): Promise<FieldDefinition[]> {
  return fetchApi(
    `/api/finance/fundraising/targets/fields?organization_id=${encodeURIComponent(organizationId)}`,
  );
}

export async function previewFundraisingTargetCount(
  organizationId: string,
  filterJson: QueryGroup | null,
): Promise<{ count: number }> {
  return fetchApi(
    `/api/finance/fundraising/targets/preview-count?organization_id=${encodeURIComponent(organizationId)}`,
    { method: "POST", body: JSON.stringify({ filter_json: filterJson }) },
  );
}

export async function downloadFundraisingTargets(
  organizationId: string,
  filterJson: QueryGroup | null,
): Promise<void> {
  const url = `${API_BASE}/api/finance/fundraising/targets/export?organization_id=${encodeURIComponent(organizationId)}`;
  return downloadAuthedFile(url, "sofnunarlisti.xlsx", {
    method: "POST",
    body: JSON.stringify({ filter_json: filterJson }),
  });
}

// ---------- Trustee registry (trúnaðarmannaskrá) ----------
//
// Standalone registry keyed by kennitala_hash (survives member deletion).
// Superadmin-only admin surface under /api/admin/trustees, plus the chairman
// board endpoints under /api/organizations/{id}. Mirrors the backend schemas in
// app/schemas/trustee.py and the read payloads in app/services/trustee_*.py.

/** One assignment row (a role or seat held in an org). ``is_active`` is computed
 *  server-side (``ended_on IS NULL``) so consumers never re-derive the rule. */
export interface TrusteeAssignmentRead {
  id: string;
  kennitala_hash: string;
  organization_id: string;
  organization_name?: string | null;
  organization_code?: string | null;
  role: string;
  title?: string | null;
  basis?: string | null;
  basis_org_id?: string | null;
  basis_org_name?: string | null;
  basis_org_code?: string | null;
  started_on?: string | null;
  ended_on?: string | null;
  is_active: boolean;
  source: string;
  source_request_id?: string | null;
  /** Trustee name, present on board/current-board rows (not on person-timeline rows). */
  name?: string | null;
}

export interface TrusteeSearchResult {
  kennitala_hash: string;
  name?: string | null;
  has_kennitala: boolean;
  kennitala_masked?: string | null;
  active_assignment_count: number;
}

export interface TrusteePersonDetail {
  kennitala_hash: string;
  name?: string | null;
  notes?: string | null;
  has_kennitala: boolean;
  kennitala_masked?: string | null;
  created_at?: string | null;
  assignments: TrusteeAssignmentRead[];
}

export interface TrusteeOrgBoard {
  active: TrusteeAssignmentRead[];
  history: TrusteeAssignmentRead[];
}

export interface TrusteeAssignmentCreate {
  organization_id: string;
  role: string;
  /** Provide exactly one identifier: a raw kennitala (hashed server-side) or a hash. */
  kennitala?: string;
  kennitala_hash?: string;
  name?: string;
  title?: string;
  basis?: string;
  basis_org_id?: string;
  started_on?: string;
  ended_on?: string;
}

/** PATCH: only keys present are applied (backend uses exclude_unset). */
export interface TrusteeAssignmentUpdate {
  title?: string | null;
  basis?: string | null;
  basis_org_id?: string | null;
  started_on?: string | null;
  ended_on?: string | null;
}

export async function searchTrustees(
  query?: string,
  limit = 50,
): Promise<{ items: TrusteeSearchResult[] }> {
  const sp = new URLSearchParams();
  if (query) sp.set("query", query);
  sp.set("limit", String(limit));
  return fetchApi(`/api/admin/trustees/people?${sp.toString()}`);
}

export async function getTrusteePerson(kennitalaHash: string): Promise<TrusteePersonDetail> {
  return fetchApi(`/api/admin/trustees/people/${kennitalaHash}`);
}

/** Audit-logged: decrypts and returns the stored kennitala. */
export async function revealTrusteeKennitala(kennitalaHash: string): Promise<{ kennitala: string }> {
  return fetchApi(`/api/admin/trustees/people/${kennitalaHash}/reveal`, { method: "POST" });
}

export async function createTrusteeAssignment(
  body: TrusteeAssignmentCreate,
): Promise<{ id: string }> {
  return fetchApi(`/api/admin/trustees/assignments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateTrusteeAssignment(
  assignmentId: string,
  body: TrusteeAssignmentUpdate,
): Promise<{ id: string }> {
  return fetchApi(`/api/admin/trustees/assignments/${assignmentId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function endTrusteeAssignment(
  assignmentId: string,
  endedOn?: string,
): Promise<{ id: string }> {
  return fetchApi(`/api/admin/trustees/assignments/${assignmentId}/end`, {
    method: "POST",
    body: JSON.stringify({ ended_on: endedOn ?? null }),
  });
}

export async function deleteTrusteeAssignment(assignmentId: string): Promise<{ status: string }> {
  return fetchApi(`/api/admin/trustees/assignments/${assignmentId}`, { method: "DELETE" });
}

/** Admin-router board for an org (current + full history). */
export async function getTrusteeOrgBoard(orgId: string): Promise<TrusteeOrgBoard> {
  return fetchApi(`/api/admin/trustees/orgs/${orgId}/board`);
}

// --- Master overview (Yfirlit + Skipurit) ---

export interface TrusteeOverviewRollup {
  org_count: number;
  active_seat_count: number;
  misraemi_count: number;
  pending_board_update: number;
  vacancy_count: number;
}

export interface TrusteeOverviewNode {
  org_id: string | null;
  org_code: string | null;
  org_name: string;
  org_type: string | null;
  is_stofnun: boolean;
  is_institution: boolean;
  kjordaemi_nr: number | null;
  is_bucket: boolean;
  formadur: string | null;
  gjaldkeri: string | null;
  board_size: number;
  active_seat_count: number;
  missing_formadur: boolean;
  missing_gjaldkeri: boolean;
  misraemi_count: number;
  pending_board_update: number;
  children: TrusteeOverviewNode[];
  rollup: TrusteeOverviewRollup;
}

export interface TrusteeOverview {
  root: TrusteeOverviewNode;
  institutions: TrusteeOverviewNode[];
  totals: TrusteeOverviewRollup;
  /** Global COUNT of every active seat (ended_on IS NULL), including seats on
   *  óvirk/dissolved orgs that the tree-scoped `totals.active_seat_count` omits. */
  global_active_seat_count: number;
  unresolved_anchors: string[];
  generated_at: string;
}

export async function getTrusteeOverview(refresh = false): Promise<TrusteeOverview> {
  return fetchApi(`/api/admin/trustees/overview${refresh ? "?refresh=true" : ""}`);
}

// --- Misræmisskýrsla (discrepancies) ---

export type TrusteeSuggestedAction =
  | { action: "create_seat"; fields: Record<string, string> }
  | { action: "end_seat"; assignment_id: string };

export interface TrusteeFinding {
  finding_key: string;
  family: string;
  severity: "high" | "medium" | "low";
  org_id: string | null;
  org_code: string | null;
  org_name: string | null;
  kennitala_hash: string | null;
  person_name: string | null;
  role: string;
  rule_ref: string;
  message: string;
  suggested_action: TrusteeSuggestedAction;
  dismissed: boolean;
  dismissal_note?: string | null;
  dismissed_at?: string | null;
}

export interface TrusteeDiscrepancies {
  findings: TrusteeFinding[];
  active_count: number;
  dismissed_count: number;
  unresolved_anchors: string[];
  families: string[];
}

export async function getTrusteeDiscrepancies(params?: {
  family?: string;
  kjordaemi?: number;
  include_dismissed?: boolean;
}): Promise<TrusteeDiscrepancies> {
  const sp = new URLSearchParams();
  if (params?.family) sp.set("family", params.family);
  if (params?.kjordaemi != null) sp.set("kjordaemi", String(params.kjordaemi));
  if (params?.include_dismissed) sp.set("include_dismissed", "true");
  const qs = sp.toString();
  return fetchApi(`/api/admin/trustees/discrepancies${qs ? `?${qs}` : ""}`);
}

export async function dismissTrusteeFinding(
  findingKey: string,
  note?: string,
): Promise<{ finding_key: string; dismissed: boolean }> {
  return fetchApi(`/api/admin/trustees/discrepancies/${findingKey}/dismiss`, {
    method: "POST",
    body: JSON.stringify({ note: note ?? null }),
  });
}

export async function undismissTrusteeFinding(
  findingKey: string,
): Promise<{ finding_key: string; dismissed: boolean }> {
  return fetchApi(`/api/admin/trustees/discrepancies/${findingKey}/dismiss`, { method: "DELETE" });
}

// --- Chairman board endpoints (org page; consumed by PR 7) ---

export interface BoardChangeEntry {
  kennitala: string;
  role: string;
  name?: string;
  title?: string;
  started_on?: string;
}

/** A superadmin pre-approval edit row: a kept entry references itself by
 *  ``kennitala_hash`` (the reviewer has no raw kennitala); a newly added entry
 *  carries a raw ``kennitala``. Exactly one identifier is required server-side. */
export interface BoardChangeEditEntry {
  kennitala?: string;
  kennitala_hash?: string;
  role: string;
  name?: string;
  title?: string;
  started_on?: string;
}

export interface BoardChangeSubmitBody {
  entries: BoardChangeEntry[];
  replace_board?: boolean;
  meeting_date?: string;
}

export interface BoardChangeEditBody {
  entries?: BoardChangeEditEntry[];
  replace_board?: boolean;
  // null explicitly clears a wrongly entered meeting date on a pending beidni
  // (the backend distinguishes null from omitted; omit to leave unchanged).
  meeting_date?: string | null;
}

export interface BoardRoleCreateBody {
  role: string;
  kennitala?: string;
  kennitala_hash?: string;
  name?: string;
  title?: string;
  basis?: string;
  basis_org_id?: string;
  started_on?: string;
}

/** Org-page board (current + history). Superadmin or the org's active formaður. */
export async function getOrgBoard(orgId: string): Promise<TrusteeOrgBoard> {
  return fetchApi(`/api/organizations/${orgId}/board`);
}

/** Resolve a raw kennitala to its Þjóðskrá name for a board-change row, so the
 *  submitter sees who each kennitala is before submitting. Nothing is stored. */
export async function resolveBoardName(
  orgId: string,
  kennitala: string,
): Promise<{ found: boolean; name: string | null }> {
  return fetchApi(`/api/organizations/${orgId}/board/resolve-name`, {
    method: "POST",
    body: JSON.stringify({ kennitala }),
  });
}

export async function submitBoardChange(
  orgId: string,
  body: BoardChangeSubmitBody,
): Promise<{ id: string; approval_id: string; status: string }> {
  return fetchApi(`/api/organizations/${orgId}/board-change`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function editBoardChange(
  orgId: string,
  changeId: string,
  body: BoardChangeEditBody,
): Promise<{ id: string; status: string }> {
  return fetchApi(`/api/organizations/${orgId}/board-change/${changeId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function addBoardRole(
  orgId: string,
  body: BoardRoleCreateBody,
): Promise<{ id: string }> {
  return fetchApi(`/api/organizations/${orgId}/board/roles`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function endBoardRole(
  orgId: string,
  assignmentId: string,
  endedOn?: string,
): Promise<{ id: string }> {
  return fetchApi(`/api/organizations/${orgId}/board/roles/${assignmentId}/end`, {
    method: "POST",
    body: JSON.stringify({ ended_on: endedOn ?? null }),
  });
}


// ── Þrír til fimm (/saman): /api/saman/*, token OR session auth ─────────────
//
// The /saman page's feature-scoped API. Every call is safe on a PUBLIC page:
// skipLoginRedirect keeps a logged-out/invalid-token 401 an answer (fall back
// to the preview) instead of a hard bounce to /login. In token mode the deep
// link's token rides the X-Folk-Token header; in session mode the normal
// cookie applies.

export interface FolkEntry {
  id: string;
  kennitala_hash: string;
  name: string | null;
  address: string | null;
  postal_code: string | null;
  /** Present only for session-authenticated members; token links stay phone-free. */
  phone?: string | null;
  relation: string | null;
  contact_status: "talked" | "needs_followup" | "followup_done" | null;
  claim_state: "active" | "superseded";
}

export interface FolkStyrkjaLink {
  amount: number | null;
  url: string;
}

export interface FolkElection {
  id: string;
  name: string;
  election_day: string;
  is_live: boolean;
}

export interface FolkMe {
  first_name: string;
  /** Registry gender ('male' | 'female' | other/null), for declined copy. */
  gender?: string | null;
  joined_at: string | null;
  election_live: boolean;
  has_email: boolean;
  volunteer_active: boolean;
  needs_consent: boolean;
  current_gdpr_version: number;
  current_election_consent_version: number;
  // The referendum the page operates on; null = campaign closed (no editing).
  election: FolkElection | null;
  entries: FolkEntry[];
  styrkja_links: FolkStyrkjaLink[];
}

function folkOptions(token?: string | null, init?: FetchApiOptions): FetchApiOptions {
  return {
    ...init,
    skipLoginRedirect: true,
    headers: {
      ...(init?.headers as Record<string, string> | undefined),
      ...(token ? { "X-Folk-Token": token } : {}),
    },
  };
}

export async function folkMe(token?: string | null, campaignId?: string | null): Promise<FolkMe> {
  // ?c= is the campaign marker from the newsletter link. It only rides along
  // for the click stamp — the payload is identical with or without it.
  const qs = campaignId ? `?c=${encodeURIComponent(campaignId)}` : "";
  return fetchApi(`/api/saman/me${qs}`, folkOptions(token));
}

export async function folkJoin(src?: string | null, token?: string | null): Promise<{ joined_at: string | null }> {
  return fetchApi(
    `/api/saman/join`,
    folkOptions(token, { method: "POST", body: JSON.stringify({ src: src ?? null }) }),
  );
}

export async function folkAddEntry(
  kennitalaHash: string,
  token?: string | null,
  relationship?: string | null,
): Promise<{ ok: boolean; entry_id: string; already_present: boolean }> {
  return fetchApi(
    `/api/saman/entries`,
    folkOptions(token, {
      method: "POST",
      body: JSON.stringify({ kennitala_hash: kennitalaHash, relationship: relationship ?? null }),
    }),
  );
}

export async function folkSetRelationship(
  entryId: string,
  relationship: string,
  token?: string | null,
): Promise<{ ok: boolean }> {
  return fetchApi(
    `/api/saman/entries/${entryId}/relationship`,
    folkOptions(token, { method: "PATCH", body: JSON.stringify({ relationship }) }),
  );
}

export async function folkRemoveEntry(entryId: string, token?: string | null): Promise<void> {
  await fetchApi(`/api/saman/entries/${entryId}`, folkOptions(token, { method: "DELETE" }));
}

export async function folkSetStatus(
  entryId: string,
  status: FolkEntry["contact_status"],
  token?: string | null,
): Promise<{ ok: boolean; contact_status: string | null; bridged: boolean }> {
  return fetchApi(
    `/api/saman/entries/${entryId}/status`,
    folkOptions(token, { method: "PATCH", body: JSON.stringify({ status }) }),
  );
}

export async function folkSearch(q: string, token?: string | null): Promise<PersonSearchItem[]> {
  const sp = new URLSearchParams();
  if (q) sp.set("q", q);
  return fetchApi(`/api/saman/search?${sp}`, folkOptions(token));
}

export async function folkSuggestions(token?: string | null): Promise<SuggestedContactsResponse> {
  return fetchApi(`/api/saman/suggestions`, folkOptions(token));
}

export async function folkSetEmail(email: string, token?: string | null): Promise<{ ok: boolean }> {
  return fetchApi(
    `/api/saman/email`,
    folkOptions(token, { method: "POST", body: JSON.stringify({ email }) }),
  );
}

/** One-tap acceptance of the current GDPR + election-data declarations. */
export async function folkAcceptConsent(
  gdprVersion: number,
  electionConsentVersion: number,
  token?: string | null,
): Promise<{ ok: boolean; needs_consent: boolean }> {
  return fetchApi(
    `/api/saman/consent`,
    folkOptions(token, {
      method: "POST",
      body: JSON.stringify({
        gdpr_consent_version: gdprVersion,
        election_consent_version: electionConsentVersion,
      }),
    }),
  );
}

/** One-tap volunteer signup (same terminal state as the /hjalpa/takk confirm). */
export async function folkVolunteerSignup(
  token?: string | null,
): Promise<{ ok: boolean; volunteer_active: boolean; already: boolean }> {
  return fetchApi(`/api/saman/volunteer`, folkOptions(token, { method: "POST" }));
}

/** "Senda 3-5 lista": SMS the person on the phone their no-login /saman deep link. */
export async function sendFolkLink(
  campaignId: string,
  data: { kennitala_hash: string },
): Promise<{ id: string; status: string; sms_sent: boolean }> {
  return fetchApi(`/api/canvassing/campaigns/${campaignId}/saman-links`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Þrír til fimm campaign funnel (war-room). */
export interface FolkFunnel {
  emailed: number;
  delivered: number;
  opened: number;
  clicked: number;
  joined: number;
  picked_1_2: number;
  picked_3: number;
  picked_4: number;
  picked_5_plus: number;
  people_covered: number;
  status_talked: number;
  status_needs_followup: number;
  status_followup_done: number;
  bridged_callback_yes: number;
  emails_collected: number;
  by_municipality: { name: string; joined: number; entries: number }[];
}

export async function getFolkFunnel(electionId: string): Promise<FolkFunnel> {
  return fetchApi(`/api/war-room/elections/${electionId}/saman-funnel`);
}

/** Þrír til fimm admin drill-down: per-send end-to-end cohorts, the caller
 * SMS arm, and the participant table. Cohorts are PER SEND: overlapping
 * audiences count in every row they received, the funnel stays the
 * deduplicated truth. */
export interface SamanCampaignRow {
  id: string;
  title: string;
  campaign_type: string;
  status: string;
  sent_at: string | null;
  recipients: number;
  delivered: number;
  opened: number;
  clicked: number;
  joined: number;
  joined_after_send: number;
  people_total: number;
  people_added_after_send: number;
  followup_done: number;
  callback_yes: number;
}

export interface SamanCallerRow {
  sent: number;
  confirmed: number;
  joined: number;
  joined_after_send: number;
  people_total: number;
  people_added_after_send: number;
  followup_done: number;
  callback_yes: number;
}

export interface SamanParticipantRow {
  member_id: string;
  name: string | null;
  municipality: string | null;
  joined_at: string | null;
  entries: number;
  talked: number;
  needs_followup: number;
  followup_done: number;
  last_activity: string | null;
}

export interface SamanTimeseriesPoint {
  day: string; // YYYY-MM-DD, UTC day (= Iceland local)
  joined: number;
  entries_added: number;
  callback_yes: number;
}

/** Styrkja donations from the /saman prefill token. Global per feature (the
 * token carries no election id) and completed-only. */
export interface SamanDonations {
  count: number;
  total_amount: number; // ISK
  donors: number;
}

export interface SamanDetail {
  campaigns: SamanCampaignRow[];
  caller_links: SamanCallerRow;
  participants: SamanParticipantRow[];
  total_participants: number;
  timeseries: SamanTimeseriesPoint[];
  donations: SamanDonations;
  volunteer_signups: number;
}

export async function getSamanDetail(electionId: string): Promise<SamanDetail> {
  return fetchApi(`/api/war-room/elections/${electionId}/saman-detail`);
}

// ---------- Paper blind lists (pappírslistar) ----------
// Printed call-list batches for offline callers + photographed re-import.
// See docs/paper-blind-list-plan.md.

export type PaperDisposition =
  | "callback_yes"
  | "callback_no"
  | "not_home"
  | "wrong_number"
  | "already_voted";

export interface PaperManifestRow {
  code: string;
  kennitala_hash: string;
  name: string;
  age: number | null;
  phone: string | null;
}

export interface PaperParsedRow {
  code: string;
  name_as_read: string | null;
  disposition: string | null;
  undecided: boolean;
  unreadable: boolean;
  confidence: "low" | "medium" | "high";
  note: string | null;
  needs_attention: boolean;
  attention_reason: string | null;
}

export type PaperScanStatus =
  | "uploaded"
  | "parsing"
  | "needs_review"
  /** Parsed fine, but the batch code on the sheet placed nothing. */
  | "unrouted"
  | "applied"
  | "failed";

export interface PaperScan {
  id: string;
  status: PaperScanStatus;
  uploaded_at: string;
  error: string | null;
  parsed: {
    page_header_code: string | null;
    page_number: number | null;
    rows: PaperParsedRow[];
  } | null;
  image_url: string | null;
  /** The batch code read off the sheet, whether or not it resolved. */
  route_code: string | null;
}

export interface PaperBatchSummary {
  id: string;
  batch_no: number;
  /** Globally unique three-letter identity — what sheets and the UI show. */
  batch_code: string;
  row_count: number;
  include_phone: boolean;
  caller_name: string | null;
  created_at: string;
  applied_at: string | null;
  scan_count: number;
  parsed_scan_count: number;
  /**
   * Sheets parsed but not yet approved. Distinct from applied_at, which the
   * FIRST approved sheet already sets: a batch can be part written and still
   * have most of its paper waiting.
   */
  pending_sheets: number;
}

export interface PaperBatchDetail extends PaperBatchSummary {
  walk_list_id: string;
  walk_list_name: string;
  campaign_id: string;
  entries: PaperManifestRow[];
  scans: PaperScan[];
}

export interface PaperApplyResult {
  logged: number;
  deduped: number;
  tags_updated: number;
  skipped_do_not_contact: string[];
  unknown_codes: string[];
  errors: string[];
}

export async function listPaperBatches(walkListId: string): Promise<PaperBatchSummary[]> {
  return fetchApi(`/api/walk-lists/${walkListId}/paper-batches`);
}

export async function createPaperBatch(
  walkListId: string,
  data: { include_phone: boolean; caller_name: string | null }
): Promise<PaperBatchDetail> {
  return fetchApi(`/api/walk-lists/${walkListId}/paper-batches`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getPaperBatch(batchId: string): Promise<PaperBatchDetail> {
  return fetchApi(`/api/paper-batches/${batchId}`);
}

// One request per photo: Vercel rejects bodies over 4.5 MB in front of the
// backend, so photos are shrunk client-side and never aggregated into a
// single multipart body. Uploads run sequentially; a failure stops the loop
// but earlier photos are already stored (the caller refetches to show them).
async function postScanFiles(
  url: string,
  files: File[],
  onProgress?: (done: number, total: number) => void
): Promise<PaperScan[]> {
  const extraHeaders: Record<string, string> = {};
  if (getViewMode() === "user") {
    extraHeaders["X-View-As"] = "user";
  }

  const scans: PaperScan[] = [];
  for (const [i, file] of files.entries()) {
    onProgress?.(i, files.length);
    const prepared = await downscaleImageForUpload(file);
    const formData = new FormData();
    formData.append("files", prepared);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120000);
    try {
      const res = await fetch(url, {
        method: "POST",
        signal: controller.signal,
        credentials: "include",
        headers: extraHeaders,
        body: formData,
      });
      if (res.status === 401) {
        if (
          typeof window !== "undefined" &&
          window.location.pathname !== "/login" &&
          window.location.pathname !== "/callback"
        )
          window.location.href = "/login";
        throw new ApiError(401, "Óheimil aðgangur");
      }
      if (res.status === 413) {
        // Platform-level rejection returns plain text, not FastAPI JSON.
        throw new ApiError(413, `Mynd ${i + 1} (${file.name}) er of stór fyrir netþjóninn`);
      }
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const detail = formatErrorDetail((body as { detail?: unknown } | null)?.detail, res.status);
        throw new ApiError(res.status, files.length > 1 ? `Mynd ${i + 1}: ${detail}` : detail, body);
      }
      scans.push(...((await res.json()) as PaperScan[]));
    } finally {
      clearTimeout(timeout);
    }
  }
  onProgress?.(files.length, files.length);
  return scans;
}

export async function uploadPaperScans(
  batchId: string,
  files: File[],
  onProgress?: (done: number, total: number) => void
): Promise<PaperScan[]> {
  return postScanFiles(`${API_BASE}/api/paper-batches/${batchId}/scans`, files, onProgress);
}

export async function deletePaperScan(batchId: string, scanId: string): Promise<void> {
  return fetchApi(`/api/paper-batches/${batchId}/scans/${scanId}`, { method: "DELETE" });
}

export async function applyPaperBatch(
  batchId: string,
  rows: { code: string; disposition: PaperDisposition; undecided: boolean }[]
): Promise<PaperApplyResult> {
  return fetchApi(`/api/paper-batches/${batchId}/apply`, {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}

// --- Móttaka: one intake for a night's paper --------------------------------
//
// Photos are dropped in a heap rather than opened list by list. Nothing here
// asks which list a photo came from, because the sheet says: the batch code is
// printed in the page footer and repeated in every row code, and the parse
// reads it. What it cannot place lands in a tray.

export interface PaperBatchIndexRow extends PaperBatchSummary {
  walk_list_id: string;
  walk_list_name: string;
  campaign_id: string;
  campaign_name: string;
  /** Flagged rows across the sheets still waiting. */
  attention_rows: number;
}

export interface InboxScan extends PaperScan {
  uploaded_by_name: string | null;
}

export interface SheetQueueRow {
  scan_id: string;
  batch_id: string;
  batch_code: string;
  walk_list_id: string;
  walk_list_name: string;
  page_number: number | null;
  status: PaperScanStatus;
  row_count: number;
  attention_rows: number;
}

export interface SheetRow {
  code: string;
  name: string;
  age: number | null;
  name_as_read: string | null;
  disposition: PaperDisposition | null;
  undecided: boolean;
  /** A marking the read could not resolve: still evidence the row was worked. */
  unreadable: boolean;
  confidence: "low" | "medium" | "high";
  note: string | null;
  needs_attention: boolean;
  attention_reason: string | null;
  in_manifest: boolean;
  row_no: number;
}

export interface SheetDetail {
  scan_id: string;
  batch_id: string;
  batch_code: string;
  walk_list_id: string;
  walk_list_name: string;
  campaign_id: string;
  status: PaperScanStatus;
  page_number: number | null;
  uploaded_at: string;
  image_url: string | null;
  error: string | null;
  rows: SheetRow[];
  /**
   * Manifest row number of the last mark on this sheet: the caller worked down
   * to here and no further. Rows past it start unticked, because "autt =
   * svarar ekki" only holds for rows somebody actually reached. 0 = nothing on
   * the sheet is marked.
   */
  last_worked_row_no: number;
}

/** Every paper batch in the election, so nobody has to open lists to find one. */
export async function listElectionPaperBatches(electionId: string): Promise<PaperBatchIndexRow[]> {
  return fetchApi(`/api/elections/${electionId}/paper-batches`);
}

/** Photos with no batch named. The parse places them. */
export async function uploadInboxScans(
  electionId: string,
  files: File[],
  onProgress?: (done: number, total: number) => void
): Promise<PaperScan[]> {
  return postScanFiles(`${API_BASE}/api/elections/${electionId}/paper-inbox/scans`, files, onProgress);
}

/** The tray: photos the parse could not place. */
export async function listInboxScans(electionId: string): Promise<InboxScan[]> {
  return fetchApi(`/api/elections/${electionId}/paper-inbox`);
}

/** Place a photo by hand. Re-reads it with the batch's manifest. */
export async function routeInboxScan(scanId: string, batchId: string): Promise<PaperScan> {
  return fetchApi(`/api/paper-scans/${scanId}/batch`, {
    method: "PUT",
    body: JSON.stringify({ batch_id: batchId }),
  });
}

export async function deleteInboxScan(scanId: string): Promise<void> {
  return fetchApi(`/api/paper-scans/${scanId}`, { method: "DELETE" });
}

/** The queue of photographed sheets, in the order you would leaf through them. */
export async function listPaperSheets(electionId: string, batchId?: string): Promise<SheetQueueRow[]> {
  const query = batchId ? `?batch_id=${batchId}` : "";
  return fetchApi(`/api/elections/${electionId}/paper-sheets${query}`);
}

/** One sheet, resolved for the screen that sits beside its photograph. */
export async function getPaperSheet(scanId: string): Promise<SheetDetail> {
  return fetchApi(`/api/paper-scans/${scanId}`);
}

/**
 * Read the photograph again: a fresh pass over the same image.
 *
 * For a reading that came back short or wrong on a picture that is perfectly
 * legible, so a sheet does not have to be re-shot over a bad read. The old
 * reading stays until the new one lands, and nothing already approved is
 * undone: approving the new reading dedups against the old.
 */
export async function reparsePaperScan(scanId: string): Promise<PaperScan> {
  return fetchApi(`/api/paper-scans/${scanId}/reparse`, { method: "POST" });
}

/**
 * Approve ONE photographed sheet.
 *
 * Idempotent for the same reason the batch apply is: contacted_at is pinned to
 * the batch, so re-approving dedups rather than doubles.
 */
export async function applyPaperSheet(
  batchId: string,
  scanId: string,
  rows: { code: string; disposition: PaperDisposition; undecided: boolean }[]
): Promise<PaperApplyResult> {
  return fetchApi(`/api/paper-batches/${batchId}/scans/${scanId}/apply`, {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}

// --- Call log from the standalone dialler -----------------------------------
//
// A separate calling system hands its callers a dial pad rather than a list.
// It has no network path here and never learns who it is calling, so its log
// comes back keyed by phone number and resolving that to a person is this
// side's job. Nothing goes the other way.

export interface CallLogCaller {
  audkenni: string;
  nafn: string;
}

/** How one dialled number resolved. */
export interface CallMatch {
  simi: string;
  nafn_slegid: string | null;
  stada: "einn" | "enginn" | "margir";
  nafn: string | null;
  fjoldi: number;
}

export interface CallLogPreview {
  simtol_alls: number;
  numer: CallMatch[];
  hringjarar: CallLogCaller[];
  okortlagdir: string[];
  kortlagning: Record<string, string>;
  okunn_malefni: string[];
}

export interface CallLogImportResult {
  logged: number;
  deduped: number;
  skipped_do_not_contact: string[];
  unmatched: string[];
  ambiguous: string[];
  unknown_tags: string[];
  unmapped_callers: string[];
  errors: string[];
}

async function postCallLogFile<T>(path: string, file: File): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: getViewMode() === "user" ? { "X-View-As": "user" } : {},
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, formatErrorDetail((body as { detail?: unknown } | null)?.detail, res.status), body);
  }
  return (await res.json()) as T;
}

/** Inspect a call log without writing anything. */
export async function previewCallLog(campaignId: string, file: File): Promise<CallLogPreview> {
  return postCallLogFile<CallLogPreview>(`/api/campaigns/${campaignId}/call-log/preview`, file);
}

/**
 * Freeze which member each caller reference belongs to.
 *
 * Must happen BEFORE the first import: contacted_by is part of the contact-log
 * idempotency key, so a mapping added afterwards re-inserts calls that were
 * already applied under the importing operator rather than moving them.
 */
export async function setCallLogCallerMap(
  campaignId: string,
  kortlagning: Record<string, string>
): Promise<{ kortlagning: Record<string, string> }> {
  return fetchApi(`/api/campaigns/${campaignId}/call-log/caller-map`, {
    method: "PUT",
    body: JSON.stringify({ kortlagning }),
  });
}

/** Apply a call log. Cumulative and idempotent, so run it whenever one arrives. */
export async function importCallLog(campaignId: string, file: File): Promise<CallLogImportResult> {
  return postCallLogFile<CallLogImportResult>(`/api/campaigns/${campaignId}/call-log/import`, file);
}

// ═══════════════════════════════════════════════════════════════════════════
// Kosningakort (executive election map): kosningastjóri only
// ═══════════════════════════════════════════════════════════════════════════

export type KortLayer = "municipality" | "postal";
export type KortDataset = "poll" | "gallup" | "paper" | "outbound";
export type KortMetric = "nei_decided" | "ja" | "nei" | "undecided" | "skil" | "volume" | "defend" | "grow";
export type KortView = "absolute" | "deviation";
export type KortChannel = "all" | "sidur" | "aa";
export type KortSegmentKind = "all" | "gender" | "age_band" | "gender_age";
/** Gallup figures are hand-entered one row at a time and never carry the
 *  crossed cut, matching the backend's narrower SegmentKind on the CRUD. */
export type KortGallupSegmentKind = "all" | "gender" | "age_band";
/** Membership layer of the outbound file; "weighted" is post-stratified to
 *  the area's real electorate using the registry member share. */
export type KortPopulation = "all" | "member" | "nonmember" | "weighted";
/** Why a cell is not painted. "member_only" means we never rang anyone
 *  outside the flokksskrá there, which is a coverage fact, not thin data. */
export type KortSuppressReason = "below_floor" | "member_only" | "wide_moe" | "no_frame";

export interface KortFeature {
  type: "Feature";
  properties: { key: string; name: string };
  geometry: Record<string, unknown>;
}

export interface KortGeometry {
  type: "FeatureCollection";
  layer: KortLayer;
  features: KortFeature[];
}

export interface KortAreaCell {
  value: number | null;
  deviation: number | null;
  ja?: number | null;
  undecided?: number | null;
  nei?: number | null;
  nei_decided?: number | null;
  n?: number | null;
  n_eff?: number | null;
  moe?: number | null;
  method?: string;
  geo_level?: string;
  suppressed?: boolean;
  stratum?: string;
  stratum_name?: string | null;
  delta_prev?: number;
  volume?: number;
  already_voted?: number;
  skil?: number | null;
  defend?: number | null;
  grow?: number | null;
  reason?: KortSuppressReason;
  /** Above the suppression floor but with an MOE too wide to read as a
   *  finding; rendered provisionally rather than at full saturation. */
  low_confidence?: boolean;
  member_n?: number;
  nonmember_n?: number;
  w_member_frame?: number | null;
  w_member_sample?: number | null;
  member_nei_decided?: number | null;
  nonmember_nei_decided?: number | null;
  /** Voters the estimate describes, from the registry. Survives suppression:
   *  a big area we know nothing about is the most actionable cell there is. */
  electorate?: number;
  caller_n?: number;
  caller_scored?: number;
  caller_spread?: number | null;
  caller_top_share?: number | null;
  /** Callers in this area differ by more than chance (chi-square, p<0.01)
   *  AND materially. The dominant error term, not sampling. */
  caller_heterogeneous?: boolean;
}

export interface KortChoropleth {
  dataset: KortDataset;
  areas: Record<string, KortAreaCell>;
  mean: number | null;
  meta: {
    wave_label?: string;
    channel?: string;
    region_label?: string | null;
    simulated?: boolean;
    prev_wave?: string | null;
    label?: string;
    fieldwork_date?: string;
    source?: string;
    window_days?: number | null;
    method?: string;
    population?: KortPopulation;
  };
}

export interface KortPollWaveInfo {
  wave_label: string;
  channel: string;
  region_key: string | null;
  region_label: string | null;
  drawn_at: string | null;
  analyzed_at: string | null;
  simulated: boolean;
}

export interface KortGallupGroup {
  group_key: string;
  label: string;
  source: string;
  fieldwork_date: string;
  geo_levels: string[];
  segment_kinds: string[];
  rows: number;
}

export interface KortDatasets {
  poll: KortPollWaveInfo[];
  gallup: KortGallupGroup[];
  paper: { batches: number; marked_logs: number; windows: (number | null)[]; channels: string[] };
  outbound: {
    logs: number;
    member_logs: number;
    nonmember_logs: number;
    windows: (number | null)[];
    populations: KortPopulation[];
  };
}

/** One membership layer of an area, for the drill panel's composition row. */
export interface KortLayerCell {
  n?: number | null;
  nei_decided?: number | null;
  undecided?: number | null;
  moe?: number | null;
  suppressed?: boolean;
}

/** The other live frame's figure for the same area. Never added to the one on
 *  screen: different selection frames, shown side by side. */
export interface KortCounterpart {
  dataset: KortDataset;
  population: KortPopulation;
  n: number;
  nei_decided: number | null;
  moe: number | null;
  suppressed: boolean;
  /** People counted in both frames. Near zero in practice, so the two are
   *  genuinely a second sample rather than the same answers twice. */
  overlap_people: number;
}

/** Which walk lists produced an area's answers. The provenance question that
 *  decides whether a number means anything: a blind list and "18-45 karlar í
 *  Árbænum" are not the same evidence. */
export interface KortSourceList {
  name: string;
  n: number;
  nei_decided: number | null;
  rollup?: boolean;
}

export interface KortAreaLayers {
  member: KortLayerCell | null;
  nonmember: KortLayerCell | null;
  /** Flokksmenn share of the electorate vs. of the people we actually reached. */
  w_member_frame: number | null;
  w_member_sample: number | null;
}

export interface KortSplitCell {
  key: string;
  n?: number | null;
  nei_decided?: number | null;
  undecided?: number | null;
  suppressed?: boolean;
  population?: string;
  [extra: string]: unknown;
}

export interface KortTrendPoint {
  wave_label?: string;
  channel?: string;
  drawn_at?: string | null;
  week?: string;
  label?: string;
  fieldwork_date?: string;
  nei_decided?: number | null;
  volume?: number;
  simulated?: boolean;
  [extra: string]: unknown;
}

export interface KortAreaDetail {
  dataset: KortDataset;
  geo_key: string;
  headline: KortAreaCell | null;
  spectrum?: { mha: number; mha_o: number; meha_o: number; meha: number; already_voted: number } | null;
  splits?: Record<string, KortSplitCell[]>;
  populations?: Record<string, KortAreaCell>;
  layers?: KortAreaLayers | null;
  counterpart?: KortCounterpart | null;
  source_lists?: KortSourceList[];
  trend?: KortTrendPoint[];
  other_channel?: KortTrendPoint | null;
  refusal_sensitivity?: {
    refusal_max_seconds?: number;
    refusal_proxy_share_of_ja?: number | null;
    nei_decided?: Record<string, number>;
  } | null;
  meta: Record<string, unknown>;
}

export interface KortGallupEntry {
  id: string;
  source: string;
  label: string;
  fieldwork_date: string;
  geo_level: "national" | "region" | "municipality";
  geo_key: string | null;
  svfnrs: string[] | null;
  postnrs: string[] | null;
  segment_kind: KortGallupSegmentKind;
  segment_key: string;
  ja_pct: number | null;
  nei_pct: number | null;
  undecided_pct: number | null;
  n: number | null;
  moe: number | null;
  notes: string | null;
  created_at: string | null;
}

export type KortGallupPayload = Omit<KortGallupEntry, "id" | "created_at">;

export interface KortChoroplethParams {
  dataset: KortDataset;
  layer: KortLayer;
  metric: KortMetric;
  wave?: string;
  channel?: KortChannel;
  segmentKind?: KortSegmentKind;
  segmentKey?: string;
  window?: number | null;
  population?: KortPopulation;
}

function kortQuery(params: KortChoroplethParams): string {
  const q = new URLSearchParams({ dataset: params.dataset, layer: params.layer, metric: params.metric });
  if (params.wave) q.set("wave", params.wave);
  if (params.channel && params.channel !== "all") q.set("channel", params.channel);
  if (params.segmentKind && params.segmentKind !== "all") {
    q.set("segment_kind", params.segmentKind);
    q.set("segment_key", params.segmentKey || "");
  }
  if (params.window) q.set("window", String(params.window));
  if (params.population && params.population !== "all") q.set("population", params.population);
  return q.toString();
}

export async function getKortGeometry(electionId: string, layer: KortLayer): Promise<KortGeometry> {
  return fetchApi(`/api/elections/${electionId}/map/geometry?layer=${layer}`);
}

export async function getKortDatasets(electionId: string): Promise<KortDatasets> {
  return fetchApi(`/api/elections/${electionId}/map/datasets`);
}

export async function getKortChoropleth(electionId: string, params: KortChoroplethParams): Promise<KortChoropleth> {
  return fetchApi(`/api/elections/${electionId}/map/choropleth?${kortQuery(params)}`);
}

export async function getKortArea(
  electionId: string,
  layer: KortLayer,
  geoKey: string,
  params: {
    dataset: KortDataset;
    wave?: string;
    channel?: KortChannel;
    window?: number | null;
    population?: KortPopulation;
  }
): Promise<KortAreaDetail> {
  const q = new URLSearchParams({ dataset: params.dataset });
  if (params.wave) q.set("wave", params.wave);
  if (params.channel && params.channel !== "all") q.set("channel", params.channel);
  if (params.window) q.set("window", String(params.window));
  if (params.population && params.population !== "all") q.set("population", params.population);
  return fetchApi(`/api/elections/${electionId}/map/area/${layer}/${encodeURIComponent(geoKey)}?${q.toString()}`);
}

export async function listKortGallup(electionId: string): Promise<KortGallupEntry[]> {
  return fetchApi(`/api/elections/${electionId}/map/gallup`);
}

export async function createKortGallup(electionId: string, body: Partial<KortGallupPayload>): Promise<{ id: string }> {
  return fetchApi(`/api/elections/${electionId}/map/gallup`, { method: "POST", body: JSON.stringify(body) });
}

export async function updateKortGallup(
  electionId: string,
  entryId: string,
  body: Partial<KortGallupPayload>
): Promise<{ ok: boolean }> {
  return fetchApi(`/api/elections/${electionId}/map/gallup/${entryId}`, { method: "PUT", body: JSON.stringify(body) });
}

export async function deleteKortGallup(electionId: string, entryId: string): Promise<{ ok: boolean }> {
  return fetchApi(`/api/elections/${electionId}/map/gallup/${entryId}`, { method: "DELETE" });
}


// ── Spurt og svarað: /api/organizations/{org}/questions (chapter admins) ─────

export type OrgQuestionTarget = "thingmenn" | "sveitarstjorn" | "stjorn";
export type OrgQuestionStatus = "open" | "answered" | "hidden" | "expired";

export interface OrgQuestion {
  id: string;
  organization_id: string;
  target: OrgQuestionTarget;
  target_label: string;
  body: string;
  status: OrgQuestionStatus;
  answer_body: string | null;
  answered_by_name: string | null;
  answered_at: string | null;
  upvote_count: number;
  /** The asker's name; present on the admin list only. */
  member_name: string | null;
  created_at: string | null;
}

export async function getOrgQuestions(orgId: string, status?: OrgQuestionStatus): Promise<OrgQuestion[]> {
  const q = status ? `?status=${status}` : "";
  return fetchApi(`/api/organizations/${orgId}/questions${q}`);
}

export async function answerOrgQuestion(
  orgId: string,
  questionId: string,
  body: { answer_body: string; answered_by_name: string },
): Promise<OrgQuestion> {
  return fetchApi(`/api/organizations/${orgId}/questions/${questionId}/answer`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function hideOrgQuestion(orgId: string, questionId: string): Promise<OrgQuestion> {
  return fetchApi(`/api/organizations/${orgId}/questions/${questionId}/hide`, { method: "POST" });
}

export async function unhideOrgQuestion(orgId: string, questionId: string): Promise<OrgQuestion> {
  return fetchApi(`/api/organizations/${orgId}/questions/${questionId}/unhide`, { method: "POST" });
}
